import React from "react";
import axios from "axios";
import { APIProvider, Map as GoogleMap, AdvancedMarker, Pin, InfoWindow } from "@vis.gl/react-google-maps";
import { MdAccessibility } from "react-icons/md";
import NoteAreaFall2023 from "../StudentOnboardingCardModalFall2023/NoteAreaFall2023/NoteAreaFall2023";
import { format, startOfWeek, addDays, addWeeks, differenceInCalendarWeeks } from "date-fns";
import { baseUrl } from "../assets/Animations";
import { getRequestHeaders } from "../api";
import {
  createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime,
  createEventObjectForTeacherWeekView,
  getTeacherEventsForWeek,
} from "../Calendar/calendarFunctions";
import useDriveMatrix from "./useDriveMatrix";
import {
  calculateScheduleMetrics,
  collectRequiredDrivePairs,
  createCanonicalStudentKey,
  haveCompatibleInstruments,
  optimizeWeeklySchedule,
  validateOptimizedSchedule,
} from "./scheduleOptimizer";

const SCHOOL_OPTIONS = [
  { id: "mma", label: "Mountainside" },
  { id: "lsma", label: "Lakeside" },
  { id: "tma", label: "Treeside" },
  { id: "mor", label: "Morningside" },
  { id: "psma", label: "Parkside" },
  { id: "den", label: "Denton" },
  { id: "tem", label: "Tempe" },
  { id: "tul", label: "Tulsa" },
];

const weekDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const SLOT_MINUTES = 5;
const PIXELS_PER_SLOT = 4; // 5 minutes per slot
const DEFAULT_START_MINUTES = 7 * 60; // 7am
const DEFAULT_END_MINUTES = 21 * 60; // 9pm
const MIN_WINDOW_MINUTES = 14 * 60; // keep a wide window by default

const parseTimeToMinutes = (val) => {
  if (val == null) return null;
  if (typeof val === "number") return val;
  if (Array.isArray(val) && val.length === 2) {
    const start = parseTimeToMinutes(val[0]);
    const end = parseTimeToMinutes(val[1]);
    if (Number.isFinite(start) && Number.isFinite(end)) return [start, end];
    return null;
  }
  if (typeof val === "string") {
    const dashParts = val.split("-");
    if (dashParts.length === 2) {
      const start = parseTimeToMinutes(dashParts[0]);
      const end = parseTimeToMinutes(dashParts[1]);
      if (start != null && end != null) return [start, end];
    }
    const match = val
      .trim()
      .match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)?$/i);
    if (match) {
      let hour = Number(match[1]);
      const minute = Number(match[2]);
      const meridiem = String(match[3] || "").toLowerCase();
      if (minute > 59) return null;
      if (meridiem) {
        if (hour < 1 || hour > 12) return null;
        if (hour === 12) hour = 0;
        if (meridiem === "pm") hour += 12;
      } else if (hour > 23) {
        return null;
      }
      return hour * 60 + minute;
    }
  }
  if (typeof val === "object") {
    const startValue =
      val.start ??
      val.StartTime ??
      val.startTime ??
      val.startMinutes ??
      val.from;
    const endValue =
      val.end ??
      val.EndTime ??
      val.endTime ??
      val.endMinutes ??
      val.to;
    const start = parseTimeToMinutes(startValue);
    const end = parseTimeToMinutes(endValue);
    if (start != null && end != null) return [start, end];
  }
  return null;
};

const normalizeDriveMinutes = (value) => {
  if (
    value == null ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return null;
  }
  const minutes = Number(value);
  return Number.isFinite(minutes) && minutes >= 0 ? minutes : null;
};

const normalizeLessonDuration = (...values) => {
  for (const value of values) {
    const minutes = Number(value);
    if (Number.isFinite(minutes) && minutes > 0) return minutes;
  }
  return 30;
};

const firstNonBlankStatus = (...values) =>
  values.find((value) => value != null && String(value).trim() !== "");

const isActiveOrUnspecifiedStatus = (status) =>
  status == null ||
  String(status).trim() === "" ||
  String(status).trim().toLowerCase() === "active";

const isEligibleOptimizationStudent = (student) => {
  const studentStatus = firstNonBlankStatus(
    student?.studentStatus,
    student?.StudentStatus,
    student?.status,
    student?.Status
  );
  const familyStatus = firstNonBlankStatus(
    student?.familyStatus,
    student?.FamilyStatus,
    student?.family?.familyStatus,
    student?.family?.status
  );
  return (
    isActiveOrUnspecifiedStatus(studentStatus) &&
    isActiveOrUnspecifiedStatus(familyStatus)
  );
};

const instrumentValuesFor = (entity) => {
  const listSources = [
    entity?.instruments,
    entity?.instrumentList,
    entity?.InstrumentList,
  ];
  const list = listSources.find(
    (candidate) => Array.isArray(candidate) && candidate.length
  );
  if (list) return list;
  const single =
    entity?.instrument ||
    entity?.Instrument ||
    (typeof entity?.instruments === "string" ? entity.instruments : null) ||
    (typeof entity?.instrumentList === "string" ? entity.instrumentList : null);
  return single ? [single] : [];
};

const instrumentLabelsFor = (entity, instrumentCatalogById = new Map()) => {
  const labels = [];
  const seen = new Set();
  instrumentValuesFor(entity).forEach((item) => {
    const parentInstrument =
      item?.parentInstrument || item?.ParentInstrument || null;
    const specificInstrument =
      item?.instrument ||
      item?.Instrument ||
      item?.name ||
      item?.Name ||
      item?.label ||
      item?.instrumentName ||
      item?.InstrumentName ||
      null;
    const identifiers = [
      typeof item === "string" ? item : null,
      item?.id,
      item?._id,
      item?.instrumentId,
      item?.InstrumentID,
      parentInstrument,
      specificInstrument,
    ].filter(Boolean);
    const catalogEntry = identifiers
      .map((identifier) =>
        instrumentCatalogById.get(String(identifier).trim().toLowerCase())
      )
      .find(Boolean);
    const catalogLabel =
      catalogEntry?.name ||
      catalogEntry?.Name ||
      catalogEntry?.instrument ||
      catalogEntry?.Instrument ||
      catalogEntry?.parentInstrument ||
      catalogEntry?.ParentInstrument ||
      null;
    let value = catalogLabel;
    if (!value) {
      value =
        typeof item === "string"
          ? item
          : String(parentInstrument || "").trim().toLowerCase() === "skill"
          ? specificInstrument
          : parentInstrument || specificInstrument;
    }
    if (
      typeof value === "string" &&
      /^[a-f\d]{24}$/i.test(value.trim())
    ) {
      value = "Instrument name unavailable";
    }
    const label = typeof value === "string" ? value.trim() : "";
    const normalized = label.toLowerCase();
    if (!label || seen.has(normalized)) return;
    seen.add(normalized);
    labels.push(label);
  });
  return labels;
};

const parseClockTimeToMinutes = (value) => {
  if (typeof value !== "string") return null;
  const match = value
    .trim()
    .match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)?$/i);
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const meridiem = String(match[3] || "").toLowerCase();
  if (minute > 59) return null;
  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    if (hour === 12) hour = 0;
    if (meridiem === "pm") hour += 12;
  }
  if (hour < 0 || hour > 23) return null;
  return hour * 60 + minute;
};

const resolveExplicitEventEndMinutes = (event, startDate, startMinutes) => {
  if (!event || !startDate || startMinutes == null) return null;
  const candidates = [
    event.endDateTime,
    event.EndDateTime,
    event.end?.dateTime,
    event.end?.date,
    event.endTime,
    event.EndTime,
  ].filter((value) => value != null && value !== "");

  for (const candidate of candidates) {
    const clockMinutes = parseClockTimeToMinutes(candidate);
    if (clockMinutes != null) {
      if (clockMinutes > startMinutes) return clockMinutes;
      continue;
    }

    if (
      typeof candidate === "number" &&
      candidate >= 0 &&
      candidate <= 24 * 60
    ) {
      if (candidate > startMinutes) return candidate;
      continue;
    }

    const endDate = candidate instanceof Date ? candidate : new Date(candidate);
    if (isNaN(endDate)) continue;
    const durationMinutes = (endDate.getTime() - startDate.getTime()) / 60000;
    if (Number.isFinite(durationMinutes) && durationMinutes > 0) {
      return startMinutes + durationMinutes;
    }
  }
  return null;
};

// Matches the optimizer's lexicographic objective: maximize coverage, then
// minimize drive, idle time, active teacher-days, and finally churn.
const compareScheduleQuality = (left, right) => {
  const leftScheduled = Number(left?.scheduledCount) || 0;
  const rightScheduled = Number(right?.scheduledCount) || 0;
  if (leftScheduled !== rightScheduled) return rightScheduled - leftScheduled;

  const costKeys = ["driveMinutes", "idleMinutes", "activeTeacherDays", "movedCount"];
  for (const key of costKeys) {
    const fallback = key === "movedCount" ? 0 : Number.POSITIVE_INFINITY;
    const leftValue = Number.isFinite(Number(left?.[key]))
      ? Number(left[key])
      : fallback;
    const rightValue = Number.isFinite(Number(right?.[key]))
      ? Number(right[key])
      : fallback;
    if (leftValue !== rightValue) return leftValue - rightValue;
  }
  return 0;
};

const hasUsableTeacherAvailability = (teacher) =>
  Object.values(teacher?.availabilityByDay || {}).some(
    (blocks) =>
      Array.isArray(blocks) &&
      blocks.some(
        (block) =>
          Number.isFinite(Number(block?.start)) &&
          Number.isFinite(Number(block?.end)) &&
          Number(block.end) > Number(block.start)
      )
  );

const DragAndDropScheduler2025 = ({ userData }) => {
  const getStudentKey = React.useCallback((obj) => {
    return createCanonicalStudentKey(obj);
  }, []);
  const [showLedger, setShowLedger] = React.useState(false);
  const [students, setStudents] = React.useState([]);
  const [allStudents, setAllStudents] = React.useState([]);
  const [teachers, setTeachers] = React.useState([]);
  const [selectedSchool, setSelectedSchool] = React.useState("");
  const [selectedTeacherId, setSelectedTeacherId] = React.useState("");
  const [welcomeStep, setWelcomeStep] = React.useState("welcome"); // "welcome", "new", null
  const [weekOffset, setWeekOffset] = React.useState(0);
  const [plannedPlacements, setPlannedPlacements] = React.useState([]); // stored as recurring patterns
  const [placedStudentIds, setPlacedStudentIds] = React.useState([]);
  const [lockedStudentIds, setLockedStudentIds] = React.useState([]);
  const [manualReassignmentStudents, setManualReassignmentStudents] = React.useState([]);
  const [resolvingManualLocations, setResolvingManualLocations] = React.useState(false);
  const manualLocationHydrationRef = React.useRef(new Set());
  const [overrideReassignmentStudents, setOverrideReassignmentStudents] = React.useState(null);
  const [scheduleName, setScheduleName] = React.useState("");
  const [showAddStudentModal, setShowAddStudentModal] = React.useState(false);
  const [showNoDriveModal, setShowNoDriveModal] = React.useState(false);
  const [removedReassignmentIds, setRemovedReassignmentIds] = React.useState([]);
  const [noDriveContext, setNoDriveContext] = React.useState(null);
  const [studentSearchTerm, setStudentSearchTerm] = React.useState("");
  const [studentSearchResults, setStudentSearchResults] = React.useState([]);
  const [studentSearchLoading, setStudentSearchLoading] = React.useState(false);
  const [studentSearchError, setStudentSearchError] = React.useState(null);
  const studentSearchTimeoutRef = React.useRef(null);
  const [currentScheduleId, setCurrentScheduleId] = React.useState(null);
  const [showLoadScheduleModal, setShowLoadScheduleModal] = React.useState(false);
  const [loadingSchedules, setLoadingSchedules] = React.useState(false);
  const [savedSchedules, setSavedSchedules] = React.useState([]);
  const skipNewScheduleResetRef = React.useRef(false);
  const [draggingStudent, setDraggingStudent] = React.useState(null);
  const [draggingPlacement, setDraggingPlacement] = React.useState(null);
  const [dragPreview, setDragPreview] = React.useState(null); // custom drag ghost
  const [isHoveringStudentColumn, setIsHoveringStudentColumn] = React.useState(false);
  const [hoverInsertIndex, setHoverInsertIndex] = React.useState(null);
  const studentListRef = React.useRef(null);
  const [loading, setLoading] = React.useState(false);
  const [maxDriveMinutes, setMaxDriveMinutes] = React.useState(""); // user-specified cap
  const [paddingMinutes, setPaddingMinutes] = React.useState(""); // optional buffer between lessons
  const dayRefs = React.useRef({});
  const calendarRef = React.useRef(null);
  const [hoverIndicator, setHoverIndicator] = React.useState(null);
  const lastHoverRef = React.useRef({ day: null, label: null });
  const lastPreviewSlotRef = React.useRef({ day: null, slot: null });
  const saveScheduleDebounceRef = React.useRef(null);
  const latestSaveScheduleRef = React.useRef(null);
  const [calendarEvents, setCalendarEvents] = React.useState([]);
  const [calendarDaysWithEvents, setCalendarDaysWithEvents] = React.useState({});
  const [weekViewEvents, setWeekViewEvents] = React.useState([]);
  const [familyInfoCache, setFamilyInfoCache] = React.useState({});
  const familyInfoCacheRef = React.useRef({});
  const familyInfoRequestRef = React.useRef(new Map());
  const [availabilityByDay, setAvailabilityByDay] = React.useState({});
  const [teacherCalendarEvents, setTeacherCalendarEvents] = React.useState([]);
  const [calendarEventsLoaded, setCalendarEventsLoaded] = React.useState(false);
  const [calendarEventsLoadError, setCalendarEventsLoadError] = React.useState(null);
  const [calendarReloadToken, setCalendarReloadToken] = React.useState(0);
  const [placeDirectoryLoadError, setPlaceDirectoryLoadError] = React.useState(null);
  const [placeDirectoryReloadToken, setPlaceDirectoryReloadToken] = React.useState(0);
  const [driveEdgesLoadError, setDriveEdgesLoadError] = React.useState(null);
  const [driveEdgesReloadToken, setDriveEdgesReloadToken] = React.useState(0);
  const [openCalendarModalAfterFetch, setOpenCalendarModalAfterFetch] = React.useState(false);
  const [allInstruments, setAllInstruments] = React.useState([]);
  const [creatingPlaceDirectories, setCreatingPlaceDirectories] = React.useState(false);
  const [placeDirectory, setPlaceDirectory] = React.useState([]);
  const [driveEdges, setDriveEdges] = React.useState([]);
  const [missingTeacherDrivePairs, setMissingTeacherDrivePairs] = React.useState([]);
  const [showMissingTeacherDriveModal, setShowMissingTeacherDriveModal] = React.useState(false);
  const [missingReassignmentDrivePairs, setMissingReassignmentDrivePairs] = React.useState([]);
  const [showMissingReassignmentDriveModal, setShowMissingReassignmentDriveModal] = React.useState(false);
  const lastMissingTeacherRef = React.useRef({ teacherId: null, count: 0 });
  const [calculatingMissingDriveTimes, setCalculatingMissingDriveTimes] = React.useState(false);
  const [calculatingReassignmentDriveTimes, setCalculatingReassignmentDriveTimes] = React.useState(false);
  const [promptReassignmentDriveTimes, setPromptReassignmentDriveTimes] = React.useState(false);
  const [showCalendarEventsModal, setShowCalendarEventsModal] = React.useState(false);
  const [optimizeLedger, setOptimizeLedger] = React.useState([]);
  const [isOptimizing, setIsOptimizing] = React.useState(false);
  const [optimizationSummary, setOptimizationSummary] = React.useState(null);
  const [optimizationUndo, setOptimizationUndo] = React.useState(null);
  const pendingOptimizationRef = React.useRef(false);
  const invalidateOptimizationResult = React.useCallback(() => {
    setOptimizationUndo(null);
    setOptimizationSummary(null);
    setOptimizeLedger([]);
    pendingOptimizationRef.current = false;
  }, []);
  const [placeDirectoryLoaded, setPlaceDirectoryLoaded] = React.useState(false);
  const [driveEdgesLoaded, setDriveEdgesLoaded] = React.useState(false);
  const [showEventDriveModal, setShowEventDriveModal] = React.useState(false);
  const [eventModalData, setEventModalData] = React.useState(null);
  const [eventModalFamily, setEventModalFamily] = React.useState(null);
  const [activeMapMarkerId, setActiveMapMarkerId] = React.useState(null);
  const [showAllWeekMarkers, setShowAllWeekMarkers] = React.useState(false);
  const [showEventMap, setShowEventMap] = React.useState(false);
  const [showMissingPlaceModal, setShowMissingPlaceModal] = React.useState(false);
  const [scheduleLoadedAt, setScheduleLoadedAt] = React.useState(0);
  const instrumentCatalogById = React.useMemo(() => {
    const catalog = new Map();
    allInstruments.forEach((instrument) => {
      [
        instrument?.id,
        instrument?._id,
        instrument?.instrumentId,
        instrument?.InstrumentID,
        instrument?.uri,
        instrument?.value,
      ]
        .filter(Boolean)
        .forEach((identifier) => {
          catalog.set(String(identifier).trim().toLowerCase(), instrument);
        });
    });
    return catalog;
  }, [allInstruments]);
  const displayInstrumentLabels = React.useCallback(
    (entity) => instrumentLabelsFor(entity, instrumentCatalogById),
    [instrumentCatalogById]
  );
  const eventHoldTimerRef = React.useRef(null);
  const LONG_PRESS_MS = 2000;
  const transparentDragImg = React.useRef(null);
  // Prefer the backend schoolId if available, otherwise use the abbreviation
  const selectedSchoolId = React.useMemo(() => {
    const teacherMatch = teachers.find(
      (t) =>
        t.School?.toLowerCase() === selectedSchool &&
        (t.SchoolID || t.SchoolId || t.schoolId)
    );
    if (teacherMatch) return teacherMatch.SchoolID || teacherMatch.SchoolId || teacherMatch.schoolId;
    const studentMatch =
      (allStudents.length ? allStudents : students).find(
        (s) =>
          s.school?.toLowerCase() === selectedSchool &&
          (s.schoolId || s.SchoolId || s.SchoolID)
      );
    if (studentMatch) return studentMatch.schoolId || studentMatch.SchoolId || studentMatch.SchoolID;
    return selectedSchool;
  }, [allStudents, selectedSchool, students, teachers]);

  const { minutesBetween, minutesMap, seedEdges, ensureAllPairs } = useDriveMatrix(
    selectedSchoolId,
    "17:00-18:00"
  );

  const formatAddress = React.useCallback((address) => {
    if (!address) return null;
    if (typeof address === "string") return address.trim() || null;
    const addr = address.Address || address.address || address;
    if (typeof addr === "string") return addr.trim() || null;
    const parts = [
      addr.addressLine1 || addr.AddressLine1 || addr.address1 || addr.streetAddress || addr.StreetAddress || addr.street,
      addr.addressLine2 || addr.AddressLine2 || addr.address2 || addr.unit,
      [addr.city || addr.City, addr.state || addr.State].filter(Boolean).join(", ").trim(),
      addr.zipcode || addr.Zipcode || addr.zip || addr.Zip || addr.zipCode,
    ].filter(Boolean);
    return parts.filter((p) => p && p !== ",").join(", ") || null;
  }, []);

  const normalizeAvailability = React.useCallback(
    (availabilityDetails) => {
      if (!availabilityDetails) return {};
      const map = {};
      weekDays.forEach((day) => {
        const key = day;
        const lower = day.toLowerCase();
        const shortKey = day.slice(0, 3).toLowerCase();
        const entries =
          availabilityDetails[key] ||
          availabilityDetails[lower] ||
          availabilityDetails[shortKey] ||
          [];
        const blocks = [];
        const addEntry = (entry) => {
          const parsed = parseTimeToMinutes(entry);
          if (
            Array.isArray(parsed) &&
            parsed.length === 2 &&
            Number.isFinite(parsed[0]) &&
            Number.isFinite(parsed[1]) &&
            parsed[1] > parsed[0]
          ) {
            blocks.push({ start: parsed[0], end: parsed[1] });
          }
        };
        const directRange = parseTimeToMinutes(entries);
        if (Array.isArray(directRange)) {
          addEntry(entries);
        } else if (Array.isArray(entries)) {
          entries.forEach(addEntry);
        } else {
          addEntry(entries);
        }
        blocks.sort((a, b) => a.start - b.start);
        map[day] = blocks;
      });
      return map;
    },
    []
  );

  const startOfThisWeek = React.useMemo(
    () => startOfWeek(addWeeks(new Date(), weekOffset), { weekStartsOn: 1 }),
    [weekOffset]
  );
  const displayedWeek = React.useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(startOfThisWeek, i)),
    [startOfThisWeek]
  );
  const displayedWeekLabel = React.useMemo(
    () =>
      `${format(displayedWeek[0], "MMM d")} – ${format(
        displayedWeek[6],
        "MMM d, yyyy"
      )}`,
    [displayedWeek]
  );
  const handleDisplayedWeekChange = React.useCallback(
    (dateValue) => {
      const parts = String(dateValue || "")
        .split("-")
        .map(Number);
      if (
        parts.length !== 3 ||
        !parts.every(Number.isFinite)
      ) {
        return;
      }
      const targetDate = new Date(parts[0], parts[1] - 1, parts[2], 12);
      const targetWeek = startOfWeek(targetDate, { weekStartsOn: 1 });
      const currentWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
      invalidateOptimizationResult();
      setWeekOffset(differenceInCalendarWeeks(targetWeek, currentWeek));
    },
    [invalidateOptimizationResult]
  );

  const driveBucket = "17:00-18:00";
  const DRIVE_TIME_COST_PER_PAIR = 0.01;
  const makeDriveKey = React.useCallback(
    (fromKey, toKey) => `${selectedSchoolId || selectedSchool || "na"}|${driveBucket}|${fromKey}=>${toKey}`,
    [selectedSchool, selectedSchoolId]
  );

  const timeWindow = React.useMemo(() => {
    const ranges = [];
    weekViewEvents.forEach((event) => {
      const start = event.startDate || event.parsedDate || (event.dateTime ? new Date(event.dateTime) : null);
      if (!start || isNaN(start)) return;
      const duration = Number(event.lessonLength) || (event.rowLength ? event.rowLength * SLOT_MINUTES : 30);
      const startMinutes = start.getHours() * 60 + start.getMinutes();
      const endMinutes = startMinutes + Math.max(duration, 15);
      ranges.push([startMinutes, endMinutes]);
    });
    plannedPlacements
      .filter((p) => p.teacherId === selectedTeacherId)
      .forEach((placement) => {
        const startMinutes = placement.startMinutes;
        const durationMinutes = Number(placement.lessonLength) || 30;
        ranges.push([startMinutes, startMinutes + durationMinutes]);
      });

    let startMinutes = DEFAULT_START_MINUTES;
    let endMinutes = DEFAULT_END_MINUTES;
    if (ranges.length) {
      const minVal = Math.min(...ranges.map((r) => r[0]));
      const maxVal = Math.max(...ranges.map((r) => r[1]));
      startMinutes = Math.min(minVal, DEFAULT_START_MINUTES);
      endMinutes = Math.max(maxVal, DEFAULT_END_MINUTES);
    }
    if (endMinutes - startMinutes < MIN_WINDOW_MINUTES) {
      endMinutes = startMinutes + MIN_WINDOW_MINUTES;
    }
    const startHour = Math.floor(startMinutes / 60);
    const endHour = Math.ceil(endMinutes / 60);
    const totalMinutes = endMinutes - startMinutes;
    const totalHeightPx = (totalMinutes / SLOT_MINUTES) * PIXELS_PER_SLOT;
    return { startMinutes, endMinutes, startHour, endHour, totalMinutes, totalHeightPx };
  }, [weekViewEvents, plannedPlacements, selectedTeacherId]);

  React.useEffect(() => {
    if (!selectedTeacherId) {
      setAvailabilityByDay({});
      return;
    }
    const teacher = teachers.find(
      (t) => String(t.TeacherID) === String(selectedTeacherId)
    );
    if (teacher?.AvailabilityDetails) {
      setAvailabilityByDay(normalizeAvailability(teacher.AvailabilityDetails));
    } else {
      setAvailabilityByDay({});
    }
  }, [normalizeAvailability, selectedTeacherId, teachers]);

  const getUnavailableSegments = React.useCallback(
    (dayName) => {
      const avail = availabilityByDay[dayName] || [];
      const segments = [];
      let cursor = timeWindow.startMinutes;
      avail.forEach((block) => {
        const blockStart = block.start;
        const blockEnd = block.end;
        if (blockStart > cursor) {
          segments.push([cursor, Math.min(blockStart, timeWindow.endMinutes)]);
        }
        cursor = Math.max(cursor, blockEnd);
      });
      if (cursor < timeWindow.endMinutes) {
        segments.push([cursor, timeWindow.endMinutes]);
      }
      return segments
        .filter((seg) => seg[1] > seg[0])
        .map((seg) => {
          const top = ((seg[0] - timeWindow.startMinutes) / SLOT_MINUTES) * PIXELS_PER_SLOT;
          const height = ((seg[1] - seg[0]) / SLOT_MINUTES) * PIXELS_PER_SLOT;
          return { top, height };
        });
    },
    [availabilityByDay, timeWindow]
  );

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [studentsRes, teachersRes, allStudentsRes, instrumentsRes] = await Promise.all([
          axios.get(`${baseUrl}/engaged-students`, getRequestHeaders()),
          axios.get(`${baseUrl}/initialadminrequest`, getRequestHeaders()),
          axios.get(`${baseUrl}/school-management`, getRequestHeaders()),
          axios.get(`${baseUrl}/inst`, getRequestHeaders()).catch(() => ({ data: [] })),
        ]);
        setStudents(studentsRes.data);
        setTeachers(teachersRes.data);
        setAllStudents(Array.isArray(allStudentsRes.data) ? allStudentsRes.data : []);
        setAllInstruments(Array.isArray(instrumentsRes.data) ? instrumentsRes.data : []);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getStudentTeacherId = React.useCallback((student) => {
    if (!student) return null;
    return (
      student.teachersId ||
      student.teacherId ||
      student.teacherID ||
      student.TeacherID ||
      student?.teacher?.TeacherID ||
      null
    );
  }, []);

  const leavingTeacherIds = React.useMemo(() => {
    if (!teachers.length) return new Set();
    return new Set(
      teachers
        .filter(
          (teacher) =>
            teacher.Status === "leaving" ||
            teacher.Status === "Left School" ||
            teacher.Status === "summer-break"
        )
        .map((teacher) => String(teacher.TeacherID))
    );
  }, [teachers]);

  // Fetch place directory for selected school
  React.useEffect(() => {
    let cancelled = false;
    setPlaceDirectoryLoaded(false);
    setPlaceDirectoryLoadError(null);
    if (!selectedSchoolId) {
      setPlaceDirectory([]);
      return undefined;
    }
    if (String(selectedSchoolId).toLowerCase() === "mor") {
      console.log("Fetching place directory for /place-directory/mor");
    }
    axios
      .get(`${baseUrl}/place-directory/${selectedSchoolId}`, getRequestHeaders())
      .then((res) => {
        if (cancelled) return;
        const payload = res.data;
        if (payload && Array.isArray(payload.directories)) {
          setPlaceDirectory(payload.directories);
        } else if (Array.isArray(payload)) {
          setPlaceDirectory(payload);
        } else {
          setPlaceDirectory([]);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setPlaceDirectory([]);
        setPlaceDirectoryLoadError("Location data failed to load.");
      })
      .finally(() => {
        if (!cancelled) setPlaceDirectoryLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [placeDirectoryReloadToken, selectedSchoolId]);

  const familiesNeedingNewTeacher = React.useMemo(() => {
    if (!students.length || !teachers.length) return [];
    return students.filter((family) =>
      family.students?.some((student) => {
        const tid = getStudentTeacherId(student);
        return tid && leavingTeacherIds.has(String(tid));
      })
    );
  }, [getStudentTeacherId, leavingTeacherIds, students, teachers]);

  const studentsInSelectedSchool = React.useMemo(() => {
    if (!selectedSchool) return [];
    const sourceFamilies = allStudents.length ? allStudents : students;
    const list = [];
    const seen = new Set();
    sourceFamilies.forEach((family) => {
      if (family.school?.toLowerCase() !== selectedSchool) return;
      family.students?.forEach((student) => {
        const teacherId = getStudentTeacherId(student);
        if (!teacherId || !leavingTeacherIds.has(String(teacherId))) return;
        const uniqueId = `${family.familyId}-${student.studentId}`;
        if (placedStudentIds.includes(uniqueId) || seen.has(uniqueId)) return;
        list.push({
          id: uniqueId,
          familyId: family.familyId,
          familyName: family.familyName,
          studentId: student.studentId,
          studentName: student.studentName,
          instruments: student.instrumentList || student.instruments || [],
          lessonLength: student.lessonLength,
          availability: student.initialAvailability || family.initialAvailability,
          school: family.school?.toLowerCase(),
          familyAddress: family.familyAddress || family.address || student.address,
          teacherId,
        });
        seen.add(uniqueId);
      });
    });
    return list;
  }, [allStudents, getStudentTeacherId, leavingTeacherIds, placedStudentIds, selectedSchool, students]);

  const allStudentsForSelectedSchool = React.useMemo(() => {
    if (!selectedSchool) return [];
    const sourceFamilies = allStudents.length ? allStudents : students;
    const list = [];
    const seen = new Set();
    sourceFamilies.forEach((family) => {
      if (family.school?.toLowerCase() !== selectedSchool) return;
      family.students?.forEach((student) => {
        const studentId = student.studentId || student.id || student.studentName;
        const uniqueId = `${family.familyId}-${studentId}`;
        if (!studentId || !uniqueId || seen.has(uniqueId)) return;
        list.push({
          id: uniqueId,
          familyId: family.familyId,
          familyName: family.familyName,
          studentId,
          studentName: student.studentName || student.name,
          instruments: student.instrumentList || student.instruments || [],
          lessonLength: student.lessonLength,
          availability: student.initialAvailability || family.initialAvailability,
          school: family.school?.toLowerCase(),
          familyAddress: family.familyAddress || family.address || student.address,
          teacherId: getStudentTeacherId(student),
          familyStatus:
            family.familyStatus || family.FamilyStatus || family.status || family.Status,
          studentStatus:
            student.studentStatus ||
            student.StudentStatus ||
            student.status ||
            student.Status,
        });
        seen.add(uniqueId);
      });
    });
    return list;
  }, [allStudents, getStudentTeacherId, selectedSchool, students]);

  const placementStudentKeys = React.useMemo(() => {
    const keys = new Set(placedStudentIds.map((id) => String(id)));
    plannedPlacements.forEach((placement) => {
      if (!placement) return;
      const candidates = [
        placement.placementKey,
        placement.id,
        placement.studentId,
        placement.familyId && placement.studentId
          ? `${placement.familyId}-${placement.studentId}`
          : null,
      ].filter(Boolean);
      candidates.forEach((candidate) => keys.add(String(candidate)));
    });
    return keys;
  }, [placedStudentIds, plannedPlacements]);

  const studentsNeedingReassignment = React.useMemo(() => {
    const list = [];
    const seen = new Set();

    // If we have an override list (e.g., loaded a single teacher's students), use it
    if (overrideReassignmentStudents?.length) {
      overrideReassignmentStudents.forEach((student) => {
        const sid =
          student.id ||
          student.studentId ||
          `${student.familyId || "family"}-${student.studentId || student.studentName}`;
        if (!sid || placementStudentKeys.has(String(sid)) || seen.has(sid) || removedReassignmentIds.includes(sid)) return;
        list.push(student);
        seen.add(sid);
      });
      return list;
    }

    // If a school is selected without an override, start empty and let manual additions fill in.
    if (!selectedSchool) {
      familiesNeedingNewTeacher.forEach((family) => {
        family.students?.forEach((student) => {
          const teacherId = getStudentTeacherId(student);
          if (!teacherId || !leavingTeacherIds.has(String(teacherId))) return;
          const uniqueId = `${family.familyId}-${student.studentId}`;
          if (placementStudentKeys.has(String(uniqueId)) || seen.has(uniqueId) || removedReassignmentIds.includes(uniqueId)) return;
          const item = {
            id: uniqueId,
            familyId: family.familyId,
            familyName: family.familyName,
            studentId: student.studentId,
            studentName: student.studentName,
            instruments: student.instrumentList || [],
            lessonLength: student.lessonLength,
            availability: student.initialAvailability || family.initialAvailability,
            school: family.school?.toLowerCase(),
            familyAddress: family.familyAddress || family.address || student.address,
            teacherId,
            familyStatus:
              family.familyStatus || family.FamilyStatus || family.status || family.Status,
            studentStatus:
              student.studentStatus ||
              student.StudentStatus ||
              student.status ||
              student.Status,
          };
          list.push(item);
          seen.add(uniqueId);
        });
      });
    }

    manualReassignmentStudents.forEach((student) => {
      if (!student) return;
      if (selectedSchool && student.school?.toLowerCase() !== selectedSchool) return;
      const sid =
        student.id ||
        student.studentId ||
        `${student.familyId || "family"}-${student.studentId || student.studentName}`;
      if (!sid || placementStudentKeys.has(String(sid)) || seen.has(sid) || removedReassignmentIds.includes(sid)) return;
      list.push({ ...student, id: sid });
      seen.add(sid);
    });
    return list;
  }, [
    familiesNeedingNewTeacher,
    overrideReassignmentStudents,
    getStudentTeacherId,
    leavingTeacherIds,
    manualReassignmentStudents,
    placementStudentKeys,
    removedReassignmentIds,
    selectedSchool,
  ]);

  const existingReassignmentIds = React.useMemo(() => {
    const existingIds = new Set();
    studentsNeedingReassignment.forEach((s) => {
      const id = s.id || s.studentId || `${s.familyId || "family"}-${s.studentId || s.studentName}`;
      if (id) existingIds.add(String(id));
    });
    placementStudentKeys.forEach((id) => existingIds.add(String(id)));
    return existingIds;
  }, [placementStudentKeys, studentsNeedingReassignment]);

  const reassignmentIdSet = React.useMemo(() => {
    const ids = new Set();
    const addStudent = (student) => {
      if (!student) return;
      const id =
        student.id ||
        student.studentId ||
        `${student.familyId || "family"}-${student.studentId || student.studentName}`;
      if (id) ids.add(String(id));
    };
    if (Array.isArray(overrideReassignmentStudents)) {
      overrideReassignmentStudents.forEach(addStudent);
    }
    manualReassignmentStudents.forEach(addStudent);
    return ids;
  }, [manualReassignmentStudents, overrideReassignmentStudents]);

  const instrumentSetForSchool = React.useMemo(() => {
    const set = new Set();
    const addInstruments = (student) => {
      student.instrumentList?.forEach((inst) => {
        if (inst.parentInstrument) set.add(inst.parentInstrument);
      });
      student.instruments?.forEach((inst) => {
        if (inst.parentInstrument) set.add(inst.parentInstrument);
      });
    };
    familiesNeedingNewTeacher
      .filter((family) => !selectedSchool || family.school?.toLowerCase() === selectedSchool)
      .forEach((family) => {
        family.students?.forEach(addInstruments);
      });
    if (selectedSchool) {
      studentsInSelectedSchool.forEach(addInstruments);
    }
    manualReassignmentStudents
      .filter((student) => !selectedSchool || student.school?.toLowerCase() === selectedSchool)
      .forEach(addInstruments);
    return set;
  }, [
    familiesNeedingNewTeacher,
    manualReassignmentStudents,
    selectedSchool,
    studentsInSelectedSchool,
  ]);

  const addableStudentsForSelectedSchool = React.useMemo(() => {
    if (!selectedSchool) return [];
    return allStudentsForSelectedSchool.filter((s) => !existingReassignmentIds.has(String(s.id)));
  }, [allStudentsForSelectedSchool, existingReassignmentIds, selectedSchool]);

  React.useEffect(() => {
    if (!showAddStudentModal || !selectedSchool) {
      setStudentSearchResults([]);
      setStudentSearchLoading(false);
      setStudentSearchError(null);
      return;
    }
    const term = studentSearchTerm.trim();
    if (!term) {
      setStudentSearchResults([]);
      setStudentSearchLoading(false);
      setStudentSearchError(null);
      return;
    }
    if (studentSearchTimeoutRef.current) {
      clearTimeout(studentSearchTimeoutRef.current);
    }
    setStudentSearchLoading(true);
    setStudentSearchError(null);
    const schoolLower = selectedSchool.toLowerCase();
    let cancelled = false;
    studentSearchTimeoutRef.current = setTimeout(() => {
      axios
        .get(`${baseUrl}/search/${encodeURIComponent(term)}`, getRequestHeaders())
        .then((res) => {
          if (cancelled) return;
          const payload = Array.isArray(res.data) ? res.data : [];
          const nextResults = [];
          const seen = new Set();
          payload.forEach((item) => {
            if (!item) return;
            if (Array.isArray(item.students)) {
              const familySchool = (item.school || item.School || "").toLowerCase();
              item.students.forEach((student) => {
                const studentSchool = (student.school || student.School || "").toLowerCase();
                const resolvedSchool = familySchool || studentSchool;
                if (!resolvedSchool || resolvedSchool !== schoolLower) return;
                const studentId = student.studentId || student.id || student.studentName;
                if (!studentId) return;
                const familyId = item.familyId || item.id || item.familyID || null;
                const uniqueId = `${familyId || "family"}-${studentId}`;
                if (seen.has(uniqueId) || existingReassignmentIds.has(String(uniqueId))) return;
                nextResults.push({
                  id: uniqueId,
                  familyId,
                  familyName: item.familyName,
                  studentId,
                  studentName: student.studentName || student.name,
                  instruments: student.instrumentList || student.instruments || [],
                  lessonLength: student.lessonLength,
                  availability: student.initialAvailability || item.initialAvailability,
                  school: resolvedSchool,
                  familyAddress: item.familyAddress || item.address || student.address,
                  teacherId: getStudentTeacherId(student),
                });
                seen.add(uniqueId);
              });
              return;
            }
            const studentId = item.studentId || item.id || item.studentName;
            if (!studentId) return;
            const studentSchool = (
              item.school ||
              item.School ||
              item.schoolName ||
              item.SchoolName ||
              ""
            ).toLowerCase();
            if (!studentSchool || studentSchool !== schoolLower) return;
            const familyId = item.familyId || item.familyID || item.FamilyID || null;
            const uniqueId = `${familyId || "family"}-${studentId}`;
            if (seen.has(uniqueId) || existingReassignmentIds.has(String(uniqueId))) return;
            nextResults.push({
              id: uniqueId,
              familyId,
              familyName: item.familyName,
              studentId,
              studentName: item.studentName || item.name,
              instruments: item.instrumentList || item.instruments || [],
              lessonLength: item.lessonLength,
              availability: item.initialAvailability,
              school: studentSchool,
              familyAddress: item.familyAddress || item.address,
              teacherId: getStudentTeacherId(item),
            });
            seen.add(uniqueId);
          });
          setStudentSearchResults(nextResults.slice(0, 50));
          setStudentSearchLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          console.log(err);
          setStudentSearchResults([]);
          setStudentSearchLoading(false);
          setStudentSearchError("Unable to search students right now.");
        });
    }, 300);
    return () => {
      cancelled = true;
      if (studentSearchTimeoutRef.current) {
        clearTimeout(studentSearchTimeoutRef.current);
      }
    };
  }, [
    existingReassignmentIds,
    getStudentTeacherId,
    selectedSchool,
    showAddStudentModal,
    studentSearchTerm,
  ]);

  const handleRemoveReassignmentStudent = React.useCallback(
    (student) => {
      if (!student) return;
      const sid =
        student.id ||
        student.studentId ||
        `${student.familyId || "family"}-${student.studentId || student.studentName}`;
      if (!sid) return;
      invalidateOptimizationResult();
      setRemovedReassignmentIds((prev) => (prev.includes(sid) ? prev : [...prev, sid]));
      setManualReassignmentStudents((prev) =>
        prev.filter((s) => String(s.id || s.studentId) !== String(sid))
      );
      setOverrideReassignmentStudents((prev) =>
        prev ? prev.filter((s) => String(s.id || s.studentId) !== String(sid)) : prev
      );
    },
    [invalidateOptimizationResult]
  );

  const handleResetReassignmentPlacements = React.useCallback(() => {
    const idsToReset = new Set(reassignmentIdSet);
    if (!idsToReset.size) {
      plannedPlacements.forEach((p) => {
        if (!p) return;
        const key =
          getStudentKey(p) ||
          p.placementKey ||
          p.studentId ||
          p.id ||
          (p.familyId && p.studentId ? `${p.familyId}-${p.studentId}` : null);
        if (key) idsToReset.add(String(key));
      });
    }
    if (!idsToReset.size) return;
    invalidateOptimizationResult();
    setPlannedPlacements((prev) =>
      prev.filter((p) => {
        const key =
          getStudentKey(p) ||
          p.placementKey ||
          p.studentId ||
          p.id ||
          (p.familyId && p.studentId ? `${p.familyId}-${p.studentId}` : null);
        return !key || !idsToReset.has(String(key));
      })
    );
    setPlacedStudentIds((prev) => prev.filter((id) => !idsToReset.has(String(id))));
    setLockedStudentIds((prev) => prev.filter((id) => !idsToReset.has(String(id))));
    setRemovedReassignmentIds((prev) => prev.filter((id) => !idsToReset.has(String(id))));
    if (Array.isArray(overrideReassignmentStudents)) {
      const current = new Map();
      overrideReassignmentStudents.forEach((student) => {
        const id =
          student?.id ||
          student?.studentId ||
          `${student?.familyId || "family"}-${student?.studentId || student?.studentName}`;
        if (id) current.set(String(id), student);
      });
      plannedPlacements.forEach((p) => {
        if (!p) return;
        const key =
          getStudentKey(p) ||
          p.placementKey ||
          p.studentId ||
          p.id ||
          (p.familyId && p.studentId ? `${p.familyId}-${p.studentId}` : null);
        if (!key || !idsToReset.has(String(key)) || current.has(String(key))) return;
        current.set(String(key), {
          id: String(key),
          familyId: p.familyId,
          familyName: p.familyName,
          studentId: p.studentId || p.placementKey || p.id,
          studentName: p.studentName,
          instruments: p.instrument
            ? [{ parentInstrument: p.instrument, instrument: p.instrument }]
            : p.instruments || [],
          lessonLength: p.lessonLength,
          availability: p.availability,
          school: selectedSchool,
          familyAddress: p.familyAddress,
          teacherId: p.teacherId,
        });
      });
      setOverrideReassignmentStudents(Array.from(current.values()));
    }
  }, [
    getStudentKey,
    invalidateOptimizationResult,
    overrideReassignmentStudents,
    plannedPlacements,
    reassignmentIdSet,
    selectedSchool,
  ]);

  const addStudentToReassignmentById = React.useCallback(
    async (studentOrId) => {
      if (!studentOrId) return;
      const match =
        typeof studentOrId === "object"
          ? studentOrId
          : addableStudentsForSelectedSchool.find((s) => String(s.id) === String(studentOrId));
      if (!match) return;
      const familyId = match.familyId;
      if (!familyId) {
        setStudentSearchError(
          "This student could not be linked to a family record, so their location could not be loaded."
        );
        return;
      }
      const localFamily = (allStudents.length ? allStudents : students).find(
        (family) => String(family.familyId) === String(familyId)
      );
      let familyInfo = familyInfoCache[familyId] || null;
      if (!familyInfo) {
        try {
          const response = await axios.get(
            `${baseUrl}/school-management/${familyId}`,
            getRequestHeaders()
          );
          familyInfo = response.data || null;
          if (familyInfo) {
            setFamilyInfoCache((prev) => ({ ...prev, [familyId]: familyInfo }));
          }
        } catch (error) {
          console.log(error);
        }
      }
      familyInfo = familyInfo || localFamily;
      const familyStudent = familyInfo?.students?.find(
        (student) =>
          String(student.studentId || student.id) === String(match.studentId) ||
          (match.studentName && student.studentName === match.studentName)
      );
      const familyAddress =
        [
          match.familyAddress,
          match.address,
          familyInfo?.familyAddress,
          familyInfo?.FamilyAddress,
          familyInfo?.address,
          familyInfo?.Address,
          familyStudent?.address,
        ].find((address) => formatAddress(address)) || null;
      const hydratedMatch = {
        ...match,
        familyName: match.familyName || familyInfo?.familyName,
        studentName:
          match.studentName || familyStudent?.studentName || familyStudent?.name,
        instruments:
          instrumentValuesFor(match).length
            ? instrumentValuesFor(match)
            : familyStudent?.instrumentList || familyStudent?.instruments || [],
        lessonLength:
          match.lessonLength || familyStudent?.lessonLength || null,
        availability:
          match.availability ||
          familyStudent?.initialAvailability ||
          familyInfo?.initialAvailability ||
          null,
        familyAddress,
      };
      const matchId =
        hydratedMatch.id ||
        `${hydratedMatch.familyId || "family"}-${hydratedMatch.studentId || hydratedMatch.studentName}`;
      if (!matchId) return;

      // Ensure a place directory entry exists for this family (so drive edges can be computed later).
      const schoolId = selectedSchoolId || selectedSchool;
      let familyPlaceKey = null;
      if (schoolId && familyId) {
        const existingDirectory = placeDirectory.find((pd) => {
          if (!pd) return false;
          if (pd.familyIds && Array.isArray(pd.familyIds) && pd.familyIds.some((fid) => String(fid) === String(familyId))) {
            return true;
          }
          return String(pd.key || "").toLowerCase() === String(familyId).toLowerCase();
        });
        familyPlaceKey = existingDirectory?.key || null;
        if (!existingDirectory) {
          const addressString = familyAddress ? formatAddress(familyAddress) : null;
          if (!addressString) {
            setStudentSearchError(
              `The family record for ${hydratedMatch.studentName || hydratedMatch.familyName || "this student"} does not have an address. Add the family address before scheduling.`
            );
            return;
          }
          const directory = {
            key: String(familyId),
            address: addressString,
            label:
              hydratedMatch.familyName ||
              hydratedMatch.studentName ||
              String(familyId),
            roles: ["family"],
            familyIds: [familyId],
          };
          try {
            await axios.post(
              `${baseUrl}/place-directories/bulk`,
              { schoolId, directories: [directory] },
              getRequestHeaders()
            );
            familyPlaceKey = directory.key;
            setPlaceDirectory((prev) => {
              const already = prev.some(
                (pd) =>
                  String(pd.key || "").toLowerCase() ===
                  String(directory.key).toLowerCase()
              );
              return already ? prev : [...prev, directory];
            });
          } catch (error) {
            console.log(error);
            setStudentSearchError(
              "The family location could not be created. Please try adding the student again."
            );
            return;
          }
        }
      }

      invalidateOptimizationResult();
      setRemovedReassignmentIds((prev) => prev.filter((id) => String(id) !== String(matchId)));
      setManualReassignmentStudents((prev) => {
        const exists = prev.some((s) => String(s.id) === String(matchId));
        return exists
          ? prev
          : [
              ...prev,
              {
                ...hydratedMatch,
                id: matchId,
                placeKey: familyPlaceKey || hydratedMatch.placeKey || null,
              },
            ];
      });
      setStudentSearchTerm("");
      setShowAddStudentModal(false);
    },
    [
      addableStudentsForSelectedSchool,
      allStudents,
      familyInfoCache,
      formatAddress,
      invalidateOptimizationResult,
      placeDirectory,
      selectedSchool,
      selectedSchoolId,
      students,
    ]
  );

  const saveSchedule = React.useCallback(async (labelOverride = undefined) => {
    if (!currentScheduleId) return;
    const schoolId = selectedSchoolId || selectedSchool;
    if (!schoolId) return;
    const labelToSave = labelOverride !== undefined ? labelOverride : scheduleName;
    const placementsSafe = Array.isArray(plannedPlacements) ? plannedPlacements : [];
    const lockedSafe = Array.isArray(lockedStudentIds) ? lockedStudentIds : [];
    const manualSafe = Array.isArray(manualReassignmentStudents) ? manualReassignmentStudents : [];
    const overrideSafe = Array.isArray(overrideReassignmentStudents) ? overrideReassignmentStudents : [];
    console.log("Saving schedule", { currentScheduleId, schoolId, labelToSave });
    try {
      await axios.put(
        `${baseUrl}/schedules/${currentScheduleId}`,
        {
          schoolId,
          weekStart: startOfThisWeek,
          placements: placementsSafe,
          lockedStudentIds: lockedSafe,
          manualReassignmentStudents: manualSafe,
          overrideReassignmentStudents: overrideSafe,
          label: labelToSave || undefined,
          status: "draft",
        },
        getRequestHeaders()
      );
    } catch (err) {
      console.log("Failed to save schedule", err?.response?.data || err?.message);
    }
  }, [
    currentScheduleId,
    manualReassignmentStudents,
    overrideReassignmentStudents,
    plannedPlacements,
    lockedStudentIds,
    scheduleName,
    selectedSchool,
    selectedSchoolId,
    startOfThisWeek,
  ]);
  latestSaveScheduleRef.current = saveSchedule;

  const createSchedule = React.useCallback(
    async (overrides = {}) => {
      const schoolId = selectedSchoolId || selectedSchool;
      if (!schoolId) return null;
      const placementsSafe = Array.isArray(overrides.placements ?? plannedPlacements)
        ? overrides.placements ?? plannedPlacements
        : [];
      const lockedSafe = Array.isArray(overrides.lockedStudentIds ?? lockedStudentIds)
        ? overrides.lockedStudentIds ?? lockedStudentIds
        : [];
      const manualSafe = Array.isArray(overrides.manualReassignmentStudents ?? manualReassignmentStudents)
        ? overrides.manualReassignmentStudents ?? manualReassignmentStudents
        : [];
      const overrideSafe = Array.isArray(overrides.overrideReassignmentStudents ?? overrideReassignmentStudents)
        ? overrides.overrideReassignmentStudents ?? overrideReassignmentStudents
        : [];
      const body = {
        schoolId,
        weekStart: startOfThisWeek,
        placements: placementsSafe,
        lockedStudentIds: lockedSafe,
        manualReassignmentStudents: manualSafe,
        overrideReassignmentStudents: overrideSafe,
        label: overrides.label ?? (scheduleName || undefined),
        status: "draft",
      };
      console.log("Creating schedule", body);
      try {
        const res = await axios.post(`${baseUrl}/schedules`, body, getRequestHeaders());
        const id = res.data?._id || res.data?.id;
        if (id) setCurrentScheduleId(id);
        return id;
      } catch (err) {
        console.log("Failed to create schedule", err?.response?.data || err?.message);
        return null;
      }
    },
    [
      manualReassignmentStudents,
      overrideReassignmentStudents,
      plannedPlacements,
      lockedStudentIds,
      scheduleName,
      selectedSchool,
      selectedSchoolId,
      startOfThisWeek,
    ]
  );

  const handleDeleteSchedule = React.useCallback(
    async (scheduleId) => {
      const id = scheduleId || currentScheduleId;
      if (!id) return;
      const confirmed = window.confirm("Delete this saved schedule? This cannot be undone.");
      if (!confirmed) return;
      const schoolId = selectedSchoolId || selectedSchool;
      try {
        const payload = { schoolId };
        await axios.delete(`${baseUrl}/schedules/${id}`, {
          ...getRequestHeaders(),
          data: payload,
        });
        setSavedSchedules((prev) => prev.filter((s) => String(s._id || s.id) !== String(id)));
        if (String(currentScheduleId) === String(id)) {
          skipNewScheduleResetRef.current = true;
          setCurrentScheduleId(null);
          setScheduleName("");
        }
      } catch (err) {
        console.log("Failed to delete schedule", err?.response?.data || err?.message);
      }
    },
    [currentScheduleId, selectedSchool, selectedSchoolId]
  );

  const placeKeyLookup = React.useMemo(() => {
    const familyToPlace = new Map();
    const teacherToPlace = new Map();
    const addressToPlace = new Map();
    const labelToPlace = new Map();
    placeDirectory.forEach((entry) => {
      if (!entry.key) return;
      entry.familyIds?.forEach((fid) => familyToPlace.set(String(fid), entry.key));
      entry.teacherIds?.forEach((tid) => teacherToPlace.set(String(tid), entry.key));
      const addrStr = formatAddress(entry.address || entry.label || entry.key);
      if (addrStr) addressToPlace.set(addrStr.toLowerCase(), entry.key);
      if (entry.label) labelToPlace.set(String(entry.label).toLowerCase(), entry.key);
      labelToPlace.set(String(entry.key).toLowerCase(), entry.key);
    });
    driveEdges.forEach((edge) => {
      const originKey = edge.origin?.key || edge.fromKey || edge.origin || edge.from;
      const destKey = edge.destination?.key || edge.toKey || edge.destination || edge.to;
      const originLabel = edge.origin?.label;
      const destLabel = edge.destination?.label;
      if (originKey) labelToPlace.set(String(originKey).toLowerCase(), originKey);
      if (destKey) labelToPlace.set(String(destKey).toLowerCase(), destKey);
      if (originLabel && originKey) labelToPlace.set(String(originLabel).toLowerCase(), originKey);
      if (destLabel && destKey) labelToPlace.set(String(destLabel).toLowerCase(), destKey);
    });
    return { familyToPlace, teacherToPlace, addressToPlace, labelToPlace };
  }, [driveEdges, formatAddress, placeDirectory]);

  const resolveStudentPlace = React.useCallback(
    (student) => {
      const explicitPlaceKey =
        student?.placeKey || student?.place_key || student?.placeId || null;
      if (explicitPlaceKey) return explicitPlaceKey;
      const familyPlace = new Map();
      placeDirectory.forEach((entry) => {
        entry.familyIds?.forEach((fid) => familyPlace.set(String(fid), entry.key));
      });
      const fid = student.familyId;
      if (fid && familyPlace.has(String(fid))) return familyPlace.get(String(fid));
      const addr = formatAddress(student.familyAddress || student.address);
      if (addr && placeKeyLookup.addressToPlace.has(addr.toLowerCase())) {
        return placeKeyLookup.addressToPlace.get(addr.toLowerCase());
      }
      const label = (student.familyName || student.studentName || "").toLowerCase();
      if (label && placeKeyLookup.labelToPlace.has(label)) {
        return placeKeyLookup.labelToPlace.get(label);
      }
      return fid || student.familyName || student.studentId || student.studentName || null;
    },
    [formatAddress, placeDirectory, placeKeyLookup]
  );

  const updatePlacementField = React.useCallback(
    (matchKey, updater) => {
      if (!matchKey) return;
      invalidateOptimizationResult();
      setPlannedPlacements((prev) =>
        prev.map((p) => {
          const key = p.placementKey || p.id || p.studentId;
          if (String(key) !== String(matchKey)) return p;
          return updater(p);
        })
      );
    },
    [invalidateOptimizationResult]
  );

  const ensurePlaceForStudent = React.useCallback(
    async (student, resolvedKey) => {
      const schoolId = selectedSchoolId || selectedSchool;
      if (!schoolId || !student) return resolvedKey || null;
      const keyCandidates = [
        resolvedKey,
        student.familyId,
        student.id,
        student.studentId,
        student.familyName,
        student.studentName,
      ].filter(Boolean);
      const key = keyCandidates.length ? String(keyCandidates[0]) : null;
      if (!key) return null;
      const exists = placeDirectory.some(
        (pd) => String(pd.key).toLowerCase() === String(key).toLowerCase()
      );
      const address = student.familyAddress || student.address;
      const addressString = address ? formatAddress(address) : null;
      if (exists) return key;
      if (!addressString) return null;
      const directory = {
        key,
        address: addressString,
        familyIds: student.familyId ? [student.familyId] : [],
        label: student.familyName || student.studentName || key,
        roles: ["family"],
      };
      try {
        await axios.post(
          `${baseUrl}/place-directories/bulk`,
          { schoolId, directories: [directory] },
          getRequestHeaders()
        );
        setPlaceDirectory((prev) => {
          const already = prev.some(
            (pd) => String(pd.key).toLowerCase() === String(key).toLowerCase()
          );
          return already ? prev : [...prev, directory];
        });
      } catch (err) {
        console.log(err);
        return null;
      }
      return key;
    },
    [formatAddress, placeDirectory, selectedSchool, selectedSchoolId]
  );

  const ensureStudentPlaceForReassignment = React.useCallback(
    async (student) => {
      if (!student) return null;
      const existingKey = resolveStudentPlace(student);
      return ensurePlaceForStudent(student, existingKey);
    },
    [ensurePlaceForStudent, resolveStudentPlace]
  );

  const getFamilyIdForEvent = React.useCallback((event) => {
    if (!event) return null;
    return event.familyId || event.familyID || event.family?.familyId || event.family?.id || null;
  }, []);

  const getStudentIdForEvent = React.useCallback((event) => {
    if (!event) return null;
    return event.studentId || event.studentID || event.placementKey || event.id || null;
  }, []);

  const getFamilyInfo = React.useCallback(
    async (familyId) => {
      if (!familyId) return null;
      const cacheKey = String(familyId);
      if (familyInfoCacheRef.current[cacheKey]) {
        return familyInfoCacheRef.current[cacheKey];
      }
      if (familyInfoRequestRef.current.has(cacheKey)) {
        return familyInfoRequestRef.current.get(cacheKey);
      }
      const request = axios
        .get(`${baseUrl}/school-management/${familyId}`, getRequestHeaders())
        .then((res) => {
          const data = res.data || null;
          if (data) {
            familyInfoCacheRef.current = {
              ...familyInfoCacheRef.current,
              [cacheKey]: data,
            };
            setFamilyInfoCache((prev) => ({ ...prev, [cacheKey]: data }));
          }
          return data;
        })
        .catch((error) => {
          console.log(error);
          return null;
        })
        .finally(() => {
          familyInfoRequestRef.current.delete(cacheKey);
        });
      familyInfoRequestRef.current.set(cacheKey, request);
      return request;
    },
    []
  );

  React.useEffect(() => {
    familyInfoCacheRef.current = familyInfoCache;
  }, [familyInfoCache]);

  const hydrateManualReassignmentStudent = React.useCallback(
    async (student) => {
      if (!student) return { student: null, error: "Missing student record" };
      const familyId = student.familyId;
      const localFamily = familyId
        ? (allStudents.length ? allStudents : students).find(
            (family) => String(family.familyId) === String(familyId)
          )
        : null;
      const fetchedFamily = familyId ? await getFamilyInfo(familyId) : null;
      const family = fetchedFamily || localFamily;
      const familyStudent = family?.students?.find(
        (candidate) =>
          String(candidate.studentId || candidate.id) === String(student.studentId) ||
          (student.studentName &&
            (candidate.studentName === student.studentName ||
              candidate.name === student.studentName))
      );
      const familyAddress =
        [
          student.familyAddress,
          student.address,
          family?.familyAddress,
          family?.FamilyAddress,
          family?.address,
          family?.Address,
          familyStudent?.address,
        ].find((address) => formatAddress(address)) || null;
      const hydratedStudent = {
        ...student,
        familyName: student.familyName || family?.familyName,
        studentName:
          student.studentName || familyStudent?.studentName || familyStudent?.name,
        instruments:
          instrumentValuesFor(student).length
            ? instrumentValuesFor(student)
            : familyStudent?.instrumentList || familyStudent?.instruments || [],
        lessonLength:
          student.lessonLength || familyStudent?.lessonLength || null,
        availability:
          student.availability ||
          familyStudent?.initialAvailability ||
          family?.initialAvailability ||
          null,
        familyAddress,
      };
      const familyDirectory = familyId
        ? placeDirectory.find(
            (entry) =>
              entry?.familyIds?.some(
                (entryFamilyId) => String(entryFamilyId) === String(familyId)
              ) ||
              String(entry?.key || "").toLowerCase() ===
                String(familyId).toLowerCase()
          )
        : null;
      const resolvedKey =
        familyDirectory?.key || resolveStudentPlace(hydratedStudent);
      const placeKey = await ensurePlaceForStudent(hydratedStudent, resolvedKey);
      if (!placeKey) {
        return {
          student: hydratedStudent,
          error: formatAddress(familyAddress)
            ? "Family location could not be created"
            : "Missing family address",
        };
      }
      return {
        student: { ...hydratedStudent, placeKey },
        error: null,
      };
    },
    [
      allStudents,
      ensurePlaceForStudent,
      formatAddress,
      getFamilyInfo,
      placeDirectory,
      resolveStudentPlace,
      students,
    ]
  );

  React.useEffect(() => {
    if (
      !selectedSchool ||
      !placeDirectoryLoaded ||
      !manualReassignmentStudents.length
    ) {
      return;
    }
    const hasFamilyDirectory = (student) =>
      placeDirectory.some(
        (entry) =>
          (student.placeKey &&
            String(entry?.key || "").toLowerCase() ===
              String(student.placeKey).toLowerCase()) ||
          (student.familyId &&
            (entry?.familyIds?.some(
              (familyId) => String(familyId) === String(student.familyId)
            ) ||
              String(entry?.key || "").toLowerCase() ===
                String(student.familyId).toLowerCase()))
      );
    const candidates = manualReassignmentStudents.filter((student) => {
      const studentKey = String(
        getStudentKey(student) || student.id || student.studentId || ""
      );
      return (
        studentKey &&
        !hasFamilyDirectory(student) &&
        !manualLocationHydrationRef.current.has(studentKey)
      );
    });
    if (!candidates.length) return;

    candidates.forEach((student) => {
      const studentKey = String(
        getStudentKey(student) || student.id || student.studentId
      );
      manualLocationHydrationRef.current.add(studentKey);
    });
    setResolvingManualLocations(true);

    Promise.all(candidates.map(hydrateManualReassignmentStudent))
      .then((results) => {
        const hydratedByKey = new Map();
        const failures = [];
        results.forEach((result, index) => {
          const original = candidates[index];
          const studentKey = String(
            getStudentKey(original) || original.id || original.studentId
          );
          if (result?.student && !result.error) {
            hydratedByKey.set(studentKey, result.student);
          } else {
            failures.push({
              name:
                original.studentName ||
                original.familyName ||
                "Student",
              reason: result?.error || "Family location could not be loaded",
            });
          }
        });
        if (hydratedByKey.size) {
          invalidateOptimizationResult();
          setManualReassignmentStudents((prev) =>
            prev.map((student) => {
              const studentKey = String(
                getStudentKey(student) || student.id || student.studentId || ""
              );
              return hydratedByKey.get(studentKey) || student;
            })
          );
        }
        if (failures.length) {
          setOptimizationSummary({
            type: "warning",
            message:
              failures.length === 1
                ? `${failures[0].name}: ${failures[0].reason}. Update the family address before optimizing.`
                : `${failures.length} students are missing usable family locations. Update their family addresses before optimizing.`,
          });
        }
      })
      .catch((error) => {
        console.log(error);
        setOptimizationSummary({
          type: "warning",
          message:
            "Family locations could not be loaded. Please try again before optimizing.",
        });
      })
      .finally(() => {
        candidates.forEach((student) => {
          const studentKey = String(
            getStudentKey(student) || student.id || student.studentId
          );
          manualLocationHydrationRef.current.delete(studentKey);
        });
        setResolvingManualLocations(
          manualLocationHydrationRef.current.size > 0
        );
      });
  }, [
    getStudentKey,
    hydrateManualReassignmentStudent,
    invalidateOptimizationResult,
    manualReassignmentStudents,
    placeDirectory,
    placeDirectoryLoaded,
    selectedSchool,
  ]);

  React.useEffect(() => {
    if (!showEventDriveModal || !eventModalData) {
      setEventModalFamily(null);
      setActiveMapMarkerId(null);
      setShowAllWeekMarkers(false);
      setShowEventMap(false);
      return;
    }
    setShowAllWeekMarkers(true);
    setShowEventMap(true);
    const familyId = getFamilyIdForEvent(eventModalData);
    if (!familyId) {
      setEventModalFamily(null);
      return;
    }
    let cancelled = false;
    getFamilyInfo(familyId).then((data) => {
      if (cancelled) return;
      setEventModalFamily(data || null);
      if (!data || !Array.isArray(data.students)) return;
      setEventModalData((prev) => {
        if (!prev) return prev;
        if (Array.isArray(prev.instruments) && prev.instruments.length) return prev;
        const studentId =
          prev.studentId || prev.studentID || prev.placementKey || prev.id || null;
        if (!studentId) return prev;
        const match = data.students.find(
          (stu) => String(stu.studentId || stu.id) === String(studentId)
        );
        if (!match) return prev;
        const instrumentList = Array.isArray(match.instrumentList) ? match.instrumentList : [];
        const instrumentLabel =
          match.instrument ||
          instrumentList?.[0]?.parentInstrument ||
          instrumentList?.[0]?.instrument ||
          instrumentList?.[0]?.name ||
          prev.instrument ||
          null;
        const nextLessonLength =
          Number(match.lessonLength) || prev.lessonLength || prev.rowLength || prev.lessonLength;
        const hasInstrumentUpdate = instrumentList.length > 0;
        const hasLessonUpdate = Number.isFinite(nextLessonLength) && nextLessonLength !== prev.lessonLength;
        if (!hasInstrumentUpdate && !hasLessonUpdate) return prev;
        const nextInstruments = hasInstrumentUpdate ? instrumentList : prev.instruments;
        return {
          ...prev,
          instruments: nextInstruments,
          instrument: hasInstrumentUpdate ? instrumentLabel : prev.instrument,
          lessonLength: hasLessonUpdate ? nextLessonLength : prev.lessonLength,
        };
      });
    });
    return () => {
      cancelled = true;
    };
  }, [eventModalData, getFamilyIdForEvent, getFamilyInfo, showEventDriveModal]);

  const normalizeInstrumentLabel = React.useCallback((inst) => {
    if (!inst) return null;
    if (typeof inst === "string") return inst.trim();
    return inst.parentInstrument || inst.instrument || inst.name || inst.label || null;
  }, []);

  const normalizeInstrumentList = React.useCallback(
    (list) => {
      if (!Array.isArray(list)) return [];
      const seen = new Set();
      const result = [];
      list.forEach((inst) => {
        const label = normalizeInstrumentLabel(inst);
        if (!label) return;
        const key = String(label).toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        if (inst && typeof inst === "object") {
          result.push({
            id: inst.id,
            name: inst.name || inst.instrument || inst.parentInstrument || label,
            uri: inst.uri,
            parentInstrument: inst.parentInstrument || inst.instrument || inst.name || label,
            instrumentGroup: inst.instrumentGroup,
          });
          return;
        }
        const catalogMatch = (allInstruments || []).find((entry) => {
          const entryLabel = normalizeInstrumentLabel(entry);
          return [
            entry?.id,
            entry?._id,
            entry?.instrumentId,
            entry?.InstrumentID,
            entry?.uri,
            entryLabel,
          ]
            .filter(Boolean)
            .some(
              (identifier) =>
                String(identifier).trim().toLowerCase() === key
            );
        });
        if (catalogMatch) {
          result.push({
            id: catalogMatch.id,
            name: catalogMatch.name || catalogMatch.instrument || catalogMatch.parentInstrument || label,
            uri: catalogMatch.uri,
            parentInstrument:
              catalogMatch.parentInstrument || catalogMatch.instrument || catalogMatch.name || label,
            instrumentGroup: catalogMatch.instrumentGroup,
          });
          return;
        }
        result.push({
          id: undefined,
          name: label,
          uri: undefined,
          parentInstrument: label,
          instrumentGroup: undefined,
        });
      });
      return result;
    },
    [allInstruments, normalizeInstrumentLabel]
  );

  const applyFamilyUpdateToState = React.useCallback((updatedFamily) => {
    if (!updatedFamily?.familyId) return;
    setFamilyInfoCache((prev) => ({ ...prev, [updatedFamily.familyId]: updatedFamily }));
    setAllStudents((prev) =>
      prev.map((fam) => (String(fam.familyId) === String(updatedFamily.familyId) ? updatedFamily : fam))
    );
    setStudents((prev) =>
      prev.map((fam) => (String(fam.familyId) === String(updatedFamily.familyId) ? updatedFamily : fam))
    );
  }, []);

  const updateStudentInStudentTwo = React.useCallback(
    async ({ event, lessonLength, instruments }) => {
      const familyId = getFamilyIdForEvent(event);
      const studentId = getStudentIdForEvent(event);
      if (!familyId || !studentId) return;

      const familyFromCache = (allStudents.length ? allStudents : students).find(
        (f) => String(f.familyId) === String(familyId)
      );
      const family = familyFromCache || (await getFamilyInfo(familyId));
      if (!family || !Array.isArray(family.students)) return;

      let found = false;
      const nextStudents = family.students.map((stu) => {
        if (String(stu.studentId || stu.id) !== String(studentId)) return stu;
        found = true;
        const next = { ...stu };
        if (lessonLength !== undefined && Number.isFinite(Number(lessonLength))) {
          next.lessonLength = Number(lessonLength);
        }
        if (instruments !== undefined) {
          next.instrumentList = normalizeInstrumentList(instruments);
        }
        return next;
      });
      if (!found) return;

      const payload = { ...family, students: nextStudents };
      try {
        const res = await axios.put(
          `${baseUrl}/school-management-new/${familyId}`,
          payload,
          getRequestHeaders()
        );
        applyFamilyUpdateToState(res.data || payload);
      } catch (err) {
        console.log(err);
      }
    },
    [
      allStudents,
      applyFamilyUpdateToState,
      getFamilyIdForEvent,
      getFamilyInfo,
      getStudentIdForEvent,
      normalizeInstrumentList,
      students,
    ]
  );

  const handleModalRemoveInstrument = React.useCallback(
    (inst) => {
      if (!eventModalData) return;
      const matchKey = eventModalData.placementKey || eventModalData.id || eventModalData.studentId;
      const target = inst?.parentInstrument || inst?.instrument || inst?.name || inst;
      updatePlacementField(matchKey, (p) => {
        const filtered = (p.instruments || []).filter((i) => {
          const name = i?.parentInstrument || i?.instrument || i?.name || i;
          return String(name) !== String(target);
        });
        return { ...p, instruments: filtered };
      });
      setEventModalData((prev) => {
        if (!prev) return prev;
        const filtered = (prev.instruments || []).filter((i) => {
          const name = i?.parentInstrument || i?.instrument || i?.name || i;
          return String(name) !== String(target);
        });
        return { ...prev, instruments: filtered };
      });
      const nextInstruments = (eventModalData.instruments || []).filter((i) => {
        const name = i?.parentInstrument || i?.instrument || i?.name || i;
        return String(name) !== String(target);
      });
      updateStudentInStudentTwo({
        event: { ...eventModalData, instruments: nextInstruments },
        instruments: nextInstruments,
      });
      if (currentScheduleId) {
        saveSchedule();
      }
    },
    [currentScheduleId, eventModalData, saveSchedule, updatePlacementField, updateStudentInStudentTwo]
  );

  const handleStartNewScheduleClick = React.useCallback(() => {
    setCurrentScheduleId(null);
    setPlannedPlacements([]);
    setPlacedStudentIds([]);
    setLockedStudentIds([]);
    setManualReassignmentStudents([]);
    manualLocationHydrationRef.current.clear();
    setResolvingManualLocations(false);
    setOverrideReassignmentStudents(null);
    setRemovedReassignmentIds([]);
    setScheduleName("");
    setOptimizeLedger([]);
    setOptimizationSummary(null);
    setOptimizationUndo(null);
    pendingOptimizationRef.current = false;
    setScheduleLoadedAt(0);
    setWeekOffset(0);
    setShowMissingTeacherDriveModal(false);
    setShowMissingReassignmentDriveModal(false);
    setShowMissingPlaceModal(false);
    setShowNoDriveModal(false);
    setShowEventDriveModal(false);
    setShowAddStudentModal(false);
    setShowLoadScheduleModal(false);
    setWelcomeStep("new");
  }, []);

  const handleSchoolSelection = React.useCallback((nextSchool) => {
    setSelectedSchool(nextSchool);
    setSelectedTeacherId("");
    setSavedSchedules([]);
    setCurrentScheduleId(null);
    setPlannedPlacements([]);
    setPlacedStudentIds([]);
    setLockedStudentIds([]);
    setManualReassignmentStudents([]);
    manualLocationHydrationRef.current.clear();
    setResolvingManualLocations(false);
    setOverrideReassignmentStudents(null);
    setRemovedReassignmentIds([]);
    setScheduleName("");
    setOptimizeLedger([]);
    setOptimizationSummary(null);
    setOptimizationUndo(null);
    pendingOptimizationRef.current = false;
    setScheduleLoadedAt(0);
    setWeekOffset(0);
    setShowMissingTeacherDriveModal(false);
    setShowMissingReassignmentDriveModal(false);
    setShowMissingPlaceModal(false);
    setShowNoDriveModal(false);
    setShowEventDriveModal(false);
    setShowAddStudentModal(false);
    setShowCalendarEventsModal(false);
    setOpenCalendarModalAfterFetch(false);
  }, []);

  const handleModalLessonLengthChange = React.useCallback(
    (newLen) => {
      if (!eventModalData) return;
      const len = Number(newLen);
      if (!Number.isFinite(len) || len <= 0) return;
      const matchKey = eventModalData.placementKey || eventModalData.id || eventModalData.studentId;
      updatePlacementField(matchKey, (p) => ({ ...p, lessonLength: len }));
      setEventModalData((prev) => (prev ? { ...prev, lessonLength: len } : prev));
      updateStudentInStudentTwo({ event: { ...eventModalData, lessonLength: len }, lessonLength: len });
      if (currentScheduleId) {
        saveSchedule();
      }
    },
    [currentScheduleId, eventModalData, saveSchedule, updatePlacementField, updateStudentInStudentTwo]
  );

  const handleModalInstrumentChange = React.useCallback(
    (instrument) => {
      if (!eventModalData) return;
      const matchKey = eventModalData.placementKey || eventModalData.id || eventModalData.studentId;
      if (!instrument) return;
      updatePlacementField(matchKey, (p) => {
        const existing = p.instruments || [];
        if (existing.some((i) => (i.parentInstrument || i.instrument || i.name) === instrument)) {
          return { ...p, instrument };
        }
        return {
          ...p,
          instrument,
          instruments: [...existing, { parentInstrument: instrument, instrument }],
        };
      });
      setEventModalData((prev) => {
        if (!prev) return prev;
        const existing = prev.instruments || [];
        if (existing.some((i) => (i.parentInstrument || i.instrument || i.name) === instrument)) {
          return { ...prev, instrument };
        }
        return {
          ...prev,
          instrument,
          instruments: [...existing, { parentInstrument: instrument, instrument }],
        };
      });
      const existing = eventModalData.instruments || [];
      const nextInstruments = existing.some((i) => (i.parentInstrument || i.instrument || i.name) === instrument)
        ? existing
        : [...existing, { parentInstrument: instrument, instrument }];
      updateStudentInStudentTwo({
        event: { ...eventModalData, instruments: nextInstruments, instrument },
        instruments: nextInstruments,
      });
      if (currentScheduleId) {
        saveSchedule();
      }
    },
    [currentScheduleId, eventModalData, saveSchedule, updatePlacementField, updateStudentInStudentTwo]
  );

  const handleRemoveFromCalendar = React.useCallback(async () => {
    if (!eventModalData) return;
    const matchKey = eventModalData.placementKey || eventModalData.id || eventModalData.studentId;
    if (!matchKey) return;
    const familyId = getFamilyIdForEvent(eventModalData);
    const studentId = getStudentIdForEvent(eventModalData);
    const familyStudent =
      eventModalFamily?.students?.find(
        (stu) => String(stu.studentId || stu.id) === String(studentId)
      ) || null;
    const sid =
      eventModalData.placementKey ||
      (familyId && studentId ? `${familyId}-${studentId}` : studentId || eventModalData.id || eventModalData.studentName);
    if (!sid) return;

    invalidateOptimizationResult();

    setPlannedPlacements((prev) =>
      prev.filter((p) => (p.placementKey || p.id || p.studentId) !== matchKey)
    );

    const removeIds = [
      sid,
      matchKey,
      studentId,
      eventModalData.id,
      eventModalData.placementKey,
    ]
      .filter(Boolean)
      .map((id) => String(id));

    setPlacedStudentIds((prev) => prev.filter((id) => !removeIds.includes(String(id))));
    setLockedStudentIds((prev) => prev.filter((id) => !removeIds.includes(String(id))));
    setRemovedReassignmentIds((prev) => prev.filter((id) => String(id) !== String(sid)));

    const instruments = normalizeInstrumentList(
      familyStudent?.instrumentList ||
        eventModalData.instruments ||
        (eventModalData.instrument ? [eventModalData.instrument] : [])
    );

    const reassignmentStudent = {
      id: sid,
      familyId: familyId || eventModalData.familyId || eventModalData.familyID,
      familyName: eventModalFamily?.familyName || eventModalData.familyName,
      studentId: studentId || eventModalData.studentId,
      studentName: familyStudent?.studentName || eventModalData.studentName,
      instruments,
      lessonLength: familyStudent?.lessonLength || eventModalData.lessonLength,
      availability:
        familyStudent?.initialAvailability ||
        eventModalFamily?.initialAvailability ||
        eventModalData.availability,
      school: (eventModalFamily?.school || eventModalData.school || selectedSchool || "").toLowerCase(),
      familyAddress: eventModalFamily?.familyAddress || eventModalFamily?.address || eventModalData.familyAddress,
      teacherId: eventModalData.teacherId || eventModalData.teacherID || eventModalData.TeacherID,
    };

    if (Array.isArray(overrideReassignmentStudents)) {
      setOverrideReassignmentStudents((prev) => {
        const base = Array.isArray(prev) ? prev : [];
        const exists = base.some((s) => String(s.id || s.studentId) === String(sid));
        return exists ? base : [...base, reassignmentStudent];
      });
    } else {
      setManualReassignmentStudents((prev) => {
        const exists = prev.some((s) => String(s.id || s.studentId) === String(sid));
        return exists ? prev : [...prev, reassignmentStudent];
      });
    }

    await ensureStudentPlaceForReassignment(reassignmentStudent);
    setEventModalData(null);
    setShowEventDriveModal(false);
  }, [
    ensureStudentPlaceForReassignment,
    eventModalData,
    eventModalFamily,
    getFamilyIdForEvent,
    getStudentIdForEvent,
    invalidateOptimizationResult,
    normalizeInstrumentList,
    overrideReassignmentStudents,
    selectedSchool,
  ]);

  const handleLoadExistingSchedule = React.useCallback(
    (schedule) => {
      if (!schedule) return;
      const targetWeek = startOfWeek(new Date(schedule.weekStart || new Date()), { weekStartsOn: 1 });
      const baseWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
      const diffWeeks = differenceInCalendarWeeks(targetWeek, baseWeek);
      setWeekOffset(diffWeeks);
      const hydratePlacement = (p) => {
        const canonicalKey = createCanonicalStudentKey(p);
        if (p.studentName || p.familyName) {
          return { ...p, placementKey: canonicalKey || p.placementKey };
        }
        let studentName = p.studentName || null;
        let familyName = p.familyName || null;
        const families = allStudents.length ? allStudents : students;
        const familyMatch = families.find((f) => String(f.familyId) === String(p.familyId));
        if (familyMatch) {
          familyName = familyMatch.familyName || familyName;
          const studentMatch =
            familyMatch.students?.find((s) => String(s.studentId) === String(p.studentId)) || null;
          if (studentMatch) {
            studentName = studentMatch.studentName || studentName;
          }
        } else {
          // fallback: search any family by studentId
          families.some((f) => {
            const sm = f.students?.find((s) => String(s.studentId) === String(p.studentId));
            if (sm) {
              familyName = f.familyName || familyName;
              studentName = sm.studentName || studentName;
              return true;
            }
            return false;
          });
        }
        return {
          ...p,
          placementKey: canonicalKey || p.placementKey,
          studentName,
          familyName,
          label: studentName || familyName || p.label,
        };
      };
      const hydratedPlacements = (schedule.placements || []).map(hydratePlacement);
      setPlannedPlacements(hydratedPlacements);
      const placementIds = hydratedPlacements
        .map((p) => getStudentKey(p))
        .filter(Boolean);
      const normalizedLocks = (schedule.lockedStudentIds || []).map((savedKey) => {
        const placement = hydratedPlacements.find((p) =>
          [p.placementKey, p.id, p.studentId, getStudentKey(p)]
            .filter(Boolean)
            .some((candidate) => String(candidate) === String(savedKey))
        );
        return placement ? getStudentKey(placement) : String(savedKey);
      });
      setPlacedStudentIds(Array.from(new Set(placementIds)));
      setLockedStudentIds(Array.from(new Set(normalizedLocks)));
      setManualReassignmentStudents(schedule.manualReassignmentStudents || []);
      setOverrideReassignmentStudents(schedule.overrideReassignmentStudents || []);
      setScheduleName(schedule.label || "");
      setCurrentScheduleId(schedule._id || schedule.id || null);
      setRemovedReassignmentIds([]);
      invalidateOptimizationResult();
      setShowLoadScheduleModal(false);
      setWelcomeStep(null);
      setScheduleLoadedAt(Date.now());
    },
    [allStudents, getStudentKey, invalidateOptimizationResult, students]
  );

  React.useEffect(() => {
    if (!plannedPlacements.length) return;
    const families = allStudents.length ? allStudents : students;
    if (!families.length) return;
    let changed = false;
    const hydrated = plannedPlacements.map((p) => {
      if (!p || (p.studentName && p.familyName)) return p;
      let studentName = p.studentName || null;
      let familyName = p.familyName || null;
      const familyMatch = families.find((f) => String(f.familyId) === String(p.familyId));
      if (familyMatch) {
        familyName = familyMatch.familyName || familyName;
        const studentMatch =
          familyMatch.students?.find((s) => String(s.studentId) === String(p.studentId)) || null;
        if (studentMatch) {
          studentName = studentMatch.studentName || studentName;
        }
      } else {
        families.some((f) => {
          const sm = f.students?.find((s) => String(s.studentId) === String(p.studentId));
          if (sm) {
            familyName = f.familyName || familyName;
            studentName = sm.studentName || studentName;
            return true;
          }
          return false;
        });
      }
      if (studentName !== p.studentName || familyName !== p.familyName) {
        changed = true;
        return { ...p, studentName, familyName, label: studentName || familyName || p.label };
      }
      return p;
    });
    if (changed) {
      setPlannedPlacements(hydrated);
    }
  }, [allStudents, plannedPlacements, students]);

  React.useEffect(() => {
    setStudentSearchTerm("");
    setOverrideReassignmentStudents(null);
  }, [selectedSchool]);

  // Load saved schedules for the selected school
  React.useEffect(() => {
    let cancelled = false;
    const schoolId = selectedSchoolId || selectedSchool;
    // Do not leave the previous school's schedules selectable while this
    // school's request is in flight.
    setSavedSchedules([]);
    if (!schoolId) {
      setLoadingSchedules(false);
      return undefined;
    }
    setLoadingSchedules(true);
    console.log("Fetching schedules", { schoolId });
    axios
      .get(`${baseUrl}/schedules`, {
        params: { schoolId },
        ...getRequestHeaders(),
      })
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.schedules || res.data || [];
        const filtered = (Array.isArray(list) ? list : []).filter(
          (s) => String(s?.status || "").toLowerCase() !== "archived"
        );
        setSavedSchedules(filtered);
      })
      .catch(() => {
        if (!cancelled) setSavedSchedules([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSchedules(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSchool, selectedSchoolId]);

  const handleStartSchedulingWithoutTeacher = React.useCallback(async () => {
    if (!selectedSchool && !selectedSchoolId) return;
    const id = await createSchedule();
    if (id) setCurrentScheduleId(id);
    setWelcomeStep(null);
  }, [createSchedule, selectedSchool, selectedSchoolId]);

  const handleLoadTeacherStudentsAndStart = React.useCallback(async () => {
    if (!selectedTeacherId) {
      setWelcomeStep(null);
      return;
    }
    const sourceFamilies = allStudents.length ? allStudents : students;
    const additions = [];
    const seen = new Set();
    sourceFamilies.forEach((family) => {
      if (selectedSchool && family.school?.toLowerCase() !== selectedSchool) return;
      family.students?.forEach((student) => {
        const tid = getStudentTeacherId(student);
        if (String(tid) !== String(selectedTeacherId)) return;
        const status = student.studentStatus || student.status || student.Status;
        if (status && String(status).toLowerCase() !== "active") return;
        const uniqueId = `${family.familyId}-${student.studentId}`;
        if (seen.has(uniqueId) || placedStudentIds.includes(uniqueId)) return;
        additions.push({
          id: uniqueId,
          familyId: family.familyId,
          familyName: family.familyName,
          studentId: student.studentId,
          studentName: student.studentName,
          instruments: student.instrumentList || student.instruments || [],
          lessonLength: student.lessonLength,
          availability: student.initialAvailability || family.initialAvailability,
          school: family.school?.toLowerCase(),
          familyAddress: family.familyAddress || family.address || student.address,
          teacherId: tid,
        });
        seen.add(uniqueId);
      });
    });
    if (additions.length) {
      await Promise.all(additions.map((s) => ensureStudentPlaceForReassignment(s)));
    }
    const scheduleId = await createSchedule({
      placements: plannedPlacements,
      lockedStudentIds,
      manualReassignmentStudents,
      overrideReassignmentStudents: additions,
    });
    if (scheduleId) setCurrentScheduleId(scheduleId);
    invalidateOptimizationResult();
    setManualReassignmentStudents([]);
    setOverrideReassignmentStudents(additions);
    setPromptReassignmentDriveTimes(true);
    setWelcomeStep(null);
  }, [
    allStudents,
    createSchedule,
    ensureStudentPlaceForReassignment,
    getStudentTeacherId,
    invalidateOptimizationResult,
    lockedStudentIds,
    manualReassignmentStudents,
    plannedPlacements,
    placedStudentIds,
    selectedSchool,
    selectedTeacherId,
    setManualReassignmentStudents,
    students,
  ]);

  React.useEffect(() => {
    if (!currentScheduleId || welcomeStep) return;
    if (saveScheduleDebounceRef.current) {
      clearTimeout(saveScheduleDebounceRef.current);
    }
    saveScheduleDebounceRef.current = setTimeout(() => {
      latestSaveScheduleRef.current?.();
    }, 600);
    return () => {
      if (saveScheduleDebounceRef.current) {
        clearTimeout(saveScheduleDebounceRef.current);
      }
    };
  }, [
    currentScheduleId,
    welcomeStep,
    plannedPlacements,
    lockedStudentIds,
    manualReassignmentStudents,
    overrideReassignmentStudents,
    scheduleName,
  ]);

  const parseEventDate = React.useCallback((ev) => {
    const candidates = [
      ev.dateTime,
      ev.dateTimeUtc,
      ev.DateTime,
      ev.DateTimeUtc,
      ev.date,
      ev.Date,
      ev.startDate,
      ev.startTime,
      ev.parsedDate,
      ev.start?.dateTime,
      ev.start?.date,
    ].filter(Boolean);
    for (const c of candidates) {
      if (c instanceof Date && !isNaN(c)) return c;
      if (typeof c === "number") {
        const dNum = new Date(c);
        if (dNum && !isNaN(dNum)) return dNum;
      }
      const d = new Date(c);
      if (d && !isNaN(d)) return d;
    }
    return null;
  }, []);

  const teacherNameById = React.useMemo(() => {
    const map = new Map();
    teachers.forEach((t) => {
      map.set(String(t.TeacherID), t.Name || t.name || "Teacher");
    });
    return map;
  }, [teachers]);

  const selectedTeacherInstruments = React.useMemo(() => {
    if (!selectedTeacherId) return [];
    const teacher = teachers.find((t) => String(t.TeacherID) === String(selectedTeacherId));
    if (!teacher?.InstrumentList) return [];
    return teacher.InstrumentList.map(
      (inst) => inst.instrument || inst.name || inst.label || inst.parentInstrument
    ).filter(Boolean);
  }, [selectedTeacherId, teachers]);

  const selectedTeacherName = React.useMemo(() => {
    if (!selectedTeacherId) return null;
    return teacherNameById.get(String(selectedTeacherId)) || null;
  }, [selectedTeacherId, teacherNameById]);

  const optimizationLedgerEntries = React.useMemo(() => {
    if (!plannedPlacements.length) return [];
    const entries = [];
    plannedPlacements.forEach((placement) => {
      if (!placement) return;
      const sid = getStudentKey({
        studentId: placement.studentId || placement.placementKey || placement.id,
        placementKey: placement.placementKey,
        id: placement.id,
        studentName: placement.studentName,
        familyName: placement.familyName,
      });
      const start = Number(placement.startMinutes) || 0;
      const timeLabel = `${Math.floor(start / 60).toString().padStart(2, "0")}:${(start % 60)
        .toString()
        .padStart(2, "0")}`;
      entries.push({
        student: placement.studentName || placement.familyName || sid,
        teacher: teacherNameById.get(String(placement.teacherId)) || placement.teacherId,
        day: weekDays[placement.dayIndex] || "Day",
        time: timeLabel,
      });
    });
    const dayOrder = new Map(weekDays.map((day, idx) => [day, idx]));
    entries.sort((a, b) => {
      const aDay = dayOrder.get(a.day) ?? 99;
      const bDay = dayOrder.get(b.day) ?? 99;
      if (aDay === bDay) return a.time.localeCompare(b.time);
      return aDay - bDay;
    });
    return entries;
  }, [getStudentKey, plannedPlacements, teacherNameById]);

  const availabilityByTeacher = React.useMemo(() => {
    const map = new Map();
    teachers.forEach((t) => {
      if (t.AvailabilityDetails) {
        map.set(String(t.TeacherID), normalizeAvailability(t.AvailabilityDetails));
      }
    });
    return map;
  }, [normalizeAvailability, teachers]);

  const placeKeyByTeacher = React.useMemo(() => {
    const map = new Map();
    placeDirectory.forEach((entry) => {
      if (Array.isArray(entry.teacherIds)) {
        entry.teacherIds.forEach((tid) => map.set(String(tid), entry.key));
      }
    });
    return map;
  }, [placeDirectory]);

  const computeDriveMinutesForPlacements = React.useCallback(
    (placements) => {
      if (!Array.isArray(placements) || !placements.length) return 0;
      const byTeacherDay = new Map();
      let complete = true;
      placements.forEach((placement) => {
        if (!placement) return;
        const tid = String(placement.teacherId || "");
        if (!tid) {
          complete = false;
          return;
        }
        const place = resolveStudentPlace(placement);
        if (!place) {
          complete = false;
          return;
        }
        const key = `${tid}|${placement.dayIndex}`;
        if (!byTeacherDay.has(key)) byTeacherDay.set(key, []);
        byTeacherDay.get(key).push({
          start: placement.startMinutes,
          end: placement.startMinutes + (placement.lessonLength || 30),
          place,
        });
      });
      let total = 0;
      byTeacherDay.forEach((events, key) => {
        const [tid] = key.split("|");
        const home = placeKeyByTeacher.get(String(tid)) || null;
        if (!events.length) return;
        if (!home) {
          complete = false;
          return;
        }
        events.sort((a, b) => a.start - b.start);
        const firstPlace = events[0].place || home;
        const lastPlace = events[events.length - 1].place || home;
        const addLeg = (fromKey, toKey) => {
          if (String(fromKey) === String(toKey)) return;
          const minutes = minutesBetween(fromKey, toKey);
          if (!Number.isFinite(minutes)) {
            complete = false;
            return;
          }
          total += minutes;
        };
        addLeg(home, firstPlace);
        for (let i = 0; i < events.length - 1; i++) {
          addLeg(events[i].place, events[i + 1].place);
        }
        addLeg(lastPlace, home);
      });
      return complete ? Math.round(total) : null;
    },
    [minutesBetween, placeKeyByTeacher, resolveStudentPlace]
  );

  const optimizationDriveMinutes = React.useMemo(
    () => computeDriveMinutesForPlacements(plannedPlacements),
    [computeDriveMinutesForPlacements, plannedPlacements]
  );

  const placeDirectoryByKey = React.useMemo(() => {
    const map = new Map();
    placeDirectory.forEach((entry) => {
      if (!entry?.key) return;
      const key = String(entry.key);
      map.set(key, entry);
      map.set(key.toLowerCase(), entry);
    });
    return map;
  }, [placeDirectory]);

  const eventModalLocation = React.useMemo(() => {
    if (!eventModalData) return null;
    const familyId = getFamilyIdForEvent(eventModalData);
    const directKey =
      eventModalData.placeKey || eventModalData.place_key || eventModalData.placeId || null;
    const resolvedKey = directKey || resolveStudentPlace(eventModalData);
    const keyLower = resolvedKey ? String(resolvedKey).toLowerCase() : null;

    let entry = null;
    if (resolvedKey) {
      entry = placeDirectory.find((pd) => String(pd.key) === String(resolvedKey));
    }
    if (!entry && keyLower) {
      entry = placeDirectory.find((pd) => String(pd.key || "").toLowerCase() === keyLower);
    }
    if (!entry && familyId) {
      entry = placeDirectory.find((pd) =>
        Array.isArray(pd.familyIds) && pd.familyIds.some((id) => String(id) === String(familyId))
      );
    }

    const lat = entry?.lat ?? entry?.location?.lat ?? eventModalData?.familyAddress?.lat ?? null;
    const lng = entry?.lng ?? entry?.location?.lng ?? eventModalData?.familyAddress?.lng ?? null;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    return {
      lat: Number(lat),
      lng: Number(lng),
      label: entry?.label || eventModalData.studentName || eventModalData.familyName || "Student",
    };
  }, [eventModalData, getFamilyIdForEvent, placeDirectory, resolveStudentPlace]);

  const eventModalTeacherLocation = React.useMemo(() => {
    if (!eventModalData) return null;
    const teacherId =
      eventModalData.teacherId ||
      eventModalData.teacherID ||
      eventModalData.TeacherID ||
      null;
    if (!teacherId) return null;
    const placeKey = placeKeyByTeacher.get(String(teacherId));
    if (!placeKey) return null;
    const entry = placeDirectory.find((pd) => String(pd.key) === String(placeKey));
    const lat = entry?.lat ?? entry?.location?.lat ?? null;
    const lng = entry?.lng ?? entry?.location?.lng ?? null;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return {
      lat: Number(lat),
      lng: Number(lng),
      label: entry?.label || "Teacher",
    };
  }, [eventModalData, placeDirectory, placeKeyByTeacher]);

  const resolveEventLocation = React.useCallback(
    (ev) => {
      if (!ev) return null;
      const fid = ev.familyId || ev.familyID;
      let placeKey = null;
      if (fid && placeKeyLookup.familyToPlace.has(String(fid))) {
        placeKey = placeKeyLookup.familyToPlace.get(String(fid));
      } else {
        const addr = formatAddress(ev.familyAddress || ev.address);
        if (addr && placeKeyLookup.addressToPlace.has(addr.toLowerCase())) {
          placeKey = placeKeyLookup.addressToPlace.get(addr.toLowerCase());
        } else {
          const label = (ev.familyName || ev.studentName || ev.label || addr || "").toLowerCase();
          if (label && placeKeyLookup.labelToPlace.has(label)) {
            placeKey = placeKeyLookup.labelToPlace.get(label);
          }
        }
      }
      const entry =
        (placeKey ? placeDirectoryByKey.get(String(placeKey)) : null) ||
        (placeKey ? placeDirectoryByKey.get(String(placeKey).toLowerCase()) : null);
      const lat =
        entry?.lat ??
        entry?.location?.lat ??
        ev?.familyAddress?.lat ??
        ev?.address?.lat ??
        null;
      const lng =
        entry?.lng ??
        entry?.location?.lng ??
        ev?.familyAddress?.lng ??
        ev?.address?.lng ??
        null;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return {
        lat: Number(lat),
        lng: Number(lng),
        key: entry?.key || placeKey || null,
        label: entry?.label || ev.studentName || ev.familyName || "Student",
      };
    },
    [formatAddress, placeDirectoryByKey, placeKeyLookup]
  );

  const mapMarkers = React.useMemo(() => {
    const MAX_MAP_MARKERS = 80;
    const markers = [];
    const usedKeys = new Set();
    const formatLessonTime = (ev) => {
      const candidates = [
        ev?.startDate,
        ev?.parsedDate,
        ev?.dateTime,
        ev?.date,
        ev?.start?.dateTime,
        ev?.start?.date,
      ].filter(Boolean);
      for (const c of candidates) {
        const d = c instanceof Date ? c : new Date(c);
        if (d && !isNaN(d)) return format(d, "EEEE h:mma");
      }
      if (Number.isFinite(ev?.dayIndex) && Number.isFinite(ev?.startMinutes)) {
        const h = Math.floor(ev.startMinutes / 60)
          .toString()
          .padStart(2, "0");
        const m = (ev.startMinutes % 60).toString().padStart(2, "0");
        const day = weekDays[ev.dayIndex] || "Day";
        return `${day} ${h}:${m}`;
      }
      return null;
    };
    if (eventModalLocation) {
      const familyName =
        eventModalFamily?.familyName ||
        eventModalData?.familyName ||
        eventModalData?.studentName ||
        "Family";
      const address = formatAddress(eventModalFamily?.address || eventModalData?.familyAddress) || "";
      const studentsCount = Array.isArray(eventModalFamily?.students) ? eventModalFamily.students.length : null;
      const instrumentNames = [];
      (eventModalFamily?.students || []).forEach((stu) => {
        (stu.instrumentList || []).forEach((inst) => {
          const name = inst?.name || inst?.parentInstrument || inst?.instrument;
          if (name && !instrumentNames.includes(name)) instrumentNames.push(name);
        });
      });
      markers.push({
        id: `family-${eventModalFamily?.familyId || eventModalData?.familyId || eventModalData?.studentId || "student"}`,
        name: familyName,
        address,
        students: studentsCount,
        inst: instrumentNames,
        location: { lat: eventModalLocation.lat, lng: eventModalLocation.lng },
        role: "selected",
        lessonTime: formatLessonTime(eventModalData),
      });
      usedKeys.add(
        String(eventModalLocation.key || eventModalData?.placeKey || eventModalData?.familyId || "").toLowerCase()
      );
    }

    if (showAllWeekMarkers) {
      const modalTeacherId =
        eventModalData?.teacherId ||
        eventModalData?.teacherID ||
        eventModalData?.TeacherID ||
        null;
      const otherEvents = weekViewEvents.filter((ev) => {
        const tid = ev.teacherId || ev.teacherID || ev.TeacherID;
        return modalTeacherId && String(tid) === String(modalTeacherId);
      });
      for (let idx = 0; idx < otherEvents.length; idx += 1) {
        if (markers.length >= MAX_MAP_MARKERS) break;
        const ev = otherEvents[idx];
        const isSameStudent =
          (eventModalData?.studentId && String(ev.studentId || ev.studentID) === String(eventModalData.studentId)) ||
          (eventModalData?.familyId && String(ev.familyId || ev.familyID) === String(eventModalData.familyId));
        if (isSameStudent) continue;
        const loc = resolveEventLocation(ev);
        if (!loc) continue;
        const dedupeKey = String(loc.key || `${loc.lat},${loc.lng}`).toLowerCase();
        if (usedKeys.has(dedupeKey)) continue;
        usedKeys.add(dedupeKey);
        const instLabel = ev.instrument || ev.lessonName || ev.name || null;
        const inst = instLabel ? [instLabel] : [];
        const address = formatAddress(ev.familyAddress || ev.address) || "";
        const timeKey = ev.startDate || ev.parsedDate || ev.dateTime || ev.date || "";
        markers.push({
          id: `event-${ev.studentId || ev.id || "student"}-${timeKey || idx}-${idx}`,
          name: ev.familyName || ev.studentName || "Student",
          address,
          students: null,
          inst,
          location: { lat: loc.lat, lng: loc.lng },
          role: "other",
          lessonTime: formatLessonTime(ev),
        });
      }
    }

    if (eventModalTeacherLocation) {
      const teacherId =
        eventModalData?.teacherId ||
        eventModalData?.teacherID ||
        eventModalData?.TeacherID ||
        null;
      const teacher = teachers.find((t) => String(t.TeacherID) === String(teacherId));
      const instrumentNames = [];
      (teacher?.InstrumentList || []).forEach((inst) => {
        const name = inst?.parentInstrument || inst?.instrument || inst?.name;
        if (name && !instrumentNames.includes(name)) instrumentNames.push(name);
      });
      markers.push({
        id: `teacher-${teacherId || "teacher"}`,
        name: teacherNameById.get(String(teacherId)) || "Teacher",
        address: eventModalTeacherLocation.label || "",
        students: null,
        inst: instrumentNames,
        location: { lat: eventModalTeacherLocation.lat, lng: eventModalTeacherLocation.lng },
        role: "teacher",
        lessonTime: null,
      });
    }
    return markers;
  }, [
    eventModalData,
    eventModalFamily,
    eventModalLocation,
    eventModalTeacherLocation,
    formatAddress,
    resolveEventLocation,
    showAllWeekMarkers,
    teacherNameById,
    teachers,
    weekViewEvents,
  ]);

  const instrumentsForModal = React.useMemo(() => {
    if (eventModalData?.instruments?.length) return eventModalData.instruments;
    const studentId =
      eventModalData?.studentId || eventModalData?.studentID || eventModalData?.placementKey || eventModalData?.id || null;
    if (!studentId || !eventModalFamily?.students) return [];
    const match = eventModalFamily.students.find(
      (stu) => String(stu.studentId || stu.id) === String(studentId)
    );
    return Array.isArray(match?.instrumentList) ? match.instrumentList : [];
  }, [eventModalData, eventModalFamily]);

  const driveEdgesForPlaceDirectory = React.useMemo(() => {
    if (!eventModalData || !driveEdges.length) return [];
    const directKey =
      eventModalData.placeKey || eventModalData.place_key || eventModalData.placeId || null;
    const resolvedKey = directKey || resolveStudentPlace(eventModalData);
    if (!resolvedKey) return [];
    const targetKey = String(resolvedKey);
    const labelByKey = new Map();
    placeDirectory.forEach((pd) => {
      if (!pd?.key) return;
      labelByKey.set(String(pd.key), pd.label || pd.key);
    });
    return driveEdges
      .filter((edge) => {
        const originKey = edge.origin?.key || edge.origin || edge.fromKey || edge.from;
        const destKey = edge.destination?.key || edge.destination || edge.toKey || edge.to;
        return String(originKey) === targetKey || String(destKey) === targetKey;
      })
      .map((edge, idx) => {
        const originKey = edge.origin?.key || edge.origin || edge.fromKey || edge.from || "";
        const destKey = edge.destination?.key || edge.destination || edge.toKey || edge.to || "";
        return {
          id: edge.id || edge._id || `${originKey}=>${destKey}-${idx}`,
          fromLabel: edge.origin?.label || labelByKey.get(String(originKey)) || originKey,
          toLabel: edge.destination?.label || labelByKey.get(String(destKey)) || destKey,
          minutes: normalizeDriveMinutes(edge.minutes),
        };
      })
      .sort((a, b) => {
        const ma = normalizeDriveMinutes(a.minutes);
        const mb = normalizeDriveMinutes(b.minutes);
        if (ma != null && mb != null) return ma - mb;
        if (ma != null) return -1;
        if (mb != null) return 1;
        return 0;
      });
  }, [driveEdges, eventModalData, placeDirectory, resolveStudentPlace]);


  const activeMapMarker = React.useMemo(
    () => mapMarkers.find((marker) => marker.id === activeMapMarkerId) || null,
    [activeMapMarkerId, mapMarkers]
  );

  const studentsDefiningTeacherScope = React.useMemo(() => {
    const lockedKeys = new Set((lockedStudentIds || []).map(String));
    const scoped = new Map();
    studentsNeedingReassignment.forEach((student) => {
      const key = getStudentKey(student);
      if (key) scoped.set(String(key), student);
    });
    plannedPlacements.forEach((placement) => {
      const key = getStudentKey(placement);
      if (!key || lockedKeys.has(String(key))) return;
      if (!scoped.has(String(key))) scoped.set(String(key), placement);
    });
    return Array.from(scoped.values());
  }, [getStudentKey, lockedStudentIds, plannedPlacements, studentsNeedingReassignment]);

  const teacherOptions = React.useMemo(() => {
    // Once students are in scope, only teachers who can teach at least one of
    // their instruments should participate in calendar loading, manual
    // placement, or optimization validation.
    if (!selectedSchool) return [];
    const activeTeachers = teachers
      .filter(
        (teacher) =>
          teacher.School?.toLowerCase() === selectedSchool &&
          String(teacher.Status || "").toLowerCase() === "active"
      );
    const compatibleTeachers = studentsDefiningTeacherScope.length
      ? activeTeachers.filter((teacher) =>
          studentsDefiningTeacherScope.some((student) =>
            haveCompatibleInstruments(
              instrumentValuesFor(teacher),
              instrumentValuesFor(student)
            )
          )
        )
      : activeTeachers;
    return compatibleTeachers
      .map((teacher) => ({
        value: teacher.TeacherID,
        label: teacher.Name,
      }))
      .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  }, [selectedSchool, studentsDefiningTeacherScope, teachers]);

  const teacherOptionsAll = React.useMemo(() => {
    // All teachers who have taught at the school (any status)
    if (!selectedSchool) return [];
    const list = teachers
      .filter((teacher) => teacher.School?.toLowerCase() === selectedSchool)
      .map((teacher) => ({
        value: teacher.TeacherID,
        label: teacher.Name,
      }));
    list.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    return list;
  }, [teachers, selectedSchool]);

  React.useEffect(() => {
    if (welcomeStep !== null || !selectedTeacherId) return;
    const remainsCompatible = teacherOptions.some(
      (option) => String(option.value) === String(selectedTeacherId)
    );
    if (!remainsCompatible) setSelectedTeacherId("");
  }, [selectedTeacherId, teacherOptions, welcomeStep]);

  const teacherIdsForCalendar = React.useMemo(() => {
    const ids = new Set();
    teacherOptions.forEach((t) => {
      if (!t.value) return;
      const teacher = teachers.find(
        (candidate) => String(candidate.TeacherID) === String(t.value)
      );
      const availabilityByDay = normalizeAvailability(
        teacher?.AvailabilityDetails || {}
      );
      if (!hasUsableTeacherAvailability({ availabilityByDay })) return;
      ids.add(String(t.value));
    });
    return Array.from(ids);
  }, [normalizeAvailability, teacherOptions, teachers]);

  // Fetch next-month calendar events for all teachers in the selected school
  React.useEffect(() => {
    let cancelled = false;
    if (!selectedSchoolId) {
      setTeacherCalendarEvents([]);
      setCalendarEventsLoaded(false);
      setCalendarEventsLoadError(null);
      if (openCalendarModalAfterFetch) {
        setOpenCalendarModalAfterFetch(false);
      }
      return undefined;
    }
    if (!teacherIdsForCalendar.length) {
      setTeacherCalendarEvents([]);
      // There are no teachers whose availability could participate in the
      // optimization, so there are no relevant calendars left to load.
      setCalendarEventsLoaded(true);
      setCalendarEventsLoadError(null);
      if (openCalendarModalAfterFetch) {
        setOpenCalendarModalAfterFetch(false);
      }
      return undefined;
    }
    setCalendarEventsLoaded(false);
    setCalendarEventsLoadError(null);
    Promise.allSettled(
      teacherIdsForCalendar.map((tid) =>
        axios.get(`${baseUrl}/calendar/teacher/${tid}`, getRequestHeaders())
      )
    ).then((results) => {
      if (cancelled) return;
      const failedCount = results.filter((result) => result.status === "rejected").length;
      const merged = results
        .filter((result) => result.status === "fulfilled")
        .flatMap((result) =>
          Array.isArray(result.value?.data) ? result.value.data : []
        );
      setTeacherCalendarEvents(merged);
      setCalendarEventsLoadError(
        failedCount
          ? `${failedCount} teacher calendar${failedCount === 1 ? "" : "s"} failed to load.`
          : null
      );
      setCalendarEventsLoaded(true);
      if (openCalendarModalAfterFetch) {
        setOpenCalendarModalAfterFetch(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    calendarReloadToken,
    openCalendarModalAfterFetch,
    parseEventDate,
    selectedSchoolId,
    teacherIdsForCalendar,
  ]);

  // Refresh place directory when modal opens to ensure badges are current
  React.useEffect(() => {
    if (!showCalendarEventsModal || !selectedSchoolId) return;
    axios
      .get(`${baseUrl}/place-directory/${selectedSchoolId}`, getRequestHeaders())
      .then((res) => {
        const payload = res.data;
        if (payload && Array.isArray(payload.directories)) {
          setPlaceDirectory(payload.directories);
        } else if (Array.isArray(payload)) {
          setPlaceDirectory(payload);
        } else {
          setPlaceDirectory([]);
        }
      })
      .catch(() => {});
  }, [selectedSchoolId, setPlaceDirectory, showCalendarEventsModal]);

  // Fetch drive edges for this school on selection change and seed matrix
  React.useEffect(() => {
    let cancelled = false;
    setDriveEdgesLoaded(false);
    setDriveEdgesLoadError(null);
    if (!selectedSchoolId) {
      setDriveEdges([]);
      return undefined;
    }
    axios
      .get(`${baseUrl}/drive-edges/${selectedSchoolId}`, getRequestHeaders())
      .then((res) => {
        if (cancelled) return;
        const payload = res.data;
        const edges = payload?.edges || (Array.isArray(payload) ? payload : []);
        setDriveEdges(edges);
        seedEdges(edges);
      })
      .catch(() => {
        if (cancelled) return;
        setDriveEdges([]);
        setDriveEdgesLoadError("Drive-time data failed to load.");
      })
      .finally(() => {
        if (!cancelled) setDriveEdgesLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [driveEdgesReloadToken, seedEdges, selectedSchoolId]);

  React.useEffect(() => {
    let cancelled = false;
    // Clear the previous teacher immediately; otherwise it can be rendered
    // (and used by manual placement checks) until the next request finishes.
    setCalendarEvents([]);
    setCalendarDaysWithEvents({});
    setWeekViewEvents([]);
    if (!selectedTeacherId) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    axios
      .get(`${baseUrl}/calendar/teacher/${selectedTeacherId}`, getRequestHeaders())
      .then((res) => {
        if (cancelled) return;
        setCalendarEvents(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.log(err);
        setCalendarEvents([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTeacherId]);

  React.useEffect(() => {
    if (!calendarEvents.length) {
      setCalendarDaysWithEvents({});
      setWeekViewEvents([]);
      return;
    }
    createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime(
      displayedWeek,
      calendarEvents,
      setCalendarDaysWithEvents
    );
  }, [calendarEvents, displayedWeek]);

  React.useEffect(() => {
    if (!Object.keys(calendarDaysWithEvents).length) {
      setWeekViewEvents([]);
      return;
    }
    createEventObjectForTeacherWeekView(
      displayedWeek,
      calendarDaysWithEvents,
      setWeekViewEvents
    );
  }, [calendarDaysWithEvents, displayedWeek]);

  const positionedEventsByDay = React.useMemo(() => {
    const grouped = {};
    weekDays.forEach((day) => (grouped[day] = []));

    const getLastName = (event) => {
      if (event.familyName) return event.familyName;
      if (event.studentName && event.studentName.includes(" ")) {
        const parts = event.studentName.trim().split(" ");
        return parts[parts.length - 1];
      }
      return event.studentName || "Lesson";
    };

    const buildTooltip = (event, start) => {
      const instrument =
        event.instrument ||
        event.lessonName ||
        event.name ||
        "Instrument N/A";
      const length = event.lessonLength || event.rowLength * SLOT_MINUTES || 30;
      const addr = event.familyAddress || {};
      const addressParts = [
        addr.addressLine1,
        addr.addressLine2,
        addr.city,
        addr.state,
        addr.zipcode,
      ].filter(Boolean);
      const address = addressParts.join(", ") || "Address N/A";
      const fullName = event.studentName || "Student";
      return `${fullName}\n${instrument}\n${length} mins\n${address}`;
    };

    const mapWeekViewEvent = (event) => {
      const start = event.startDate || event.parsedDate || new Date(event.dateTime);
      if (!start || isNaN(start)) return null;
      const dayName = format(start, "EEEE");
      const minutesFromStart = start.getHours() * 60 + start.getMinutes() - timeWindow.startMinutes;
      if (minutesFromStart < 0 || minutesFromStart > timeWindow.totalMinutes) return null;
      const durationMinutes =
        event.lessonLength ||
        (event.rowLength ? event.rowLength * 5 : null) ||
        30;
      const top = (minutesFromStart / SLOT_MINUTES) * PIXELS_PER_SLOT;
      const height = (durationMinutes / SLOT_MINUTES) * PIXELS_PER_SLOT;
      const startMinutesOfDay = start.getHours() * 60 + start.getMinutes();
      return {
        ...event,
        planned: false,
        dayName,
        top,
        height,
        startMinutesOfDay,
        endMinutesOfDay: startMinutesOfDay + durationMinutes,
        label: getLastName(event),
        tooltip: buildTooltip(event, start),
        timeLabel: format(start, "h:mma"),
      };
    };

    weekViewEvents
      .filter((event) => !event.isPendingOffer)
      .forEach((event) => {
        const positioned = mapWeekViewEvent(event);
        if (positioned && grouped[positioned.dayName]) grouped[positioned.dayName].push(positioned);
      });

    plannedPlacements.forEach((placement) => {
      if (placement.teacherId !== selectedTeacherId) return;
      const targetDate = new Date(displayedWeek[placement.dayIndex]);
      targetDate.setHours(Math.floor(placement.startMinutes / 60));
      targetDate.setMinutes(placement.startMinutes % 60);
      targetDate.setSeconds(0);
      const dayName = format(targetDate, "EEEE");
      const minutesFromStart =
        (targetDate.getHours() * 60 + targetDate.getMinutes()) - timeWindow.startMinutes;
      if (minutesFromStart < 0 || minutesFromStart > timeWindow.totalMinutes) return;
      const top = (minutesFromStart / SLOT_MINUTES) * PIXELS_PER_SLOT;
      const height = ((placement.lessonLength || 30) / SLOT_MINUTES) * PIXELS_PER_SLOT;
      const startMinutesOfDay = targetDate.getHours() * 60 + targetDate.getMinutes();
      grouped[dayName].push({
        ...placement,
        date: targetDate,
        planned: true,
        dayName,
        top,
        height,
        startMinutesOfDay,
        endMinutesOfDay: startMinutesOfDay + (placement.lessonLength || 30),
        label: getLastName(placement),
        tooltip: buildTooltip(placement, targetDate),
        timeLabel: format(targetDate, "h:mma"),
      });
    });

    Object.keys(grouped).forEach((day) => {
      grouped[day].sort((a, b) => a.top - b.top);
    });
    return grouped;
  }, [displayedWeek, weekViewEvents, plannedPlacements, selectedTeacherId, timeWindow]);

  const getDriveKey = React.useCallback(
    (event) => {
      const explicitPlaceKey =
        event?.placeKey || event?.place_key || event?.placeId || null;
      if (explicitPlaceKey) return explicitPlaceKey;

      const fid = event.familyId || event.familyID || event.FamilyID;
      if (fid && placeKeyLookup.familyToPlace.has(String(fid))) {
        return placeKeyLookup.familyToPlace.get(String(fid));
      }
      const suppliedLocation =
        event.familyAddress ||
        event.address ||
        event.locationAddress ||
        event.location ||
        event.venue ||
        null;
      const addr = formatAddress(suppliedLocation);
      if (addr && placeKeyLookup.addressToPlace.has(addr.toLowerCase())) {
        return placeKeyLookup.addressToPlace.get(addr.toLowerCase());
      }
      const label = (event.familyName || event.studentName || event.label || addr || "").toLowerCase();
      if (label && placeKeyLookup.labelToPlace.has(label)) {
        return placeKeyLookup.labelToPlace.get(label);
      }

      // A student/family lesson with no resolved place must remain missing.
      // Falling through to the teacher's home makes the optimizer silently
      // treat that lesson as a zero-drive home appointment.
      const hasStudentLocation = Boolean(
        fid ||
          event.studentId ||
          event.studentID ||
          event.StudentID ||
          event.familyName ||
          event.studentName
      );
      if (hasStudentLocation) return null;

      // An explicit offsite location that is not in the place directory is
      // unknown, not the teacher's home. Keep it unresolved so scheduling
      // cannot silently omit travel around the appointment.
      if (suppliedLocation) return null;

      // Teacher-only/personal events without another location are treated as
      // home appointments.
      const tid = event.teacherId || event.teacherID || event.TeacherID;
      if (tid && placeKeyLookup.teacherToPlace.has(String(tid))) {
        return placeKeyLookup.teacherToPlace.get(String(tid));
      }
      return null;
    },
    [formatAddress, placeKeyLookup]
  );

  const driveSegmentsByDay = React.useMemo(() => {
    const res = {};
    weekDays.forEach((d) => (res[d] = []));
    weekDays.forEach((day) => {
      const events = (positionedEventsByDay[day] || []).slice().sort((a, b) => a.startMinutesOfDay - b.startMinutesOfDay);

      // Between consecutive lessons
      for (let i = 0; i < events.length - 1; i++) {
        const current = events[i];
        const next = events[i + 1];
        // Skip drive time when consecutive events belong to the same family
        if (
          current.familyId &&
          next.familyId &&
          String(current.familyId) === String(next.familyId)
        ) {
          continue;
        }
        const minutes = minutesBetween(getDriveKey(current), getDriveKey(next));
        const top = (current.top + current.height + next.top) / 2;
        const prevEnd =
          current.endMinutesOfDay != null
            ? current.endMinutesOfDay
            : current.startMinutesOfDay + (current.lessonLength || 30);
        const nextStart = next.startMinutesOfDay;
        res[day].push({ minutes, top, prevEnd, nextStart });
      }

      // Home -> first lesson and last lesson -> home (per availability block)
      const teacherHome = placeKeyByTeacher.get(String(selectedTeacherId));
      if (teacherHome && selectedTeacherId) {
        const dayBlocks = availabilityByDay[day] || [];
        dayBlocks.forEach((block) => {
          const inBlock = events.filter(
            (ev) => ev.startMinutesOfDay >= block.start && ev.startMinutesOfDay < block.end
          );
          if (!inBlock.length) return;
          const first = inBlock[0];
          const last = inBlock[inBlock.length - 1];

          const homeToFirst = minutesBetween(teacherHome, getDriveKey(first));
          if (Number.isFinite(homeToFirst)) {
            const mid = (block.start + first.startMinutesOfDay) / 2;
            const top = ((mid - timeWindow.startMinutes) / SLOT_MINUTES) * PIXELS_PER_SLOT;
            if (top >= 0 && top <= timeWindow.totalHeightPx) {
              res[day].push({
                minutes: homeToFirst,
                top,
                prevEnd: block.start,
                nextStart: first.startMinutesOfDay,
              });
            }
          }

          const lastToHome = minutesBetween(getDriveKey(last), teacherHome);
          if (Number.isFinite(lastToHome)) {
            const mid = (last.endMinutesOfDay + block.end) / 2;
            const top = ((mid - timeWindow.startMinutes) / SLOT_MINUTES) * PIXELS_PER_SLOT;
            if (top >= 0 && top <= timeWindow.totalHeightPx) {
              const lastEnd =
                last.endMinutesOfDay != null
                  ? last.endMinutesOfDay
                  : last.startMinutesOfDay + (last.lessonLength || 30);
              res[day].push({
                minutes: lastToHome,
                top,
                prevEnd: lastEnd,
                nextStart: block.end,
              });
            }
          }
        });
      }
    });
    return res;
  }, [availabilityByDay, getDriveKey, minutesBetween, placeKeyByTeacher, positionedEventsByDay, selectedTeacherId, timeWindow]);

  const driveSegmentsWithWarn = React.useMemo(() => {
    const availMap = availabilityByDay;
    const result = {};
    weekDays.forEach((day) => {
      const blocks = availMap[day] || [];
      const earliest = blocks.length ? Math.min(...blocks.map((b) => b.start)) : null;
      result[day] = (driveSegmentsByDay[day] || []).map((seg) => {
        const gap = seg.nextStart != null && seg.prevEnd != null ? seg.nextStart - seg.prevEnd : null;
        let warn = false;
        if (Number.isFinite(seg.minutes)) {
          if (gap != null && gap < seg.minutes) warn = true;
          if (seg.prevEnd == null && earliest != null && seg.nextStart != null) {
            if (seg.nextStart - earliest < seg.minutes) warn = true;
          }
        }
        return { ...seg, warn, gap };
      });
    });
    return result;
  }, [availabilityByDay, driveSegmentsByDay]);

  const totalDriveMinutes = React.useMemo(() => {
    if (!selectedTeacherId) return null;
    let sum = 0;
    weekDays.forEach((day) => {
      (driveSegmentsWithWarn[day] || []).forEach((seg) => {
        if (Number.isFinite(seg.minutes)) sum += seg.minutes;
      });
    });
    return sum;
  }, [driveSegmentsWithWarn, selectedTeacherId]);

  const totalAvailabilityMinutes = React.useMemo(() => {
    if (!selectedTeacherId) return null;
    let sum = 0;
    weekDays.forEach((day) => {
      const blocks = availabilityByDay[day] || [];
      blocks.forEach((b) => {
        if (b.start != null && b.end != null && b.end > b.start) {
          sum += b.end - b.start;
        }
      });
    });
    return sum;
  }, [availabilityByDay, selectedTeacherId]);

  const totalTeachingMinutes = React.useMemo(() => {
    if (!selectedTeacherId) return null;
    let sum = 0;
    weekDays.forEach((day) => {
      (positionedEventsByDay[day] || []).forEach((ev) => {
        if (ev.endMinutesOfDay != null && ev.startMinutesOfDay != null) {
          sum += ev.endMinutesOfDay - ev.startMinutesOfDay;
        } else if (ev.lessonLength) {
          sum += ev.lessonLength;
        }
      });
    });
    return sum;
  }, [positionedEventsByDay, selectedTeacherId]);

  // Helper: get day index within displayedWeek by date
  const getDayIndexForDate = React.useCallback(
    (d) => {
      if (!d || isNaN(d)) return -1;
      for (let i = 0; i < displayedWeek.length; i++) {
        const dayDate = displayedWeek[i];
        if (format(d, "yyyy-MM-dd") === format(dayDate, "yyyy-MM-dd")) {
          return i;
        }
      }
      return -1;
    },
    [displayedWeek]
  );

  const ensureTransparentImg = () => {
    if (transparentDragImg.current) return transparentDragImg.current;
    const img = new Image();
    img.src =
      "data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScxJyBoZWlnaHQ9JzEnPjwvc3ZnPg==";
    transparentDragImg.current = img;
    return img;
  };

  const dropdownTeachersForModal = React.useMemo(() => {
    const directoryTeacherIds = new Set();
    const directoryAddresses = new Set();
    placeDirectory.forEach((entry) => {
      entry.teacherIds?.forEach((tid) => directoryTeacherIds.add(String(tid)));
      if (entry.address) directoryAddresses.add(String(entry.address).toLowerCase());
      if (entry.label) directoryAddresses.add(String(entry.label).toLowerCase());
      if (entry.key) directoryAddresses.add(String(entry.key).toLowerCase());
    });

    const optionIds = new Set(teacherOptions.map((t) => String(t.value)));
    const list = [];
    teachers
      .filter(
        (t) =>
          optionIds.has(String(t.TeacherID)) &&
          t.School?.toLowerCase() === selectedSchool
      )
      .forEach((t) => {
        const id = String(t.TeacherID);
        const address = formatAddress(
          t.Address ||
            t.address ||
            t.HomeAddress || {
              addressLine1: t.Address1,
              addressLine2: t.Address2,
              city: t.City,
              state: t.State,
              zipcode: t.Zip,
            }
        );
        list.push({
          id: `teacher-${id}`,
          teacherId: id,
          teacherName: t.Name || "Teacher",
          address,
          hasDirectory:
            directoryTeacherIds.has(id) ||
            (address && directoryAddresses.has(address.toLowerCase())),
        });
      });
    return list;
  }, [formatAddress, placeDirectory, selectedSchool, teacherOptions, teachers]);

  // Recurring-aware calendar events for dropdown teachers within the displayed week
  const weeklyTeacherEventsForModal = React.useMemo(() => {
    if (!selectedSchool || !startOfThisWeek) return [];
    const ids = dropdownTeachersForModal
      .map((t) => String(t.teacherId || t.TeacherID || t.id))
      .filter(Boolean);
    if (!ids.length) return [];
    const events = getTeacherEventsForWeek(teacherCalendarEvents || [], ids, startOfThisWeek);
    return events.map((ev, idx) => {
      const dt = ev.dateTime ? new Date(ev.dateTime) : parseEventDate(ev);
      return { ev, date: dt, idx };
    });
  }, [dropdownTeachersForModal, selectedSchool, startOfThisWeek, teacherCalendarEvents, parseEventDate]);

  const missingPlaceSummary = React.useMemo(() => {
    const missingTeachers = dropdownTeachersForModal.filter((t) => !t.hasDirectory);
    const missingStudents = studentsNeedingReassignment.filter((student) => {
      const key = resolveStudentPlace(student);
      if (!key) return true;
      const k = String(key);
      return !placeDirectoryByKey.has(k) && !placeDirectoryByKey.has(k.toLowerCase());
    });

    const missingEventFamilies = new Set();
    const missingEvents = [];
    weeklyTeacherEventsForModal.forEach(({ ev, idx }) => {
      const fid = ev?.familyId || ev?.familyID;
      if (fid && missingStudents.some((s) => String(s.familyId) === String(fid))) return;
      if (!getDriveKey(ev)) {
        missingEvents.push({ ev, idx });
        if (fid) missingEventFamilies.add(String(fid));
      }
    });
    const missingExternalEvents = missingEvents.filter(
      ({ ev }) => !(ev?.familyId || ev?.familyID)
    );

    const totalMissing =
      missingTeachers.length +
      missingStudents.length +
      missingEventFamilies.size +
      missingExternalEvents.length;

    return {
      missingTeachers,
      missingStudents,
      missingEventFamilies,
      missingEvents,
      missingExternalEvents,
      totalMissing,
    };
  }, [
    dropdownTeachersForModal,
    getDriveKey,
    placeDirectoryByKey,
    resolveStudentPlace,
    studentsNeedingReassignment,
    weeklyTeacherEventsForModal,
  ]);

  React.useEffect(() => {
    if (!scheduleLoadedAt) return;
    if (missingPlaceSummary.totalMissing > 0) {
      setShowMissingPlaceModal(true);
    }
  }, [missingPlaceSummary.totalMissing, scheduleLoadedAt]);


  // ----- Debug helper: slice open time into lesson-sized slots (no drive logic) -----
  const candidateSlotsByTeacher = React.useMemo(() => {
    if (!showCalendarEventsModal) return new Map();
    if (!selectedSchool) return new Map();
    const slotLength = 30; // minutes
    const step = SLOT_MINUTES;

    const candidateIds = teacherOptions
      .map((t) => String(t.value))
      .filter(Boolean)
      .filter((tid) =>
        teachers.some(
          (teacher) =>
            String(teacher.TeacherID) === tid &&
            teacher.School?.toLowerCase() === selectedSchool
        )
      );

    const busyMap = new Map();
    const addBusy = (tid, dayIndex, start, end, place) => {
      if (dayIndex < 0 || dayIndex > 6) return;
      if (!busyMap.has(tid)) busyMap.set(tid, {});
      if (!busyMap.get(tid)[dayIndex]) busyMap.get(tid)[dayIndex] = [];
      busyMap.get(tid)[dayIndex].push({ start, end, place });
    };

    const collect = (ev, explicitTid) => {
      const tid = explicitTid || String(ev.teacherId || ev.teacherID || ev.TeacherID || "");
      if (!candidateIds.includes(tid)) return;
      const d = parseEventDate(ev);
      if (!d) return;
      let dayIndex = getDayIndexForDate(d);
      if (dayIndex < 0 || dayIndex > 6) {
        const name = format(d, "EEEE");
        const fallbackIdx = weekDays.indexOf(name);
        dayIndex = fallbackIdx;
      }
      if (dayIndex < 0 || dayIndex > 6) return;
      const start = d.getHours() * 60 + d.getMinutes();
      const len =
        Number(ev.lessonLength) && Number(ev.lessonLength) > 0
          ? Number(ev.lessonLength)
          : ev.rowLength
          ? ev.rowLength * SLOT_MINUTES
          : 60;
      addBusy(tid, dayIndex, start, start + len, getDriveKey(ev));
    };

    // Recurring-aware events for this week
    weeklyTeacherEventsForModal.forEach(({ ev, date }) => {
      const tid = String(ev.teacherId || ev.teacherID || ev.TeacherID || "");
      if (!candidateIds.includes(tid)) return;
      const d = date || parseEventDate(ev);
      if (!d) return;
      let dayIndex = getDayIndexForDate(d);
      if (dayIndex < 0 || dayIndex > 6) {
        const name = format(d, "EEEE");
        const fallbackIdx = weekDays.indexOf(name);
        dayIndex = fallbackIdx;
      }
      if (dayIndex < 0 || dayIndex > 6) return;
      const start = d.getHours() * 60 + d.getMinutes();
      const len =
        Number(ev.lessonLength) && Number(ev.lessonLength) > 0
          ? Number(ev.lessonLength)
          : ev.rowLength
          ? ev.rowLength * SLOT_MINUTES
          : 60;
      addBusy(tid, dayIndex, start, start + len, getDriveKey(ev));
    });
    teacherCalendarEvents.forEach((ev) => collect(ev));
    calendarEvents.forEach((ev) => collect(ev, selectedTeacherId ? String(selectedTeacherId) : ""));
    weekViewEvents.forEach((ev) => collect(ev, ev.teacherId || selectedTeacherId));
    plannedPlacements.forEach((p) => {
      const tid = String(p.teacherId || "");
      if (!candidateIds.includes(tid)) return;
      addBusy(tid, p.dayIndex, p.startMinutes, p.startMinutes + (p.lessonLength || 30), getDriveKey(p));
    });
    // Also account for currently positioned events in the displayed week for the selected teacher
    if (selectedTeacherId && candidateIds.includes(String(selectedTeacherId))) {
      weekDays.forEach((day, idx) => {
        (positionedEventsByDay[day] || []).forEach((ev) => {
          if (ev.startMinutesOfDay != null && ev.endMinutesOfDay != null) {
            addBusy(String(selectedTeacherId), idx, ev.startMinutesOfDay, ev.endMinutesOfDay, getDriveKey(ev));
          }
        });
      });
    }

    candidateIds.forEach((tid) => {
      const dayMap = busyMap.get(tid) || {};
      Object.keys(dayMap).forEach((k) => {
        dayMap[k].sort((a, b) => a.start - b.start);
      });
      busyMap.set(tid, dayMap);
    });

    const slotsByTeacher = new Map();
    candidateIds.forEach((tid) => {
      const teacher = teachers.find((t) => String(t.TeacherID) === String(tid));
      const availMap =
        availabilityByTeacher.get(String(tid)) ||
        (teacher?.AvailabilityDetails ? normalizeAvailability(teacher.AvailabilityDetails) : {});
      const dayBusy = busyMap.get(tid) || {};
      const slotByDay = {};

      weekDays.forEach((_, dayIdx) => {
        const dayName = weekDays[dayIdx];
        const availBlocks = availMap[dayName] || availMap[dayIdx] || [];
        const busyList = (dayBusy[dayIdx] || []).slice().sort((a, b) => a.start - b.start);
        const free = [];

        availBlocks.forEach((block) => {
          let cursor = block.start;
          busyList.forEach((b) => {
            if (b.end <= block.start || b.start >= block.end) return;
            if (b.start > cursor) free.push({ start: cursor, end: Math.min(b.start, block.end) });
            cursor = Math.max(cursor, b.end);
          });
          if (cursor < block.end) free.push({ start: cursor, end: block.end });
        });

        const slots = [];
        const home = placeKeyByTeacher.get(String(tid)) || null;
        free.forEach((f) => {
          for (let s = f.start; s + slotLength <= f.end; s += step) {
            const e = s + slotLength;
            // find prev/next events with place context
            const prev =
              busyList
                .filter((b) => b.end <= s)
                .sort((a, b) => b.end - a.end)[0] || { end: f.start, place: home };
            const next =
              busyList
                .filter((b) => b.start >= e)
                .sort((a, b) => a.start - b.start)[0] || { start: f.end, place: home };
            slots.push({
              start: s,
              end: e,
              prevPlace: prev.place || home,
              nextPlace: next.place || home,
            });
          }
        });
        slotByDay[dayIdx] = slots;
      });

      slotsByTeacher.set(tid, slotByDay);
    });

    return slotsByTeacher;
  }, [
    availabilityByTeacher,
    calendarEvents,
    getDriveKey,
    normalizeAvailability,
    parseEventDate,
    placeKeyByTeacher,
    plannedPlacements,
    positionedEventsByDay,
    selectedSchool,
    selectedTeacherId,
    showCalendarEventsModal,
    teacherCalendarEvents,
    teacherOptions,
    teachers,
    weekViewEvents,
    weeklyTeacherEventsForModal,
    getDayIndexForDate,
  ]);

  // Student options built from candidate slots with drive deltas
  const studentSlotOptions = React.useMemo(() => {
    if (!selectedSchool || !candidateSlotsByTeacher.size) return [];
    const travel = (fromKey, toKey) => {
      if (!fromKey || !toKey) return Infinity;
      if (fromKey === toKey) return 0;
      const m = minutesBetween(fromKey, toKey);
      return Number.isFinite(m) ? m : Infinity;
    };

    return studentsNeedingReassignment
      .filter((s) => !selectedSchool || s.school === selectedSchool)
      .map((student) => {
        const studentPlace = resolveStudentPlace(student);
        const lessonLength = student.lessonLength || 30;
        const options = [];
        if (!studentPlace) return { student, options };

        candidateSlotsByTeacher.forEach((dayMap, tid) => {
          if (!tid) return;
          // instrument check
          const teacher = teachers.find((t) => String(t.TeacherID) === String(tid));
          const studentInst =
            student.instruments?.[0]?.parentInstrument || student.instruments?.[0]?.instrument;
          if (studentInst && teacher?.InstrumentList && !teacher.InstrumentList.some((inst) => inst.parentInstrument === studentInst || inst.instrument === studentInst)) {
            return;
          }

          Object.keys(dayMap || {}).forEach((k) => {
            const dayIdx = Number(k);
            (dayMap?.[dayIdx] || []).forEach((slot, idx) => {
              // compute extra drive
              const prevKey = slot.prevPlace || null;
              const nextKey = slot.nextPlace || null;
              const driveIn = travel(prevKey, studentPlace);
              const driveOut = travel(studentPlace, nextKey);
              const direct = travel(prevKey, nextKey);
              if (!Number.isFinite(driveIn) || !Number.isFinite(driveOut)) return;
              const slotId = `${tid}-${dayIdx}-${slot.start}-${idx}`;
              options.push({
                teacherId: tid,
                slotId,
                dayIndex: dayIdx,
                startMinutes: slot.start,
                endMinutes: slot.end,
                lessonLength,
                extraDriveMinutes: driveIn + driveOut - (Number.isFinite(direct) ? direct : 0),
                prevPlace: prevKey,
                nextPlace: nextKey,
              });
            });
          });
        });

        options.sort((a, b) => (a.score ?? a.extraDriveMinutes) - (b.score ?? b.extraDriveMinutes));
        return { student, options };
      });
  }, [candidateSlotsByTeacher, minutesBetween, resolveStudentPlace, selectedSchool, studentsNeedingReassignment, teachers]);

  const onEventClick = React.useCallback(
    (event) => {
      setEventModalData(event);
      setShowEventDriveModal(true);
    },
    []
  );

  const optimizationTeachers = React.useMemo(() => {
    return teacherOptions
      .map((option) => {
        const teacher = teachers.find(
          (candidate) => String(candidate.TeacherID) === String(option.value)
        );
        if (!teacher) return null;
        return {
          id: String(teacher.TeacherID),
          name: teacher.Name || teacher.name || String(teacher.TeacherID),
          homeKey: placeKeyByTeacher.get(String(teacher.TeacherID)) || null,
          instruments: teacher.InstrumentList || [],
          availabilityByDay: normalizeAvailability(teacher.AvailabilityDetails || {}),
        };
      })
      .filter(Boolean);
  }, [normalizeAvailability, placeKeyByTeacher, teacherOptions, teachers]);

  const optimizationTeachersWithAvailability = React.useMemo(
    () => optimizationTeachers.filter(hasUsableTeacherAvailability),
    [optimizationTeachers]
  );

  const schedulableOptimizationTeachers = React.useMemo(
    () => optimizationTeachersWithAvailability.filter((teacher) => teacher.homeKey),
    [optimizationTeachersWithAvailability]
  );

  const optimizationStudentCandidates = React.useMemo(() => {
    const lockedKeys = new Set((lockedStudentIds || []).map(String));
    const explicitlyScopedKeys = new Set();
    const rememberExplicitStudent = (student) => {
      const key = getStudentKey(student);
      if (key) explicitlyScopedKeys.add(String(key));
    };
    manualReassignmentStudents.forEach(rememberExplicitStudent);
    if (Array.isArray(overrideReassignmentStudents)) {
      overrideReassignmentStudents.forEach(rememberExplicitStudent);
    }
    const sources = [
      ...allStudentsForSelectedSchool,
      ...manualReassignmentStudents,
      ...(Array.isArray(overrideReassignmentStudents) ? overrideReassignmentStudents : []),
      ...studentsNeedingReassignment,
    ];
    const sourceByKey = new Map();
    sources.forEach((student) => {
      if (!student) return;
      const key = getStudentKey(student);
      if (key && !sourceByKey.has(String(key))) sourceByKey.set(String(key), student);
      if (student.studentId && !sourceByKey.has(String(student.studentId))) {
        sourceByKey.set(String(student.studentId), student);
      }
    });

    const pool = new Map();
    const addStudent = (
      student,
      incumbentPlacement = null,
      allowExplicitStatusOverride = false
    ) => {
      if (!student) return;
      const key = getStudentKey(student);
      if (!key || lockedKeys.has(String(key))) return;
      if (
        !allowExplicitStatusOverride &&
        !isEligibleOptimizationStudent(student)
      ) {
        return;
      }
      if (
        selectedSchool &&
        student.school &&
        String(student.school).toLowerCase() !== selectedSchool
      ) {
        return;
      }
      const resolvedPlace = resolveStudentPlace(student);
      const explicitPlace =
        student.placeKey || student.place_key || student.placeId || null;
      const directoryKey = resolvedPlace ? String(resolvedPlace) : null;
      const hasDirectory =
        directoryKey &&
        (placeDirectoryByKey.has(directoryKey) ||
          placeDirectoryByKey.has(directoryKey.toLowerCase()));
      pool.set(String(key), {
        ...student,
        key: String(key),
        id: student.id || String(key),
        placeKey: explicitPlace
          ? String(explicitPlace)
          : hasDirectory
          ? directoryKey
          : null,
        duration: normalizeLessonDuration(student.lessonLength, student.duration),
        instruments:
          Array.isArray(student.instruments) && student.instruments.length
            ? student.instruments
            : student.instrument
            ? [student.instrument]
            : [],
        availability: student.availability || student.initialAvailability || null,
        incumbentPlacement,
      });
    };

    studentsNeedingReassignment.forEach((student) => {
      const key = getStudentKey(student);
      addStudent(
        student,
        null,
        key ? explicitlyScopedKeys.has(String(key)) : false
      );
    });
    plannedPlacements.forEach((placement) => {
      const key = getStudentKey(placement);
      if (!key || lockedKeys.has(String(key))) return;
      const original =
        sourceByKey.get(String(key)) ||
        sourceByKey.get(String(placement.studentId || "")) ||
        {};
      addStudent(
        {
          ...original,
          ...placement,
          id: original.id || placement.placementKey || key,
          placementKey: placement.placementKey || key,
          instruments:
            Array.isArray(original.instruments) && original.instruments.length
              ? original.instruments
              : Array.isArray(placement.instruments) && placement.instruments.length
              ? placement.instruments
              : placement.instrument
              ? [placement.instrument]
              : [],
          availability:
            original.availability ||
            original.initialAvailability ||
            placement.availability ||
            null,
          familyAddress: original.familyAddress || placement.familyAddress,
          school: original.school || placement.school || selectedSchool,
        },
        {
          teacherId: String(placement.teacherId || ""),
          dayIndex: Number(placement.dayIndex),
          startMinutes: Number(placement.startMinutes),
        },
        explicitlyScopedKeys.has(String(key))
      );
    });
    return Array.from(pool.values()).sort((a, b) => a.key.localeCompare(b.key));
  }, [
    allStudentsForSelectedSchool,
    getStudentKey,
    lockedStudentIds,
    manualReassignmentStudents,
    overrideReassignmentStudents,
    placeDirectoryByKey,
    plannedPlacements,
    resolveStudentPlace,
    selectedSchool,
    studentsNeedingReassignment,
  ]);

  const optimizationFixedEvents = React.useMemo(() => {
    const teacherIds = optimizationTeachers.map((teacher) => teacher.id);
    const activeTeacherIds = new Set(teacherIds);
    let weeklyEvents = [];
    try {
      weeklyEvents = getTeacherEventsForWeek(
        teacherCalendarEvents || [],
        teacherIds,
        startOfThisWeek
      );
    } catch (error) {
      console.log("Unable to expand calendar events for optimization", error);
    }
    const fixed = (weeklyEvents || [])
      .filter((event) => !event?.isPendingOffer && !event?.isAvailabilityPlaceholder)
      .map((event, index) => {
        const date = parseEventDate(event);
        const teacherId = String(
          event.teacherId || event.teacherID || event.TeacherID || ""
        );
        const dayIndex = date ? getDayIndexForDate(date) : -1;
        const start = date ? date.getHours() * 60 + date.getMinutes() : null;
        const explicitEnd = resolveExplicitEventEndMinutes(event, date, start);
        const fallbackDuration = normalizeLessonDuration(
          event.lessonLength,
          Number(event.rowLength) > 0
            ? Number(event.rowLength) * SLOT_MINUTES
            : null
        );
        const end =
          start == null
            ? null
            : explicitEnd != null
            ? explicitEnd
            : start + fallbackDuration;
        return {
          id: event.id || event._id || `calendar-${teacherId}-${dayIndex}-${start}-${index}`,
          studentKey: getStudentKey(event),
          teacherId,
          dayIndex,
          start,
          end,
          duration:
            start != null && end != null ? end - start : fallbackDuration,
          placeKey: getDriveKey(event),
          locked: true,
          source: "calendar",
        };
      })
      .filter(
        (event) =>
          activeTeacherIds.has(event.teacherId) &&
          event.dayIndex >= 0 &&
          event.dayIndex <= 6 &&
          event.start != null
      );

    const lockedKeys = new Set((lockedStudentIds || []).map(String));
    plannedPlacements.forEach((placement, index) => {
      const studentKey = getStudentKey(placement);
      const teacherId = String(placement.teacherId || "");
      if (!lockedKeys.has(String(studentKey)) || !activeTeacherIds.has(teacherId)) return;
      const duration = normalizeLessonDuration(
        placement.lessonLength,
        placement.duration
      );
      const explicitPlace =
        placement.placeKey || placement.place_key || placement.placeId || null;
      const resolvedPlace = resolveStudentPlace(placement);
      const resolvedDirectoryKey = resolvedPlace ? String(resolvedPlace) : null;
      const hasResolvedDirectory =
        resolvedDirectoryKey &&
        (placeDirectoryByKey.has(resolvedDirectoryKey) ||
          placeDirectoryByKey.has(resolvedDirectoryKey.toLowerCase()));
      fixed.push({
        id: placement.id || `locked-${studentKey}-${index}`,
        studentKey,
        teacherId,
        dayIndex: Number(placement.dayIndex),
        start: Number(placement.startMinutes),
        end: Number(placement.startMinutes) + duration,
        placeKey: explicitPlace
          ? String(explicitPlace)
          : hasResolvedDirectory
          ? resolvedDirectoryKey
          : null,
        locked: true,
        source: "locked-placement",
      });
    });
    return fixed;
  }, [
    getDayIndexForDate,
    getDriveKey,
    getStudentKey,
    lockedStudentIds,
    optimizationTeachers,
    parseEventDate,
    plannedPlacements,
    placeDirectoryByKey,
    resolveStudentPlace,
    startOfThisWeek,
    teacherCalendarEvents,
  ]);

  const optimizationStudents = React.useMemo(() => {
    const fixedCalendarStudentKeys = new Set(
      optimizationFixedEvents
        .filter((event) => event.source === "calendar" && event.studentKey)
        .map((event) => String(event.studentKey))
    );
    return optimizationStudentCandidates.filter(
      (student) => !fixedCalendarStudentKeys.has(String(student.key))
    );
  }, [optimizationFixedEvents, optimizationStudentCandidates]);

  const getOptimizationDriveMinutes = React.useCallback(
    (fromKey, toKey) => {
      if (!fromKey || !toKey) return null;
      if (String(fromKey) === String(toKey)) return 0;
      const value = minutesMap.get(makeDriveKey(fromKey, toKey));
      return normalizeDriveMinutes(value);
    },
    [makeDriveKey, minutesMap]
  );

  const runImprovedOptimization = React.useCallback(
    (driveLookup = getOptimizationDriveMinutes) => {
      if (!selectedSchool) {
        setOptimizationSummary({ type: "warning", message: "Select a school first." });
        return null;
      }
      if (resolvingManualLocations) {
        setOptimizationSummary({
          type: "info",
          message: "Family locations are still being loaded. Optimization will be ready in a moment.",
        });
        return null;
      }
      if (calendarEventsLoadError) {
        setOptimizationSummary({
          type: "error",
          message: `${calendarEventsLoadError} Retry before optimizing so occupied times are not mistaken for free time.`,
        });
        return null;
      }
      if (placeDirectoryLoadError || driveEdgesLoadError) {
        setOptimizationSummary({
          type: "error",
          message: `${placeDirectoryLoadError || driveEdgesLoadError} Retry before optimizing.`,
        });
        return null;
      }
      if (!calendarEventsLoaded || !placeDirectoryLoaded || !driveEdgesLoaded) {
        setOptimizationSummary({
          type: "info",
          message: "Calendar, location, and drive data are still loading.",
        });
        return null;
      }
      if (!optimizationTeachers.length) {
        setOptimizationSummary({
          type: "warning",
          message: "There are no active teachers for this school.",
        });
        return null;
      }
      if (!optimizationTeachersWithAvailability.length) {
        setOptimizationSummary({
          type: "warning",
          message: "Active teachers do not have usable availability for this school.",
        });
        return null;
      }
      const schedulableTeachers = schedulableOptimizationTeachers;
      if (!schedulableTeachers.length) {
        setOptimizationSummary({
          type: "warning",
          message: "Available teachers are missing home locations, so no routes can be optimized.",
        });
        setShowMissingPlaceModal(true);
        return null;
      }
      if (!optimizationStudents.length) {
        setOptimizationSummary({
          type: "info",
          message: "There are no unlocked students to optimize.",
        });
        return null;
      }

      const schedulableTeacherIds = new Set(
        schedulableTeachers.map((teacher) => teacher.id)
      );
      const optimizableKeySet = new Set(
        optimizationStudents.map((student) => String(student.key))
      );
      const lockedKeySet = new Set((lockedStudentIds || []).map(String));
      const outOfScopePlacements = plannedPlacements.filter((placement) => {
        const key = String(getStudentKey(placement));
        return !lockedKeySet.has(key) && !optimizableKeySet.has(key);
      });
      const fixedEventsForOptimization = [
        ...optimizationFixedEvents.filter((event) =>
          schedulableTeacherIds.has(String(event.teacherId))
        ),
        ...outOfScopePlacements
          .filter((placement) =>
            schedulableTeacherIds.has(String(placement.teacherId || ""))
          )
          .map((placement, index) => {
            const duration = normalizeLessonDuration(
              placement.lessonLength,
              placement.duration
            );
            const explicitPlace =
              placement.placeKey ||
              placement.place_key ||
              placement.placeId ||
              null;
            const resolvedPlace = resolveStudentPlace(placement);
            const resolvedDirectoryKey = resolvedPlace
              ? String(resolvedPlace)
              : null;
            const hasResolvedDirectory =
              resolvedDirectoryKey &&
              (placeDirectoryByKey.has(resolvedDirectoryKey) ||
                placeDirectoryByKey.has(resolvedDirectoryKey.toLowerCase()));
            return {
              id:
                placement.id ||
                `preserved-${getStudentKey(placement) || index}`,
              studentKey: getStudentKey(placement),
              teacherId: String(placement.teacherId || ""),
              dayIndex: Number(placement.dayIndex),
              start: Number(placement.startMinutes),
              end: Number(placement.startMinutes) + duration,
              duration,
              placeKey: explicitPlace
                ? String(explicitPlace)
                : hasResolvedDirectory
                ? resolvedDirectoryKey
                : null,
              locked: true,
              source: "preserved-placement",
            };
          }),
      ];
      const missingEventPlaces = fixedEventsForOptimization.filter(
        (event) => !event.placeKey
      );
      if (missingEventPlaces.length) {
        const missingCount = missingEventPlaces.length;
        pendingOptimizationRef.current = false;
        setOptimizationSummary({
          type: "warning",
          message: `${missingCount} existing calendar location${missingCount === 1 ? " is" : "s are"} missing. Create the location records before optimizing.`,
        });
        setShowMissingPlaceModal(true);
        return null;
      }

      const maxDriveCap =
        maxDriveMinutes !== "" && Number.isFinite(Number(maxDriveMinutes))
          ? Math.max(0, Number(maxDriveMinutes))
          : null;
      const paddingCap =
        paddingMinutes !== "" && Number.isFinite(Number(paddingMinutes))
          ? Math.max(0, Number(paddingMinutes))
          : 0;

      const missingPairs = collectRequiredDrivePairs({
        teachers: schedulableTeachers,
        students: optimizationStudents,
        fixedEvents: fixedEventsForOptimization,
        travelMinutes: driveLookup,
        paddingMinutes: paddingCap,
        maxDriveMinutes: maxDriveCap,
      });
      if (missingPairs.length) {
        pendingOptimizationRef.current = true;
        setMissingReassignmentDrivePairs(missingPairs);
        setShowMissingReassignmentDriveModal(true);
        setOptimizationSummary({
          type: "info",
          message: `${missingPairs.length} directed drive time${missingPairs.length === 1 ? " is" : "s are"} needed. Create them to continue optimization.`,
        });
        return null;
      }

      const sourceByKey = new Map(
        optimizationStudents.map((student) => [String(student.key), student])
      );
      const currentPlacements = plannedPlacements
        .filter((placement) => {
          const studentKey = String(getStudentKey(placement));
          return !lockedKeySet.has(studentKey) && sourceByKey.has(studentKey);
        })
        .map((placement) => {
          const studentKey = getStudentKey(placement);
          const source = sourceByKey.get(String(studentKey));
          const duration = normalizeLessonDuration(
            placement.lessonLength,
            placement.duration
          );
          return {
            studentKey,
            teacherId: String(placement.teacherId || ""),
            dayIndex: Number(placement.dayIndex),
            startMinutes: Number(placement.startMinutes),
            endMinutes: Number(placement.startMinutes) + duration,
            placeKey: source?.placeKey || resolveStudentPlace(placement),
            duration,
          };
        })
        .filter(
          (placement) =>
            placement.studentKey &&
            placement.teacherId &&
            Number.isFinite(placement.dayIndex) &&
            Number.isFinite(placement.startMinutes)
        );

      const beforeValidation = validateOptimizedSchedule({
        teachers: schedulableTeachers,
        students: optimizationStudents,
        fixedEvents: fixedEventsForOptimization,
        placements: currentPlacements,
        travelMinutes: driveLookup,
        paddingMinutes: paddingCap,
        maxDriveMinutes: maxDriveCap,
      });
      const beforeMetrics = calculateScheduleMetrics({
        teachers: schedulableTeachers,
        fixedEvents: fixedEventsForOptimization,
        placements: currentPlacements,
        travelMinutes: driveLookup,
        paddingMinutes: paddingCap,
        maxDriveMinutes: maxDriveCap,
      });

      const result = optimizeWeeklySchedule({
        teachers: schedulableTeachers,
        students: optimizationStudents,
        fixedEvents: fixedEventsForOptimization,
        travelMinutes: driveLookup,
        slotMinutes: SLOT_MINUTES,
        paddingMinutes: paddingCap,
        maxDriveMinutes: maxDriveCap,
      });
      pendingOptimizationRef.current = false;
      if (!result.valid) {
        setOptimizationSummary({
          type: "error",
          message:
            result.errors?.[0] ||
            "The best-found schedule failed validation and was not applied.",
        });
        return result;
      }
      const detailedUnassigned = (result.unassigned || []).map((entry) => {
        const student = sourceByKey.get(String(entry.studentKey)) || {};
        return {
          ...entry,
          studentName: student.studentName,
          familyName: student.familyName,
          instruments: instrumentValuesFor(student),
        };
      });

      if (
        result.metrics.totalStudents > 0 &&
        result.metrics.scheduledCount === 0
      ) {
        setOptimizationSummary({
          type: "warning",
          message: `The optimizer could not place ${result.metrics.totalStudents === 1 ? "this student" : "these students"} without violating availability, calendar, or travel constraints. The current schedule was left unchanged.`,
          scheduled: 0,
          total: result.metrics.totalStudents,
          unassigned: detailedUnassigned,
          warnings: result.warnings || [],
          weekLabel: displayedWeekLabel,
        });
        return result;
      }

      const currentQuality = {
        scheduledCount: currentPlacements.length,
        driveMinutes: beforeMetrics.driveMinutes,
        idleMinutes: beforeMetrics.idleMinutes,
        activeTeacherDays: beforeMetrics.activeTeacherDays,
        // Churn is measured against the current placements, so retaining the
        // current schedule necessarily moves zero students.
        movedCount: 0,
      };
      const currentIsBetter =
        beforeValidation.valid &&
        beforeMetrics.completeDriveData &&
        compareScheduleQuality(currentQuality, result.metrics) <= 0;
      if (currentIsBetter) {
        const currentAssignedKeys = new Set(
          currentPlacements.map((placement) => String(placement.studentKey))
        );
        const resultReasonByStudent = new Map(
          (result.unassigned || []).map((entry) => [
            String(entry.studentKey),
            entry.reason,
          ])
        );
        const currentUnassigned = optimizationStudents
          .filter((student) => !currentAssignedKeys.has(String(student.key)))
          .map((student) => ({
            studentKey: student.key,
            studentName: student.studentName,
            familyName: student.familyName,
            instruments: instrumentValuesFor(student),
            reason:
              resultReasonByStudent.get(String(student.key)) ||
              "No additional placement was found without removing another scheduled student",
          }));
        if (currentPlacements[0]?.teacherId) {
          setSelectedTeacherId(String(currentPlacements[0].teacherId));
        }
        setOptimizationSummary({
          type:
            currentUnassigned.length || result.warnings?.length
              ? "warning"
              : "success",
          message: currentUnassigned.length
            ? `No valid change increased coverage. The current schedule was retained with ${currentPlacements.length} of ${optimizationStudents.length} unlocked students placed.`
            : `The current schedule is already as good as the best schedule found (${currentPlacements.length} placed, ${Math.round(beforeMetrics.driveMinutes)} drive minutes).`,
          scheduled: currentPlacements.length,
          total: optimizationStudents.length,
          beforeDrive: currentPlacements.length
            ? beforeMetrics.driveMinutes
            : null,
          afterDrive: currentPlacements.length
            ? beforeMetrics.driveMinutes
            : null,
          unassigned: currentUnassigned,
          warnings: result.warnings || [],
          weekLabel: displayedWeekLabel,
        });
        return result;
      }

      const optimizedPlacements = result.placements.map((choice) => {
        const source = sourceByKey.get(String(choice.studentKey)) || {};
        const chosenTeacher = optimizationTeachers.find(
          (teacher) => String(teacher.id) === String(choice.teacherId)
        );
        const teacherInstrumentNames = new Set();
        (chosenTeacher?.instruments || []).forEach((entry) => {
          [
            entry?.parentInstrument,
            entry?.instrument,
            entry?.name,
            entry?.label,
            entry,
          ].forEach(
            (value) => {
              if (typeof value === "string" && value.trim()) {
                teacherInstrumentNames.add(value.trim().toLowerCase());
              }
            }
          );
        });
        const matchedInstrument = (source.instruments || []).find((entry) =>
          [
            entry?.parentInstrument,
            entry?.instrument,
            entry?.name,
            entry?.label,
            entry,
          ]
            .filter((value) => typeof value === "string")
            .some((value) => teacherInstrumentNames.has(value.trim().toLowerCase()))
        );
        const instrumentCandidate =
          matchedInstrument?.parentInstrument ||
          matchedInstrument?.instrument ||
          matchedInstrument?.name ||
          matchedInstrument?.label ||
          (typeof matchedInstrument === "string" ? matchedInstrument : null) ||
          source.instruments?.[0]?.parentInstrument ||
          source.instruments?.[0]?.instrument ||
          source.instruments?.[0]?.name ||
          source.instruments?.[0]?.label ||
          (typeof source.instruments?.[0] === "string"
            ? source.instruments[0]
            : null) ||
          (typeof source.instrument === "string" ? source.instrument : null) ||
          null;
        const instrument =
          typeof instrumentCandidate === "string" ? instrumentCandidate : null;
        return {
          id: `opt-${choice.studentKey}-${choice.dayIndex}-${choice.startMinutes}`,
          placementKey: choice.studentKey,
          studentId: source.studentId || choice.studentKey,
          familyId: source.familyId,
          studentName: source.studentName,
          familyName: source.familyName,
          dayIndex: choice.dayIndex,
          teacherId: choice.teacherId,
          startMinutes: choice.startMinutes,
          lessonLength: normalizeLessonDuration(
            choice.duration,
            source.duration,
            source.lessonLength
          ),
          instrument,
          instruments: source.instruments || [],
          availability: source.availability || source.initialAvailability || null,
          familyAddress: source.familyAddress || null,
          placeKey: choice.placeKey || source.placeKey,
          school: selectedSchool,
        };
      });
      const preservedPlacements = plannedPlacements.filter((placement) => {
        const key = String(getStudentKey(placement));
        return lockedKeySet.has(key) || !optimizableKeySet.has(key);
      });
      const finalPlacements = [...preservedPlacements, ...optimizedPlacements];
      setOptimizationUndo({
        plannedPlacements,
        placedStudentIds,
        optimizeLedger,
      });
      setPlannedPlacements(finalPlacements);
      setPlacedStudentIds(
        Array.from(new Set(finalPlacements.map((placement) => getStudentKey(placement)).filter(Boolean)))
      );
      if (optimizedPlacements[0]?.teacherId) {
        setSelectedTeacherId(String(optimizedPlacements[0].teacherId));
      }
      setOptimizeLedger(
        optimizedPlacements.map((placement) => ({
          student: placement.studentName || placement.familyName || placement.placementKey,
          teacher:
            teacherNameById.get(String(placement.teacherId)) || placement.teacherId,
          day: weekDays[placement.dayIndex],
          time: `${Math.floor(placement.startMinutes / 60)
            .toString()
            .padStart(2, "0")}:${String(placement.startMinutes % 60).padStart(2, "0")}`,
        }))
      );
      const hasCompleteBaselineDrive = beforeMetrics.completeDriveData;
      const driveDelta = hasCompleteBaselineDrive
        ? Math.round(result.metrics.driveMinutes - beforeMetrics.driveMinutes)
        : null;
      const drivePhrase = driveDelta < 0
        ? `${Math.abs(driveDelta)} fewer drive minutes`
        : driveDelta > 0
        ? `${driveDelta} more drive minutes to place more students`
        : "no increase in drive time";
      const resultMessage = hasCompleteBaselineDrive
        ? `Best schedule found: ${result.metrics.scheduledCount} of ${result.metrics.totalStudents} unlocked students placed, with ${drivePhrase}.`
        : `Best schedule found: ${result.metrics.scheduledCount} of ${result.metrics.totalStudents} unlocked students placed. The previous route total was unavailable because its drive data was incomplete.`;
      setOptimizationSummary({
        type:
          result.unassigned.length || result.warnings?.length
            ? "warning"
            : "success",
        message: resultMessage,
        scheduled: result.metrics.scheduledCount,
        total: result.metrics.totalStudents,
        beforeDrive: hasCompleteBaselineDrive
          ? beforeMetrics.driveMinutes
          : null,
        afterDrive: result.metrics.driveMinutes,
        unassigned: detailedUnassigned,
        warnings: result.warnings || [],
        weekLabel: displayedWeekLabel,
      });
      return result;
    },
    [
      calendarEventsLoaded,
      calendarEventsLoadError,
      driveEdgesLoadError,
      driveEdgesLoaded,
      displayedWeekLabel,
      getOptimizationDriveMinutes,
      getStudentKey,
      lockedStudentIds,
      maxDriveMinutes,
      optimizationFixedEvents,
      optimizationStudents,
      optimizationTeachers,
      optimizationTeachersWithAvailability,
      optimizeLedger,
      paddingMinutes,
      placeDirectoryByKey,
      placeDirectoryLoadError,
      placeDirectoryLoaded,
      placedStudentIds,
      plannedPlacements,
      resolveStudentPlace,
      resolvingManualLocations,
      schedulableOptimizationTeachers,
      selectedSchool,
      teacherNameById,
    ]
  );

  const optimizeSchedule = React.useCallback(async () => {
    setIsOptimizing(true);
    setOptimizationSummary({ type: "info", message: "Searching for the best schedule…" });
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
      runImprovedOptimization();
    } finally {
      setIsOptimizing(false);
    }
  }, [runImprovedOptimization]);

  const undoOptimization = React.useCallback(() => {
    if (!optimizationUndo) return;
    setPlannedPlacements(optimizationUndo.plannedPlacements || []);
    setPlacedStudentIds(optimizationUndo.placedStudentIds || []);
    setOptimizeLedger(optimizationUndo.optimizeLedger || []);
    setOptimizationUndo(null);
    setOptimizationSummary({ type: "info", message: "The previous schedule was restored." });
  }, [optimizationUndo]);

  // Track missing drive edges only between consecutive lessons for the selected teacher.
  React.useEffect(() => {
    if (!selectedSchool || !selectedTeacherId) {
      setMissingTeacherDrivePairs([]);
      setShowMissingTeacherDriveModal(false);
      return;
    }
    const labelByKey = new Map();
    placeDirectory.forEach((pd) => {
      if (!pd?.key) return;
      labelByKey.set(String(pd.key), pd.label || pd.key);
    });
    const missing = [];
    const teacherHomeKey = placeKeyByTeacher.get(String(selectedTeacherId)) || null;
    const teacherHomeLabel = selectedTeacherName ? `${selectedTeacherName} (Home)` : "Teacher Home";
    weekDays.forEach((day) => {
      const events = (positionedEventsByDay[day] || []).slice();
      if (!events.length) return;
      events.sort((a, b) => a.startMinutesOfDay - b.startMinutesOfDay);
      if (teacherHomeKey) {
        const firstEvent = events[0];
        const lastEvent = events[events.length - 1];
        const firstKey = getDriveKey(firstEvent);
        const lastKey = getDriveKey(lastEvent);
        if (firstKey && String(firstKey) !== String(teacherHomeKey)) {
          const hasMinutes = minutesMap.has(makeDriveKey(teacherHomeKey, firstKey));
          if (!hasMinutes) {
            missing.push({
              id: `${day}-home-first-${teacherHomeKey}-${firstKey}`,
              day,
              time: `Home → ${firstEvent.timeLabel}`,
              fromKey: teacherHomeKey,
              toKey: firstKey,
              fromLabel: teacherHomeLabel,
              toLabel:
                labelByKey.get(String(firstKey)) ||
                firstEvent.familyName ||
                firstEvent.studentName ||
                firstEvent.label ||
                firstKey,
            });
          }
        }
        if (lastKey && String(lastKey) !== String(teacherHomeKey)) {
          const hasMinutes = minutesMap.has(makeDriveKey(lastKey, teacherHomeKey));
          if (!hasMinutes) {
            missing.push({
              id: `${day}-last-home-${lastKey}-${teacherHomeKey}`,
              day,
              time: `${lastEvent.timeLabel} → Home`,
              fromKey: lastKey,
              toKey: teacherHomeKey,
              fromLabel:
                labelByKey.get(String(lastKey)) ||
                lastEvent.familyName ||
                lastEvent.studentName ||
                lastEvent.label ||
                lastKey,
              toLabel: teacherHomeLabel,
            });
          }
        }
      }
      if (events.length > 1) {
        for (let i = 0; i < events.length - 1; i++) {
          const fromEvent = events[i];
          const toEvent = events[i + 1];
          const fromKey = getDriveKey(fromEvent);
          const toKey = getDriveKey(toEvent);
          if (!fromKey || !toKey || String(fromKey) === String(toKey)) continue;
          const hasMinutes = minutesMap.has(makeDriveKey(fromKey, toKey));
          if (hasMinutes) continue;
          missing.push({
            id: `${day}-${i}-${fromKey}-${toKey}`,
            day,
            time: `${fromEvent.timeLabel} → ${toEvent.timeLabel}`,
            fromKey,
            toKey,
            fromLabel:
              labelByKey.get(String(fromKey)) ||
              fromEvent.familyName ||
              fromEvent.studentName ||
              fromEvent.label ||
              fromKey,
            toLabel:
              labelByKey.get(String(toKey)) ||
              toEvent.familyName ||
              toEvent.studentName ||
              toEvent.label ||
              toKey,
          });
        }
      }
    });
    setMissingTeacherDrivePairs(missing);
    const prev = lastMissingTeacherRef.current;
    const shouldOpen =
      missing.length > 0 &&
      (String(prev.teacherId) !== String(selectedTeacherId) || prev.count === 0);
    if (shouldOpen) {
      setShowMissingTeacherDriveModal(true);
    }
    lastMissingTeacherRef.current = { teacherId: selectedTeacherId, count: missing.length };
  }, [
    getDriveKey,
    makeDriveKey,
    minutesMap,
    placeKeyByTeacher,
    placeDirectory,
    positionedEventsByDay,
    selectedSchool,
    selectedTeacherId,
    selectedTeacherName,
  ]);

  const buildMissingReassignmentDrivePairs = React.useCallback(() => {
    const paddingCap =
      paddingMinutes !== "" && Number.isFinite(Number(paddingMinutes))
        ? Math.max(0, Number(paddingMinutes))
        : 0;
    const maxDriveCap =
      maxDriveMinutes !== "" && Number.isFinite(Number(maxDriveMinutes))
        ? Math.max(0, Number(maxDriveMinutes))
        : null;
    return collectRequiredDrivePairs({
      teachers: schedulableOptimizationTeachers,
      students: optimizationStudents,
      fixedEvents: optimizationFixedEvents,
      travelMinutes: getOptimizationDriveMinutes,
      paddingMinutes: paddingCap,
      maxDriveMinutes: maxDriveCap,
    });
  }, [
    getOptimizationDriveMinutes,
    maxDriveMinutes,
    optimizationFixedEvents,
    optimizationStudents,
    paddingMinutes,
    schedulableOptimizationTeachers,
  ]);

  React.useEffect(() => {
    if (!promptReassignmentDriveTimes) return;
    if (!calendarEventsLoaded) return;
    const missingPairs = buildMissingReassignmentDrivePairs();
    setMissingReassignmentDrivePairs(missingPairs);
    if (missingPairs.length) {
      setShowMissingReassignmentDriveModal(true);
    }
    setPromptReassignmentDriveTimes(false);
  }, [
    buildMissingReassignmentDrivePairs,
    calendarEventsLoaded,
    promptReassignmentDriveTimes,
  ]);

  const mergeDriveEdges = React.useCallback((edges) => {
    if (!edges?.length) return;
    setDriveEdges((prev) => {
      const next = new Map();
      prev.forEach((edge) => {
        const fromKey = edge.origin?.key || edge.origin || edge.fromKey || edge.from;
        const toKey = edge.destination?.key || edge.destination || edge.toKey || edge.to;
        const key = `${fromKey}=>${toKey}`;
        next.set(key, edge);
      });
      edges.forEach((edge) => {
        const fromKey = edge.origin?.key || edge.origin || edge.fromKey || edge.from;
        const toKey = edge.destination?.key || edge.destination || edge.toKey || edge.to;
        const key = `${fromKey}=>${toKey}`;
        next.set(key, edge);
      });
      return Array.from(next.values());
    });
  }, []);

  const handleCalculateMissingTeacherDriveTimes = React.useCallback(async () => {
    if (!missingTeacherDrivePairs.length) return;
    const uniquePairs = [];
    const seen = new Set();
    missingTeacherDrivePairs.forEach((pair) => {
      if (!pair?.fromKey || !pair?.toKey) return;
      const key = `${pair.fromKey}=>${pair.toKey}`;
      if (seen.has(key)) return;
      seen.add(key);
      uniquePairs.push({ fromKey: pair.fromKey, toKey: pair.toKey });
    });
    if (!uniquePairs.length) return;
    setCalculatingMissingDriveTimes(true);
    try {
      const edges = (await ensureAllPairs(uniquePairs)) || [];
      mergeDriveEdges(edges);
      setShowMissingTeacherDriveModal(false);
    } finally {
      setCalculatingMissingDriveTimes(false);
    }
  }, [ensureAllPairs, mergeDriveEdges, missingTeacherDrivePairs]);

  const handleCalculateMissingReassignmentDriveTimes = React.useCallback(async () => {
    if (!missingReassignmentDrivePairs.length) return;
    const uniquePairs = [];
    const seen = new Set();
    missingReassignmentDrivePairs.forEach((pair) => {
      if (!pair?.fromKey || !pair?.toKey) return;
      const key = `${pair.fromKey}=>${pair.toKey}`;
      if (seen.has(key)) return;
      seen.add(key);
      uniquePairs.push({ fromKey: pair.fromKey, toKey: pair.toKey });
    });
    if (!uniquePairs.length) return;
    setCalculatingReassignmentDriveTimes(true);
    try {
      const edges = (await ensureAllPairs(uniquePairs)) || [];
      mergeDriveEdges(edges);
      const mergedMinutes = new Map(minutesMap);
      edges.forEach((edge) => {
        const fromKey = edge.origin?.key || edge.origin || edge.fromKey || edge.from;
        const toKey =
          edge.destination?.key || edge.destination || edge.toKey || edge.to;
        const minutes = normalizeDriveMinutes(edge.minutes);
        if (!fromKey || !toKey || minutes == null) return;
        mergedMinutes.set(makeDriveKey(fromKey, toKey), minutes);
      });
      const mergedLookup = (fromKey, toKey) => {
        if (!fromKey || !toKey) return null;
        if (String(fromKey) === String(toKey)) return 0;
        const value = mergedMinutes.get(makeDriveKey(fromKey, toKey));
        return normalizeDriveMinutes(value);
      };
      const stillMissing = collectRequiredDrivePairs({
        teachers: schedulableOptimizationTeachers,
        students: optimizationStudents,
        fixedEvents: optimizationFixedEvents,
        travelMinutes: mergedLookup,
        paddingMinutes:
          paddingMinutes !== "" && Number.isFinite(Number(paddingMinutes))
            ? Math.max(0, Number(paddingMinutes))
            : 0,
        maxDriveMinutes:
          maxDriveMinutes !== "" && Number.isFinite(Number(maxDriveMinutes))
            ? Math.max(0, Number(maxDriveMinutes))
            : null,
      });
      setMissingReassignmentDrivePairs(stillMissing);
      if (stillMissing.length) {
        setOptimizationSummary({
          type: "error",
          message: `${stillMissing.length} drive time${stillMissing.length === 1 ? " is" : "s are"} still unavailable. The schedule was not changed.`,
        });
        return;
      }
      setShowMissingReassignmentDriveModal(false);
      if (pendingOptimizationRef.current) {
        setIsOptimizing(true);
        runImprovedOptimization(mergedLookup);
      }
    } finally {
      setIsOptimizing(false);
      setCalculatingReassignmentDriveTimes(false);
    }
  }, [
    ensureAllPairs,
    makeDriveKey,
    maxDriveMinutes,
    mergeDriveEdges,
    minutesMap,
    missingReassignmentDrivePairs,
    optimizationFixedEvents,
    optimizationStudents,
    paddingMinutes,
    runImprovedOptimization,
    schedulableOptimizationTeachers,
  ]);

  const driveEdgesForModal = React.useMemo(() => {
    const labelByKey = new Map();
    placeDirectory.forEach((pd) => {
      const label = pd.label || pd.key || pd.address || "Place";
      if (pd.key) labelByKey.set(pd.key, label);
    });
    const edges = (driveEdges || []).map((edge, idx) => {
      const fromKey =
        edge.origin?.key || edge.fromKey || edge.origin || edge.from || edge.key || `edge-${idx}-from`;
      const toKey =
        edge.destination?.key || edge.toKey || edge.destination || edge.to || `edge-${idx}-to`;
      return {
        id: edge.id || edge._id || `${fromKey}=>${toKey}-${idx}`,
        fromKey,
        toKey,
        fromLabel: edge.origin?.label || labelByKey.get(fromKey) || fromKey,
        toLabel: edge.destination?.label || labelByKey.get(toKey) || toKey,
        minutes: normalizeDriveMinutes(edge.minutes),
        bucket: edge.todBucket || edge.bucket,
      };
    });
    edges.sort((a, b) => (a.fromLabel || "").localeCompare(b.fromLabel || ""));
    return edges;
  }, [driveEdges, placeDirectory]);

  const createPlaceDirectoriesFromModal = React.useCallback(async () => {
    if (!dropdownTeachersForModal.length || !selectedSchoolId) return;
    setCreatingPlaceDirectories(true);
    try {
      const directories = dropdownTeachersForModal
        .filter((f) => f.address)
        .map((f) => ({
          key: f.address || f.teacherName,
          address: f.address,
          label: f.teacherName,
          roles: ["teacher"],
          familyIds: [],
          teacherIds: f.teacherId ? [f.teacherId] : [],
        }));

      if (!directories.length) {
        setCreatingPlaceDirectories(false);
        return;
      }

      await axios
        .post(
          `${baseUrl}/place-directories/bulk`,
          {
            schoolId: selectedSchoolId,
            directories,
          },
          getRequestHeaders()
        )
        .catch((err) => {
          console.log("place-directories bulk failed", err?.response?.data || err?.message);
        });

      // refresh place directory
      axios
        .get(`${baseUrl}/place-directory/${selectedSchoolId}`, getRequestHeaders())
        .then((res) => setPlaceDirectory(Array.isArray(res.data) ? res.data : []))
        .catch(() => {});
    } finally {
      setCreatingPlaceDirectories(false);
    }
  }, [dropdownTeachersForModal, selectedSchoolId, setPlaceDirectory]);

  const createMissingPlaceDirectories = React.useCallback(async () => {
    if (!selectedSchoolId) return;
    setCreatingPlaceDirectories(true);
    try {
      await createPlaceDirectoriesFromModal();
      for (const student of missingPlaceSummary.missingStudents) {
        await ensureStudentPlaceForReassignment(student);
      }
      if (missingPlaceSummary.missingEventFamilies.size) {
        for (const { ev } of weeklyTeacherEventsForModal) {
          const fid = ev?.familyId || ev?.familyID;
          if (!fid || !missingPlaceSummary.missingEventFamilies.has(String(fid))) continue;
          const tempStudent = {
            familyId: fid,
            familyName: ev.familyName,
            studentName: ev.studentName,
            familyAddress: ev.familyAddress || ev.address,
            address: ev.address,
          };
          const existingKey = resolveStudentPlace(tempStudent);
          await ensurePlaceForStudent(tempStudent, existingKey);
        }
      }
      for (const { ev, idx } of missingPlaceSummary.missingExternalEvents) {
        const suppliedAddress =
          ev?.familyAddress ||
          ev?.address ||
          ev?.locationAddress ||
          ev?.location ||
          null;
        const tempEventPlace = {
          id: `calendar-place-${ev?.id || ev?._id || idx}`,
          familyName: ev?.title || ev?.label || ev?.name || "Calendar event",
          familyAddress: suppliedAddress,
          address: suppliedAddress,
        };
        await ensurePlaceForStudent(tempEventPlace, tempEventPlace.id);
      }
      const payload = await axios.get(`${baseUrl}/place-directory/${selectedSchoolId}`, getRequestHeaders());
      const dirs = payload?.data?.directories || payload?.data || [];
      setPlaceDirectory(Array.isArray(dirs) ? dirs : []);
      setShowMissingPlaceModal(false);
    } catch (err) {
      console.log(err);
    } finally {
      setCreatingPlaceDirectories(false);
    }
  }, [
    createPlaceDirectoriesFromModal,
    ensurePlaceForStudent,
    ensureStudentPlaceForReassignment,
    missingPlaceSummary.missingEventFamilies,
    missingPlaceSummary.missingExternalEvents,
    missingPlaceSummary.missingStudents,
    resolveStudentPlace,
    selectedSchoolId,
    weeklyTeacherEventsForModal,
  ]);

  const onDragStart = (student, domEvent) => {
    if (domEvent?.dataTransfer) {
      domEvent.dataTransfer.setDragImage(ensureTransparentImg(), 0, 0);
    }
    setDraggingStudent(student);
    setDraggingPlacement(null);
    setDragPreview({ x: domEvent.clientX, y: domEvent.clientY, label: student.familyName || student.studentName || "…" });
  };

  const onDragStartPlacement = (placement, domEvent) => {
    if (!placement.planned) return;
    if (lockedStudentIds.includes(getStudentKey(placement))) {
      domEvent?.preventDefault?.();
      setOptimizationSummary({
        type: "info",
        message: "Unlock this lesson before moving it.",
      });
      return;
    }
    setDraggingPlacement(placement);
    setDraggingStudent(null);
    if (domEvent?.dataTransfer) {
      domEvent.dataTransfer.setDragImage(ensureTransparentImg(), 0, 0);
    }
    setDragPreview({
      x: domEvent?.clientX || 0,
      y: domEvent?.clientY || 0,
      label: placement.familyName || placement.studentName || "…",
    });
  };

  const onDragOverDayArea = (dayName, e) => {
    if ((!draggingStudent && !draggingPlacement) || !selectedTeacherId) return;
    const ref = dayRefs.current[dayName];
    if (!ref) return;
    const rect = ref.getBoundingClientRect();
    const offsetY = e.clientY - rect.top + ref.scrollTop;
    const rawMinutes = Math.round((offsetY / PIXELS_PER_SLOT) * SLOT_MINUTES);
    const clampedMinutes = Math.min(Math.max(rawMinutes, 0), timeWindow.totalMinutes);
    const slotMinutes = Math.round(clampedMinutes / SLOT_MINUTES) * SLOT_MINUTES;
    // throttle updates: only when day or slot changes
    if (lastPreviewSlotRef.current.day === dayName && lastPreviewSlotRef.current.slot === slotMinutes) {
      e.preventDefault();
      return;
    }
    lastPreviewSlotRef.current = { day: dayName, slot: slotMinutes };
    const slotDate = new Date(startOfThisWeek);
    slotDate.setDate(slotDate.getDate() + weekDays.indexOf(dayName));
    const totalMinutesFromMidnight = timeWindow.startMinutes + slotMinutes;
    slotDate.setHours(Math.floor(totalMinutesFromMidnight / 60));
    slotDate.setMinutes(totalMinutesFromMidnight % 60);
    slotDate.setSeconds(0);
    const label = format(slotDate, "h:mma");
    if (lastHoverRef.current.day !== dayName || lastHoverRef.current.label !== label) {
      lastHoverRef.current = { day: dayName, label };
      setHoverIndicator({ day: dayName, label });
    }
    setDragPreview({ x: e.clientX, y: e.clientY, label: `${dayName} ${label}` });
    e.preventDefault();
  };

  const validateManualPlacement = (student, placement, driveLookup, movingKey = null) => {
    const lockedKeys = new Set((lockedStudentIds || []).map(String));
    const otherPlannedEvents = plannedPlacements
      .filter((existing) => {
        const key = getStudentKey(existing);
        return (
          String(key) !== String(movingKey || "") &&
          !lockedKeys.has(String(key))
        );
      })
      .map((existing, index) => ({
        id: existing.id || `manual-existing-${index}`,
        studentKey: getStudentKey(existing),
        teacherId: String(existing.teacherId || ""),
        dayIndex: Number(existing.dayIndex),
        start: Number(existing.startMinutes),
        end:
          Number(existing.startMinutes) +
          normalizeLessonDuration(existing.lessonLength, existing.duration),
        placeKey: resolveStudentPlace(existing),
        locked: true,
      }));
    const studentKey = getStudentKey(student);
    const candidateStudent = {
      ...student,
      key: studentKey,
      placementKey: studentKey,
      placeKey: placement.placeKey,
      duration: Number(placement.lessonLength) || 30,
      instruments:
        Array.isArray(student.instruments) && student.instruments.length
          ? student.instruments
          : Array.isArray(placement.instruments) && placement.instruments.length
          ? placement.instruments
          : placement.instrument
          ? [placement.instrument]
          : [],
      availability:
        student.availability || student.initialAvailability || placement.availability || null,
    };
    return validateOptimizedSchedule({
      teachers: optimizationTeachers,
      students: [candidateStudent],
      fixedEvents: [...optimizationFixedEvents, ...otherPlannedEvents],
      placements: [
        {
          studentKey,
          teacherId: String(placement.teacherId),
          dayIndex: Number(placement.dayIndex),
          startMinutes: Number(placement.startMinutes),
          endMinutes:
            Number(placement.startMinutes) + (Number(placement.lessonLength) || 30),
          placeKey: placement.placeKey,
        },
      ],
      travelMinutes: driveLookup,
      paddingMinutes:
        paddingMinutes !== "" && Number.isFinite(Number(paddingMinutes))
          ? Math.max(0, Number(paddingMinutes))
          : 0,
      maxDriveMinutes:
        maxDriveMinutes !== "" && Number.isFinite(Number(maxDriveMinutes))
          ? Math.max(0, Number(maxDriveMinutes))
          : null,
    });
  };

  const onDropOnDayArea = async (dayName, e) => {
    if ((!draggingStudent && !draggingPlacement) || !selectedTeacherId) return;
    if (
      !calendarEventsLoaded ||
      calendarEventsLoadError ||
      placeDirectoryLoadError ||
      driveEdgesLoadError ||
      !placeDirectoryLoaded ||
      !driveEdgesLoaded
    ) {
      setOptimizationSummary({
        type: "info",
        message: calendarEventsLoadError
          ? "Retry the failed calendar request before placing lessons."
          : placeDirectoryLoadError || driveEdgesLoadError
          ? `${placeDirectoryLoadError || driveEdgesLoadError} Retry before placing lessons.`
          : "Calendar, location, and drive data are still loading.",
      });
      setDraggingStudent(null);
      setDraggingPlacement(null);
      setDragPreview(null);
      return;
    }
    const fetchedDriveMinutes = new Map();
    const rememberDriveEdges = (edges) => {
      (edges || []).forEach((edge) => {
        const fromKey = edge.origin?.key || edge.origin || edge.fromKey || edge.from;
        const toKey =
          edge.destination?.key || edge.destination || edge.toKey || edge.to;
        const minutes = normalizeDriveMinutes(edge.minutes);
        if (!fromKey || !toKey || minutes == null) return;
        fetchedDriveMinutes.set(`${fromKey}=>${toKey}`, minutes);
      });
    };
    const manualDriveLookup = (fromKey, toKey) => {
      if (!fromKey || !toKey) return null;
      if (String(fromKey) === String(toKey)) return 0;
      const fetched = fetchedDriveMinutes.get(`${fromKey}=>${toKey}`);
      return Number.isFinite(fetched)
        ? fetched
        : getOptimizationDriveMinutes(fromKey, toKey);
    };
    const ref = dayRefs.current[dayName];
    if (!ref) return;
    const rect = ref.getBoundingClientRect();
    const offsetY = e.clientY - rect.top + ref.scrollTop;
    const rawMinutes = Math.round((offsetY / PIXELS_PER_SLOT) * SLOT_MINUTES);
    const clampedMinutes = Math.min(Math.max(rawMinutes, 0), timeWindow.totalMinutes);
    const slotMinutes = Math.round(clampedMinutes / SLOT_MINUTES) * SLOT_MINUTES;
    const dayIndex = weekDays.indexOf(dayName);
    const slotDate = new Date(displayedWeek[dayIndex]);
    const totalMinutesFromMidnight = timeWindow.startMinutes + slotMinutes;
    slotDate.setHours(Math.floor(totalMinutesFromMidnight / 60));
    slotDate.setMinutes(totalMinutesFromMidnight % 60);
    slotDate.setSeconds(0);
    // hydrate placement details
    if (draggingStudent) {
      let instrumentLabel =
        draggingStudent.instruments?.[0]?.parentInstrument ||
        draggingStudent.instruments?.[0]?.instrument ||
        null;
      let lessonLength = Number(draggingStudent.lessonLength) || null;
      let familyAddress = draggingStudent.familyAddress || null;

      const familyInfo = await getFamilyInfo(draggingStudent.familyId);
      if (familyInfo) {
        const match =
          familyInfo.students?.find(
            (s) => String(s.studentId) === String(draggingStudent.studentId)
          ) || {};
        if (!instrumentLabel && match.instrumentList?.length) {
          instrumentLabel = match.instrumentList[0]?.parentInstrument || null;
        }
        if (!lessonLength && Number(match.lessonLength) > 0) {
          lessonLength = Number(match.lessonLength);
        }
        familyAddress = familyInfo.address || familyInfo.familyAddress || familyAddress;
      }
      lessonLength = lessonLength || 30;

      let studentPlaceKey = resolveStudentPlace(draggingStudent);
      const teacherPlaceKey = placeKeyByTeacher.get(String(selectedTeacherId));
      console.log("Looking for Drivetime");
      let driveMinutes =
        teacherPlaceKey && studentPlaceKey ? minutesBetween(teacherPlaceKey, studentPlaceKey) : Infinity;
      let missingDriveInfo = !Number.isFinite(driveMinutes);
      if (missingDriveInfo) {
        studentPlaceKey = await ensurePlaceForStudent(draggingStudent, studentPlaceKey);
        if (teacherPlaceKey && studentPlaceKey) {
          const edges = (await ensureAllPairs([{ fromKey: teacherPlaceKey, toKey: studentPlaceKey }])) || [];
          rememberDriveEdges(edges);
          const found = edges.find(
            (edge) =>
              (edge.origin?.key || edge.fromKey) === teacherPlaceKey &&
              (edge.destination?.key || edge.toKey) === studentPlaceKey
          );
          const foundMinutes = normalizeDriveMinutes(found?.minutes);
          driveMinutes =
            foundMinutes != null
              ? foundMinutes
              : minutesBetween(teacherPlaceKey, studentPlaceKey);
          missingDriveInfo = !Number.isFinite(driveMinutes);
          console.log("Drive edge lookup (student drop)", {
            teacherPlaceKey,
            studentPlaceKey,
            foundMinutes: found?.minutes,
            driveMinutes,
            edge: found,
            edgesReturned: edges.length,
          });
        }
      } else {
        console.log("Drive edge cache hit (student drop)", {
          teacherPlaceKey,
          studentPlaceKey,
          driveMinutes,
        });
      }
      // Also ensure edges between this student and other known places for the teacher/day (home + events on this day)
      const neighborPlaceKeys = new Set();
      const dayEvents = positionedEventsByDay[dayName] || [];
      dayEvents.forEach((ev) => {
        const tid = String(ev.teacherId || ev.teacherID || ev.TeacherID || selectedTeacherId || "");
        if (tid === String(selectedTeacherId)) {
          const pk = getDriveKey(ev);
          if (pk) neighborPlaceKeys.add(pk);
        }
      });
      if (teacherPlaceKey) neighborPlaceKeys.add(teacherPlaceKey);
      const pairRequests = [];
      neighborPlaceKeys.forEach((pk) => {
        if (!studentPlaceKey || pk === studentPlaceKey) return;
        pairRequests.push({ fromKey: pk, toKey: studentPlaceKey });
        pairRequests.push({ fromKey: studentPlaceKey, toKey: pk });
      });
      if (pairRequests.length) {
        rememberDriveEdges((await ensureAllPairs(pairRequests)) || []);
        driveMinutes =
          teacherPlaceKey && studentPlaceKey
            ? manualDriveLookup(teacherPlaceKey, studentPlaceKey)
            : Infinity;
        missingDriveInfo = !Number.isFinite(driveMinutes);
      }

      const placement = {
        id: `${getStudentKey(draggingStudent)}-${dayName}-${slotMinutes}`,
        placementKey: getStudentKey(draggingStudent),
        studentId: draggingStudent.studentId || getStudentKey(draggingStudent),
        familyId: draggingStudent.familyId,
        studentName: draggingStudent.studentName,
        familyName: draggingStudent.familyName,
        dayIndex,
        teacherId: selectedTeacherId,
        startMinutes: timeWindow.startMinutes + slotMinutes,
        lessonLength,
        instrument: instrumentLabel,
        instruments: draggingStudent.instruments || (instrumentLabel ? [instrumentLabel] : []),
        availability: draggingStudent.availability || draggingStudent.initialAvailability || null,
        familyAddress,
        placeKey: studentPlaceKey,
        school: selectedSchool,
      };
      const validation = validateManualPlacement(
        draggingStudent,
        placement,
        manualDriveLookup
      );
      if (!validation.valid) {
        setOptimizationSummary({
          type: "error",
          message: `Placement not added: ${validation.errors[0]}`,
        });
        setDraggingStudent(null);
        setHoverIndicator(null);
        setDragPreview(null);
        return;
      }
      invalidateOptimizationResult();
      setPlannedPlacements((prev) => {
        const filtered = prev.filter((p) => !(p.id === placement.id));
        return [...filtered, placement];
      });
      setPlacedStudentIds((prev) => {
        const studentKey = getStudentKey(draggingStudent);
        return prev.includes(studentKey) ? prev : [...prev, studentKey];
      });
      setDraggingStudent(null);
      if (missingDriveInfo) {
        setNoDriveContext({
          teacher: teacherNameById.get(String(selectedTeacherId)) || selectedTeacherId,
          student: draggingStudent.studentName || draggingStudent.familyName || draggingStudent.id,
          fromKey: teacherPlaceKey,
          toKey: studentPlaceKey,
        });
        setShowNoDriveModal(true);
      }
    } else if (draggingPlacement) {
      const placementKey = draggingPlacement.placementKey || draggingPlacement.id;
      const lessonLength = draggingPlacement.lessonLength || 30;
      let studentPlaceKey = resolveStudentPlace(draggingPlacement);
      const teacherPlaceKey = placeKeyByTeacher.get(String(selectedTeacherId));
      let driveMinutes =
        teacherPlaceKey && studentPlaceKey ? minutesBetween(teacherPlaceKey, studentPlaceKey) : Infinity;
      let missingDriveInfo = !Number.isFinite(driveMinutes);
      if (missingDriveInfo) {
        studentPlaceKey = await ensurePlaceForStudent(draggingPlacement, studentPlaceKey);
        if (teacherPlaceKey && studentPlaceKey) {
          const edges = (await ensureAllPairs([{ fromKey: teacherPlaceKey, toKey: studentPlaceKey }])) || [];
          rememberDriveEdges(edges);
          const found = edges.find(
            (edge) =>
              (edge.origin?.key || edge.fromKey) === teacherPlaceKey &&
              (edge.destination?.key || edge.toKey) === studentPlaceKey
          );
          const foundMinutes = normalizeDriveMinutes(found?.minutes);
          driveMinutes =
            foundMinutes != null
              ? foundMinutes
              : minutesBetween(teacherPlaceKey, studentPlaceKey);
          missingDriveInfo = !Number.isFinite(driveMinutes);
          console.log("Drive edge lookup (placement drop)", {
            teacherPlaceKey,
            studentPlaceKey,
            foundMinutes: found?.minutes,
            driveMinutes,
            edge: found,
            edgesReturned: edges.length,
          });
        }
      } else {
        console.log("Drive edge cache hit (placement drop)", {
          teacherPlaceKey,
          studentPlaceKey,
          driveMinutes,
        });
      }
      const neighborPlaceKeys = new Set();
      const dayEvents = positionedEventsByDay[dayName] || [];
      dayEvents.forEach((ev) => {
        const tid = String(ev.teacherId || ev.teacherID || ev.TeacherID || selectedTeacherId || "");
        if (tid === String(selectedTeacherId)) {
          const pk = getDriveKey(ev);
          if (pk) neighborPlaceKeys.add(pk);
        }
      });
      if (teacherPlaceKey) neighborPlaceKeys.add(teacherPlaceKey);
      const pairRequests = [];
      neighborPlaceKeys.forEach((pk) => {
        if (!studentPlaceKey || pk === studentPlaceKey) return;
        pairRequests.push({ fromKey: pk, toKey: studentPlaceKey });
        pairRequests.push({ fromKey: studentPlaceKey, toKey: pk });
      });
      if (pairRequests.length) {
        rememberDriveEdges((await ensureAllPairs(pairRequests)) || []);
        driveMinutes =
          teacherPlaceKey && studentPlaceKey
            ? manualDriveLookup(teacherPlaceKey, studentPlaceKey)
            : Infinity;
        missingDriveInfo = !Number.isFinite(driveMinutes);
      }
      const updatedPlacement = {
        ...draggingPlacement,
        id: `${placementKey}-${dayName}-${slotMinutes}`,
        placementKey,
        studentId: draggingPlacement.studentId || placementKey,
        dayIndex,
        teacherId: selectedTeacherId,
        startMinutes: timeWindow.startMinutes + slotMinutes,
        lessonLength,
        placeKey: studentPlaceKey,
        school: selectedSchool,
      };
      const validation = validateManualPlacement(
        draggingPlacement,
        updatedPlacement,
        manualDriveLookup,
        getStudentKey(draggingPlacement)
      );
      if (!validation.valid) {
        setOptimizationSummary({
          type: "error",
          message: `Placement not moved: ${validation.errors[0]}`,
        });
        setDraggingPlacement(null);
        setHoverIndicator(null);
        setDragPreview(null);
        return;
      }
      invalidateOptimizationResult();
      setPlannedPlacements((prev) => {
        const filtered = prev.filter((p) => (p.placementKey || p.id) !== placementKey);
        return [...filtered, updatedPlacement];
      });
      setDraggingPlacement(null);
      if (missingDriveInfo) {
        setNoDriveContext({
          teacher: teacherNameById.get(String(selectedTeacherId)) || selectedTeacherId,
          student:
            draggingPlacement.studentName || draggingPlacement.familyName || draggingPlacement.id,
          fromKey: teacherPlaceKey,
          toKey: studentPlaceKey,
        });
        setShowNoDriveModal(true);
      }
    }
    lastHoverRef.current = { day: null, label: null };
    setHoverIndicator(null);
    setDragPreview(null);
  };

  const onDragOverStudentColumn = (e) => {
    if (draggingPlacement || draggingStudent) {
      e.preventDefault();
      if (!isHoveringStudentColumn) setIsHoveringStudentColumn(true);
      // compute insert index based on cursor position
      if (studentListRef.current) {
        const cards = Array.from(
          studentListRef.current.querySelectorAll(".dds2025-student-card")
        );
        let insertAt = cards.length;
        for (let i = 0; i < cards.length; i++) {
          const rect = cards[i].getBoundingClientRect();
          if (e.clientY < rect.top + rect.height / 2) {
            insertAt = i;
            break;
          }
        }
        if (insertAt !== hoverInsertIndex) {
          setHoverInsertIndex(insertAt);
        }
      }
    }
  };

  const onDragLeaveStudentColumn = (e) => {
    if (e && studentListRef.current && e.relatedTarget) {
      // Ignore leave events when moving between children inside the list
      if (studentListRef.current.contains(e.relatedTarget)) return;
    }
    if (isHoveringStudentColumn) setIsHoveringStudentColumn(false);
    if (hoverInsertIndex !== null) setHoverInsertIndex(null);
  };

  const onDropOnStudentColumn = (e) => {
    e.preventDefault();
    setIsHoveringStudentColumn(false);
    setHoverInsertIndex(null);
    if (draggingPlacement) {
      const sid = getStudentKey(draggingPlacement);
      // Don't remove locked placements
      if (!lockedStudentIds.includes(sid)) {
        invalidateOptimizationResult();
        setPlannedPlacements((prev) =>
          prev.filter(
            (p) =>
              (p.placementKey || p.studentId || p.id) !==
              (draggingPlacement.placementKey || draggingPlacement.studentId || draggingPlacement.id)
          )
        );
        setPlacedStudentIds((prev) =>
          prev.filter(
            (id) =>
              id !== sid &&
              id !== (draggingPlacement.placementKey || draggingPlacement.studentId || draggingPlacement.id)
          )
        );
        setRemovedReassignmentIds((prev) => prev.filter((id) => String(id) !== String(sid)));
        const reassignmentStudent = {
          id: sid,
          familyId: draggingPlacement.familyId,
          familyName: draggingPlacement.familyName,
          studentId: draggingPlacement.studentId || draggingPlacement.placementKey || draggingPlacement.id,
          studentName: draggingPlacement.studentName,
          instruments: draggingPlacement.instrument
            ? [{ parentInstrument: draggingPlacement.instrument, instrument: draggingPlacement.instrument }]
            : draggingPlacement.instruments || [],
          lessonLength: draggingPlacement.lessonLength,
          availability: draggingPlacement.availability,
          school: selectedSchool,
          familyAddress: draggingPlacement.familyAddress,
          teacherId: draggingPlacement.teacherId,
        };
        if (Array.isArray(overrideReassignmentStudents)) {
          setOverrideReassignmentStudents((prev) => {
            const base = Array.isArray(prev) ? prev : [];
            const exists = base.some((s) => String(s.id || s.studentId) === String(sid));
            return exists ? base : [...base, reassignmentStudent];
          });
        } else {
          setManualReassignmentStudents((prev) => {
            const exists = prev.some((s) => String(s.id || s.studentId) === String(sid));
            return exists ? prev : [...prev, reassignmentStudent];
          });
        }
      }
      setDraggingPlacement(null);
      setDragPreview(null);
    } else if (draggingStudent) {
      setDraggingStudent(null);
      setDragPreview(null);
    }
  };

  const renderStudentCard = (student) => {
    const sid = getStudentKey(student);
    const isLocked = lockedStudentIds.includes(sid);
    const instrumentLabels = displayInstrumentLabels(student);

    return (
      <div
        key={sid}
        className="dds2025-student-card"
        draggable
        onDragStart={(e) => onDragStart(student, e)}
        onDragEnd={() => {
          setDraggingStudent(null);
          setDragPreview(null);
        }}
      >
        <div className="dds2025-student-header">
          <div className="dds2025-student-name-row">
            <span className="dds2025-drag-hint">⋮⋮</span>
            <div className="dds2025-student-name">
              {student.studentName} {student.familyName}
            </div>
          </div>
          <div className="dds2025-student-chips">
            <span className="dds2025-chip">{student.lessonLength || "--"} mins</span>
            {isLocked && <span className="dds2025-chip" title="Locked in place">🔒 Locked</span>}
            <button
              type="button"
              className="dds2025__chip-remove"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRemoveReassignmentStudent(student);
              }}
              aria-label="Remove student from reassignment list"
              title="Remove from reassignment list"
            >
              ×
            </button>
            {instrumentLabels.length ? (
              instrumentLabels.map((instrument, idx) => (
                <span
                  key={`${instrument}-${idx}`}
                  className="dds2025-chip dds2025-chip--neutral"
                >
                  Instrument: {instrument}
                </span>
              ))
            ) : (
              <span className="dds2025-chip dds2025-chip--neutral">Instrument TBD</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (welcomeStep) {
    return (
      <div className="dds2025 dds2025__welcome">
        <div className="dds2025__welcome-card" style={{ position: "fixed", left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}>
          {welcomeStep === "welcome" && (
            <>
              <h1 className="dds2025__welcome-title">Welcome to Atharo Scheduling</h1>
              <p className="dds2025__welcome-subtitle">
                Build a new schedule or pick up where you left off.
              </p>
              <div className="dds2025__welcome-actions">
                <button
                  className="dds2025__nav-btn dds2025__nav-btn--primary"
                  onClick={handleStartNewScheduleClick}
                >
                  Start New Schedule
                </button>
                <button
                  className="dds2025__nav-btn"
                  onClick={() => {
                    if (!selectedSchool && !selectedSchoolId) {
                      setWelcomeStep(null);
                      return;
                    }
                    setShowLoadScheduleModal(true);
                  }}
                >
                  Load Schedule
                </button>
              </div>
            </>
          )}

          {welcomeStep === "new" && (
            <>
              <h1 className="dds2025__welcome-title">Select a school</h1>
              <p className="dds2025__welcome-subtitle">
                Choose which school, then start new project or choose a leaving teacher
              </p>
              <div className="dds2025__welcome-field">
                <label className="dds2025__welcome-label">School</label>
                <select
                  value={selectedSchool}
                  onChange={(e) => handleSchoolSelection(e.target.value)}
                >
                  <option value="">Select a school</option>
                  {SCHOOL_OPTIONS.map((school) => (
                    <option key={school.id} value={school.id}>
                      {school.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="dds2025__welcome-field">
                <label className="dds2025__welcome-label">Leaving Teacher (optional)</label>
                <select
                  value={selectedTeacherId}
                  onChange={(e) => setSelectedTeacherId(e.target.value)}
                  disabled={!teacherOptionsAll.length}
                >
                  <option value="">Choose a teacher studio</option>
                  {teacherOptionsAll.map((teacher) => (
                    <option key={teacher.value} value={teacher.value}>
                      {teacher.label}
                    </option>
                  ))}
                </select>
                {!teacherOptionsAll.length && (
                  <div className="dds2025__welcome-hint">No teachers loaded yet.</div>
                )}
              </div>
              <div className="dds2025__welcome-actions">
                <button className="dds2025__nav-btn" onClick={() => setWelcomeStep("welcome")}>
                  Back
                </button>
                <button
                  className="dds2025__nav-btn"
                  onClick={handleLoadTeacherStudentsAndStart}
                  disabled={!selectedSchool || !selectedTeacherId}
                >
                  Load Teacher's Students
                </button>
                <button
                  className="dds2025__nav-btn dds2025__nav-btn--primary"
                  onClick={handleStartSchedulingWithoutTeacher}
                  disabled={!selectedSchool}
                >
                  Just Start Scheduling
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="dds2025">
      <section className={`dds2025__summary-panel ${showLedger ? "is-open" : ""}`}>
        <div className="dds2025__summary-header">
          <div>
            <div className="dds2025__summary-title">Schedule summary</div>
            <div className="dds2025__summary-stats">
              <span>{plannedPlacements.length} placed</span>
              <span>{studentsNeedingReassignment.length} awaiting reassignment</span>
              <span>
                Drive estimate: {Number.isFinite(optimizationDriveMinutes)
                  ? `${Math.floor(optimizationDriveMinutes / 60)}h ${String(
                      optimizationDriveMinutes % 60
                    ).padStart(2, "0")}m`
                  : "incomplete data"}
              </span>
            </div>
          </div>
          <button
            className="dds2025__summary-toggle"
            type="button"
            onClick={() => setShowLedger((prev) => !prev)}
            aria-expanded={showLedger}
            aria-controls="dds2025-schedule-summary-details"
          >
            {showLedger ? "Hide details" : "View details"}
          </button>
        </div>
        {showLedger && (
          <div id="dds2025-schedule-summary-details" className="dds2025__summary-list">
            {optimizationLedgerEntries.length ? (
              optimizationLedgerEntries.map((entry, idx) => (
                <div key={`${entry.student}-${idx}`} className="dds2025__summary-row">
                  <span className="dds2025__summary-student">{entry.student}</span>
                  <span className="dds2025__summary-teacher">{entry.teacher}</span>
                  <span className="dds2025__summary-time">
                    {entry.day} @ {entry.time}
                  </span>
                </div>
              ))
            ) : (
              <div className="dds2025__simple-sub" style={{ padding: "10px 12px" }}>
                No placements yet.
              </div>
            )}
          </div>
        )}
      </section>
      {showAddStudentModal && (
        <div className="dds2025__modal-backdrop">
          <div className="dds2025__modal dds2025__modal--wide">
            <div className="dds2025__modal-title">Add student to reassignment</div>
            <div className="dds2025__modal-body">
              <input
                type="text"
                value={studentSearchTerm}
                onChange={(e) => setStudentSearchTerm(e.target.value)}
                placeholder={selectedSchool ? "Search students in this school" : "Select a school first"}
                disabled={!selectedSchool}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #d0d5dd",
                  borderRadius: "8px",
                  marginBottom: "12px",
                }}
              />
              <div className="dds2025__simple-list" style={{ maxHeight: 320, overflow: "auto" }}>
                {!selectedSchool && (
                  <div className="dds2025__simple-sub">Select a school first.</div>
                )}
                {selectedSchool && !studentSearchTerm.trim() && (
                  <div className="dds2025__simple-sub">Start typing to search.</div>
                )}
                {selectedSchool && studentSearchTerm.trim() && studentSearchLoading && (
                  <div className="dds2025__simple-sub">Searching…</div>
                )}
                {selectedSchool && studentSearchTerm.trim() && studentSearchError && (
                  <div className="dds2025__simple-sub">{studentSearchError}</div>
                )}
                {selectedSchool &&
                  studentSearchTerm.trim() &&
                  !studentSearchLoading &&
                  !studentSearchError &&
                  (studentSearchResults.length ? (
                    studentSearchResults.map((student) => (
                      <button
                        key={student.id}
                        className="dds2025__simple-row dds2025__simple-row--clickable"
                        onClick={() => addStudentToReassignmentById(student)}
                        style={{ textAlign: "left", width: "100%" }}
                      >
                        <div className="dds2025__simple-name">
                          {student.studentName} {student.familyName ? `(${student.familyName})` : ""}
                        </div>
                        <div className="dds2025__simple-sub">
                          {displayInstrumentLabels(student).join(", ") ||
                            "Instrument TBD"}
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="dds2025__simple-sub">No students found for this search.</div>
                  ))}
              </div>
            </div>
            <div className="dds2025__modal-actions" style={{ justifyContent: "flex-end" }}>
              <button className="dds2025__nav-btn" onClick={() => setShowAddStudentModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showMissingTeacherDriveModal && missingTeacherDrivePairs.length > 0 && (
        <div className="dds2025__modal-backdrop">
          <div className="dds2025__modal dds2025__modal--wide">
            <div className="dds2025__modal-title">
              Missing drive times for {selectedTeacherName || "teacher"}
            </div>
            <div className="dds2025__modal-body">
              <div className="dds2025__simple-sub" style={{ marginBottom: 8 }}>
                These are consecutive lessons without a stored drive time.
              </div>
              <div className="dds2025__simple-list" style={{ maxHeight: 320, overflow: "auto" }}>
                {missingTeacherDrivePairs.map((pair) => (
                  <div key={pair.id} className="dds2025__simple-row">
                    <div className="dds2025__simple-name">
                      {pair.fromLabel} → {pair.toLabel}
                    </div>
                    <div className="dds2025__simple-sub">
                      {pair.day} • {pair.time}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="dds2025__modal-actions" style={{ justifyContent: "flex-end" }}>
              <button
                className="dds2025__nav-btn"
                onClick={() => setShowMissingTeacherDriveModal(false)}
                disabled={calculatingMissingDriveTimes}
              >
                Close
              </button>
              <button
                className="dds2025__nav-btn dds2025__nav-btn--primary"
                onClick={handleCalculateMissingTeacherDriveTimes}
                disabled={calculatingMissingDriveTimes}
              >
                {calculatingMissingDriveTimes ? "Calculating..." : "Calculate Drive Times"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showMissingReassignmentDriveModal && (
        <div className="dds2025__modal-backdrop">
          <div className="dds2025__modal">
            <div className="dds2025__modal-title">
              {missingReassignmentDrivePairs.length} Missing Drive Times
            </div>
            <div className="dds2025__modal-body">
              <div className="dds2025__simple-sub" style={{ marginBottom: 6 }}>
                These complete the directed routes between compatible teachers, students, and calendar stops.
              </div>
              <div className="dds2025__simple-sub">
                Estimated cost: $
                {(missingReassignmentDrivePairs.length * DRIVE_TIME_COST_PER_PAIR).toFixed(2)} (
                {missingReassignmentDrivePairs.length} × ${DRIVE_TIME_COST_PER_PAIR.toFixed(2)})
              </div>
            </div>
            <div className="dds2025__modal-actions" style={{ justifyContent: "flex-end" }}>
              <button
                className="dds2025__nav-btn"
                onClick={() => {
                  pendingOptimizationRef.current = false;
                  setShowMissingReassignmentDriveModal(false);
                  setOptimizationSummary({
                    type: "info",
                    message: "Optimization was canceled; the schedule was not changed.",
                  });
                }}
                disabled={calculatingReassignmentDriveTimes}
              >
                Close
              </button>
              <button
                className="dds2025__nav-btn dds2025__nav-btn--primary"
                onClick={handleCalculateMissingReassignmentDriveTimes}
                disabled={calculatingReassignmentDriveTimes}
              >
                {calculatingReassignmentDriveTimes ? "Calculating..." : "Create Drive Times"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showLoadScheduleModal && (
        <div className="dds2025__modal-backdrop">
          <div className="dds2025__modal dds2025__modal--wide">
            <div className="dds2025__modal-title">Load a saved schedule</div>
            <div className="dds2025__modal-body">
              {!selectedSchool && (
                <div className="dds2025__simple-sub" style={{ marginBottom: 8 }}>
                  Select a school first to view saved schedules.
                </div>
              )}
              {loadingSchedules && <div className="dds2025__simple-sub">Loading schedules…</div>}
              {!loadingSchedules && selectedSchool && (
                <div className="dds2025__simple-list" style={{ maxHeight: 360, overflow: "auto" }}>
                  {savedSchedules.length ? (
                    savedSchedules.map((s) => {
                      const week =
                        s.weekStart ? format(new Date(s.weekStart), "MMM d, yyyy") : "Week not set";
                      return (
                        <div
                          key={s._id || s.id}
                          className="dds2025__simple-row dds2025__simple-row--clickable"
                          onClick={() => handleLoadExistingSchedule(s)}
                          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}
                        >
                          <div>
                            <div className="dds2025__simple-name">{s.label || "Untitled schedule"}</div>
                            <div className="dds2025__simple-sub">
                              {week} • {s.status || "draft"} • {s.placements?.length || 0} placements
                            </div>
                          </div>
                          <button
                            className="dds2025__nav-btn"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSchedule(s._id || s.id);
                            }}
                            style={{ padding: "6px 10px" }}
                            aria-label={`Delete ${s.label || "schedule"}`}
                          >
                            Delete
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <div className="dds2025__simple-sub">No schedules saved for this school.</div>
                  )}
                </div>
              )}
            </div>
            <div className="dds2025__modal-actions">
              <button className="dds2025__nav-btn" onClick={() => setShowLoadScheduleModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showMissingPlaceModal && (
        <div className="dds2025__modal-backdrop">
          <div className="dds2025__modal">
            <div className="dds2025__modal-title">Missing place directories</div>
            <div className="dds2025__modal-body">
              <div className="dds2025__simple-sub" style={{ marginBottom: 8 }}>
                We found missing place directories after loading this schedule.
              </div>
              <div className="dds2025__simple-list">
                <div className="dds2025__simple-row">
                  <div className="dds2025__simple-name">Teachers missing</div>
                  <div className="dds2025__simple-sub">{missingPlaceSummary.missingTeachers.length}</div>
                </div>
                <div className="dds2025__simple-row">
                  <div className="dds2025__simple-name">Calendar event families missing</div>
                  <div className="dds2025__simple-sub">{missingPlaceSummary.missingEventFamilies.size}</div>
                </div>
                <div className="dds2025__simple-row">
                  <div className="dds2025__simple-name">Other calendar locations missing</div>
                  <div className="dds2025__simple-sub">{missingPlaceSummary.missingExternalEvents.length}</div>
                </div>
                <div className="dds2025__simple-row">
                  <div className="dds2025__simple-name">Reassignment students missing</div>
                  <div className="dds2025__simple-sub">{missingPlaceSummary.missingStudents.length}</div>
                </div>
                <div className="dds2025__simple-row">
                  <div className="dds2025__simple-name">Total missing</div>
                  <div className="dds2025__simple-sub">{missingPlaceSummary.totalMissing}</div>
                </div>
              </div>
              <div className="dds2025__simple-sub" style={{ marginTop: 8 }}>
                Only entries with addresses can be created automatically.
              </div>
            </div>
            <div className="dds2025__modal-actions">
              <button
                className="dds2025__nav-btn dds2025__nav-btn--primary"
                onClick={createMissingPlaceDirectories}
                disabled={creatingPlaceDirectories}
              >
                {creatingPlaceDirectories ? "Creating..." : "Create place directories"}
              </button>
              <button
                className="dds2025__nav-btn"
                onClick={() => setShowMissingPlaceModal(false)}
                disabled={creatingPlaceDirectories}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showNoDriveModal && (
        <div className="dds2025__modal-backdrop">
          <div className="dds2025__modal">
            <div className="dds2025__modal-title">No drive time info</div>
            <div className="dds2025__modal-body">
              No drive time info is available for this route.
              <div style={{ marginTop: 8, fontSize: "12px", color: "#475569" }}>
                {noDriveContext?.teacher || "Teacher"} ({noDriveContext?.fromKey || "from"}) →{" "}
                {noDriveContext?.student || "Student"} ({noDriveContext?.toKey || "to"})
              </div>
            </div>
            <div className="dds2025__modal-actions">
              <button
                className="dds2025__nav-btn"
                onClick={() => {
                  setShowNoDriveModal(false);
                  setNoDriveContext(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <section className="dds2025__control-panel" aria-label="Schedule controls">
        <div className="dds2025__control-header">
          <div>
            <div className="dds2025__control-title">Schedule controls</div>
            <div className="dds2025__control-subtitle">
              Choose the scheduling scope, then optimize compatible unlocked students.
            </div>
          </div>
          <button
            type="button"
            className="dds2025__optimize-btn"
            onClick={optimizeSchedule}
            disabled={
              isOptimizing ||
              resolvingManualLocations ||
              !selectedSchool ||
              !calendarEventsLoaded ||
              Boolean(calendarEventsLoadError) ||
              Boolean(placeDirectoryLoadError) ||
              Boolean(driveEdgesLoadError) ||
              !placeDirectoryLoaded ||
              !driveEdgesLoaded ||
              !optimizationTeachers.length ||
              !optimizationStudents.length
            }
            title="Optimizes unlocked students across instrument-compatible active teachers at this school"
          >
            {resolvingManualLocations
              ? "Loading locations…"
              : isOptimizing
              ? "Optimizing…"
              : "Optimize schedule"}
          </button>
        </div>
        <div className="dds2025__filters">
        <div className="dds2025__filter">
          <label>School</label>
          <select
            value={selectedSchool}
            onChange={(e) => handleSchoolSelection(e.target.value)}
  >
            <option value="">Select a school</option>
            {SCHOOL_OPTIONS.map((school) => (
              <option key={school.id} value={school.id}>
                {school.label}
              </option>
            ))}
          </select>
        </div>
        <div className="dds2025__filter">
          <label>Schedule name</label>
          <input
            type="text"
            value={scheduleName}
            onChange={(e) => setScheduleName(e.target.value)}
            onBlur={() => {
              if (currentScheduleId) {
                // Use the latest typed value to avoid stale label in the save call
                saveSchedule(scheduleName);
              }
            }}
            placeholder="Untitled schedule"
          />
        </div>
        <div className="dds2025__filter">
          <label>Select schedule</label>
          <div className="dds2025__schedule-select-row">
            <select
              value={currentScheduleId || ""}
              onChange={(e) => {
                const val = e.target.value;
                if (!val) {
                  if (skipNewScheduleResetRef.current) {
                    skipNewScheduleResetRef.current = false;
                    return;
                  }
                  handleStartNewScheduleClick();
                  return;
                }
                const match = savedSchedules.find((s) => String(s._id || s.id) === String(val));
                if (match) handleLoadExistingSchedule(match);
              }}
              disabled={!savedSchedules.length}
            >
              <option value="">New / unsaved schedule</option>
              {savedSchedules.map((s) => (
                <option key={s._id || s.id} value={s._id || s.id}>
                  {s.label || "Untitled"} – {s.status || "draft"}
                </option>
              ))}
            </select>
            <button
              className="dds2025__nav-btn"
              type="button"
              onClick={() => handleDeleteSchedule(currentScheduleId)}
              disabled={!currentScheduleId}
            >
              Delete schedule
            </button>
          </div>
        </div>
        <div className="dds2025__filter">
          <label>Compatible calendar teacher</label>
          <select
            value={selectedTeacherId}
            onChange={(e) => setSelectedTeacherId(e.target.value)}
            disabled={!teacherOptions.length}
          >
            <option value="">Select a teacher</option>
            {teacherOptions.map((teacher) => (
              <option key={teacher.value} value={teacher.value}>
                {teacher.label}
              </option>
            ))}
          </select>
          {studentsDefiningTeacherScope.length > 0 && !teacherOptions.length && (
            <div className="dds2025__status" style={{ color: "#b91c1c" }}>
              No active teacher at this school teaches the instruments currently being scheduled.
            </div>
          )}
        </div>
        <div className="dds2025__filter">
          <label>Max drive per leg (min)</label>
          <input
            type="number"
            min="0"
            step="5"
            value={maxDriveMinutes}
            onChange={(event) => {
              invalidateOptimizationResult();
              setMaxDriveMinutes(event.target.value);
            }}
            placeholder="No limit"
          />
        </div>
        <div className="dds2025__filter">
          <label>Travel padding (min)</label>
          <input
            type="number"
            min="0"
            step="5"
            value={paddingMinutes}
            onChange={(event) => {
              invalidateOptimizationResult();
              setPaddingMinutes(event.target.value);
            }}
            placeholder="0"
          />
        </div>
        {loading && <div className="dds2025__status">Loading data…</div>}
        {resolvingManualLocations && (
          <div className="dds2025__status">Loading family locations…</div>
        )}
        {calendarEventsLoadError && (
          <div className="dds2025__status" style={{ color: "#b91c1c" }}>
            {calendarEventsLoadError}{" "}
            <button
              type="button"
              className="dds2025__nav-btn"
              onClick={() => {
                invalidateOptimizationResult();
                setCalendarReloadToken((value) => value + 1);
              }}
            >
              Retry
            </button>
          </div>
        )}
        {placeDirectoryLoadError && (
          <div className="dds2025__status" style={{ color: "#b91c1c" }}>
            {placeDirectoryLoadError}{" "}
            <button
              type="button"
              className="dds2025__nav-btn"
              onClick={() => {
                invalidateOptimizationResult();
                setPlaceDirectoryReloadToken((value) => value + 1);
              }}
            >
              Retry
            </button>
          </div>
        )}
        {driveEdgesLoadError && (
          <div className="dds2025__status" style={{ color: "#b91c1c" }}>
            {driveEdgesLoadError}{" "}
            <button
              type="button"
              className="dds2025__nav-btn"
              onClick={() => {
                invalidateOptimizationResult();
                setDriveEdgesReloadToken((value) => value + 1);
              }}
            >
              Retry
            </button>
          </div>
        )}
        </div>
      </section>

      {optimizationSummary && (
        <div
          role={optimizationSummary.type === "error" ? "alert" : "status"}
          className={`dds2025__optimization-result dds2025__optimization-result--${optimizationSummary.type || "info"}`}
        >
          <div>
            <strong>{optimizationSummary.message}</strong>
            {Number.isFinite(optimizationSummary.beforeDrive) &&
              Number.isFinite(optimizationSummary.afterDrive) && (
                <span className="dds2025__optimization-metrics">
                  Route drive: {Math.round(optimizationSummary.beforeDrive)}m →{" "}
                  {Math.round(optimizationSummary.afterDrive)}m
                </span>
              )}
            {optimizationSummary.weekLabel && (
              <span className="dds2025__optimization-metrics">
                Scheduling week: {optimizationSummary.weekLabel}
              </span>
            )}
            {optimizationSummary.unassigned?.length > 0 && (
              <details open={optimizationSummary.scheduled === 0}>
                <summary>{optimizationSummary.unassigned.length} unassigned student(s)</summary>
                <ul>
                  {optimizationSummary.unassigned.map((entry) => {
                    const student =
                      optimizationStudents.find(
                        (candidate) =>
                          String(candidate.key) === String(entry.studentKey)
                      ) || entry;
                    const instrumentLabel =
                      displayInstrumentLabels(student).join(", ") ||
                      "Instrument TBD";
                    return (
                      <li key={entry.studentKey}>
                        {student.studentName || student.familyName || entry.studentKey} — {instrumentLabel}: {entry.reason}
                      </li>
                    );
                  })}
                </ul>
              </details>
            )}
            {optimizationSummary.warnings?.length > 0 && (
              <details>
                <summary>
                  {optimizationSummary.warnings.length} existing calendar route notice
                  {optimizationSummary.warnings.length === 1 ? "" : "s"}
                </summary>
                <ul>
                  {optimizationSummary.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
          {optimizationUndo && (
            <button type="button" className="dds2025__nav-btn" onClick={undoOptimization}>
              Undo optimization
            </button>
          )}
        </div>
      )}

      <div className="dds2025__grid">
        <div className="dds2025__students">
          <div className="dds2025__column-title">
            Students needing reassignment ({studentsNeedingReassignment.length})
            <button
              className="dds2025__icon-btn"
              onClick={() => {
                setStudentSearchTerm("");
                setShowAddStudentModal(true);
              }}
              disabled={!selectedSchool}
              aria-label="Add student to reassignment"
            >
              +
            </button>
          </div>
          <div
            className="dds2025__student-list"
            ref={studentListRef}
            onDragOver={onDragOverStudentColumn}
            onDragLeave={onDragLeaveStudentColumn}
            onDrop={onDropOnStudentColumn}
            style={{ minHeight: 220 }}
          >
            {studentsNeedingReassignment.map((student, idx) => (
              <React.Fragment key={student.id || student.studentId || idx}>
                {hoverInsertIndex === idx &&
                  (draggingStudent || draggingPlacement) &&
                  isHoveringStudentColumn && (
                    <div className="dds2025__drop-ghost">Release to return student</div>
                  )}
                {renderStudentCard(student)}
              </React.Fragment>
            ))}
            {hoverInsertIndex === studentsNeedingReassignment.length &&
              (draggingStudent || draggingPlacement) &&
              isHoveringStudentColumn && (
                <div className="dds2025__drop-ghost">Release to return student</div>
              )}
            {!studentsNeedingReassignment.length && (
              <div className="dds2025__empty">No students to reassign for this school.</div>
            )}
          </div>
        </div>

        <div className="dds2025__calendar">
          <div className="dds2025__column-title">
            <div className="dds2025__calendar-header">
              <div className="dds2025__calendar-week">
                {displayedWeekLabel}
              </div>
              <div className="dds2025__calendar-nav">
                <button className="dds2025__nav-btn" onClick={handleResetReassignmentPlacements}>
                  Reset
                </button>
                <button
                  className="dds2025__nav-btn"
                  onClick={() => {
                    invalidateOptimizationResult();
                    setWeekOffset((prev) => prev - 1);
                  }}
                >
                  &lt;
                </button>
                <button
                  className="dds2025__nav-btn"
                  onClick={() => {
                    invalidateOptimizationResult();
                    setWeekOffset((prev) => prev + 1);
                  }}
                >
                  &gt;
                </button>
                <input
                  type="date"
                  value={format(displayedWeek[0], "yyyy-MM-dd")}
                  onChange={(event) =>
                    handleDisplayedWeekChange(event.target.value)
                  }
                  aria-label="Scheduling week"
                  title="Choose the scheduling week"
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    padding: "5px 8px",
                    fontSize: 12,
                  }}
                />
                <button
                  className="dds2025__nav-btn"
                  type="button"
                  onClick={() => handleDisplayedWeekChange(format(new Date(), "yyyy-MM-dd"))}
                  disabled={weekOffset === 0}
                >
                  This week
                </button>
              </div>
            </div>
            {selectedTeacherId && (
              <div className="dds2025__teacher-meta">
                {selectedTeacherName && (
                  <span className="dds2025__teacher-name">Teacher: {selectedTeacherName}</span>
                )}
                {typeof totalDriveMinutes === "number" && (
                  <span className="dds2025-chip dds2025-chip--neutral">
                    Drive time: {Math.floor(totalDriveMinutes / 60)}h {totalDriveMinutes % 60}m
                  </span>
                )}
                {typeof totalAvailabilityMinutes === "number" && typeof totalTeachingMinutes === "number" && totalAvailabilityMinutes > 0 && (
                  <span className="dds2025-chip dds2025-chip--neutral">
                    TAUI: {Math.round((totalTeachingMinutes / totalAvailabilityMinutes) * 100)}% ({Math.floor(totalTeachingMinutes / 60)}h {totalTeachingMinutes % 60}m of {Math.floor(totalAvailabilityMinutes / 60)}h {totalAvailabilityMinutes % 60}m)
                  </span>
                )}
              </div>
            )}
            {selectedTeacherId && selectedTeacherInstruments.length > 0 && (
              <div className="dds2025__teacher-instruments">
                <span className="dds2025__teacher-instruments-label">Instruments:</span>
                {selectedTeacherInstruments.map((inst) => (
                  <span key={inst} className="dds2025-chip dds2025-chip--neutral">
                    {inst}
                  </span>
                ))}
              </div>
            )}
            {/* <div className="dds2025__drive-cta">
              <span className="dds2025__drive-text">
                Drive pairs missing: {missingDrivePairs.length}
              </span>
            </div> */}
            {!selectedTeacherId && <div>Choose a teacher to view their week</div>}
          </div>
          <div className="dds2025__week-grid" ref={calendarRef}>
            <div className="dds2025__time-col" style={{ height: timeWindow.totalHeightPx }}>
              {Array.from({ length: timeWindow.endHour - timeWindow.startHour + 1 }).map((_, i) => {
                const hour = timeWindow.startHour + i;
                return (
                  <div key={hour} className="dds2025__time-marker" style={{ top: (((hour * 60) - timeWindow.startMinutes) / SLOT_MINUTES) * PIXELS_PER_SLOT }}>
                    {format(new Date().setHours(hour, 0, 0, 0), "h a")}
                  </div>
        );
      })}
            </div>
            {weekDays.map((day, dayIndex) => (
              <div key={day} className="dds2025__day-grid">
                <div className="dds2025__day-header">
                  <span>{day.slice(0, 3)}</span>
                  <span className="dds2025__day-date">
                    {displayedWeek[dayIndex] ? format(displayedWeek[dayIndex], "M/d") : ""}
                  </span>
                </div>
                <div
                  className="dds2025__day-body"
                  ref={(el) => (dayRefs.current[day] = el)}
                  style={{ height: timeWindow.totalHeightPx }}
                  onDragOver={(e) => onDragOverDayArea(day, e)}
                  onDrop={(e) => onDropOnDayArea(day, e)}
                  onDragLeave={() => {
                    lastHoverRef.current = { day: null, label: null };
                    setHoverIndicator(null);
                  }}
                >
      {(driveSegmentsWithWarn[day] || []).map((seg, i) => (
        <div
          key={`drive-${day}-${i}`}
          className={`dds2025__drive-chip ${seg.warn ? "dds2025__drive-chip--warn" : ""}`}
          style={{ top: seg.top }}
          title={Number.isFinite(seg.minutes) ? `Drive ${seg.minutes}m` : "Drive time unavailable"}
        >
          Drive {Number.isFinite(seg.minutes) ? `${seg.minutes}m` : "--"}
        </div>
      ))}
                  {getUnavailableSegments(day).map((seg, idx) => (
                    <div
                      key={`unavail-${day}-${idx}`}
                      className="dds2025__unavailable"
                      style={{ top: seg.top, height: seg.height }}
                    />
                  ))}
                  {hoverIndicator?.day === day && !draggingStudent && !draggingPlacement && (
                    <div className="dds2025__hover-badge">{hoverIndicator.label}</div>
                  )}
                  <div className="dds2025__grid-lines">
                    {Array.from({ length: Math.ceil(timeWindow.totalMinutes / SLOT_MINUTES) }).map((_, idx) => {
                      const minute = idx * SLOT_MINUTES;
                      const top = (minute / SLOT_MINUTES) * PIXELS_PER_SLOT;
                      const isHalfHour = minute % 30 === 0;
                      const isHour = minute % 60 === 0;
                      return (
                        <div
                          key={idx}
                          className={`dds2025__grid-line ${isHour ? "dds2025__grid-line--hour" : isHalfHour ? "dds2025__grid-line--half" : ""}`}
                          style={{ top }}
                        />
                      );
                    })}
                  </div>
                  {(positionedEventsByDay[day] || []).map((event) => {
                    const eventKey = event.placementKey || event.id;
                    const sid = getStudentKey(event);
                    const isLockedPlacement = event.planned && lockedStudentIds.includes(sid);
                    const startEventHold = () => {
                      if (!event.planned) return;
                      if (eventHoldTimerRef.current) clearTimeout(eventHoldTimerRef.current);
                      eventHoldTimerRef.current = setTimeout(() => {
                        invalidateOptimizationResult();
                        setLockedStudentIds((prev) =>
                          prev.includes(sid) ? prev.filter((id) => id !== sid) : [...prev, sid]
                        );
                        eventHoldTimerRef.current = null;
                      }, LONG_PRESS_MS);
                    };
                    const cancelEventHold = () => {
                      if (eventHoldTimerRef.current) {
                        clearTimeout(eventHoldTimerRef.current);
                        eventHoldTimerRef.current = null;
                      }
                    };
                    const isDraggingThis =
                      draggingPlacement && (draggingPlacement.placementKey || draggingPlacement.id) === eventKey;
                    const displayTime =
                      isDraggingThis && hoverIndicator?.day === day ? hoverIndicator.label : event.timeLabel;
                    return (
                      <div
                        key={event.id || `${event.studentId}-${event.timeLabel}`}
                        className={`dds2025__event-block ${event.planned ? "dds2025__event-block--planned" : ""}`}
                        style={{
                          top: event.top,
                          height: event.height,
                          borderColor: isLockedPlacement ? "#f97316" : undefined,
                          background: isLockedPlacement
                            ? "#fff7ed"
                            : event.planned
                            ? "#dcfce7"
                            : "#e0f2fe",
                        }}
                        data-tooltip={isDraggingThis ? "" : event.tooltip}
                        draggable={event.planned}
                        onDragStart={(e) => {
                          cancelEventHold();
                          onDragStartPlacement(event, e);
                        }}
        onDragEnd={() => {
          setDraggingPlacement(null);
          setDragPreview(null);
        }}
                        onClick={() => onEventClick(event)}
                        onMouseDown={startEventHold}
                        onMouseUp={cancelEventHold}
                        onMouseLeave={cancelEventHold}
                        onTouchStart={startEventHold}
                        onTouchEnd={cancelEventHold}
                      >
                        <div className="dds2025__event-row">
                          <div className="dds2025__event-time">
                            {displayTime} {isLockedPlacement ? "🔒" : ""}
                          </div>
                          <div className="dds2025__event-name">{event.label}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        .dds2025 { display: flex; flex-direction: column; gap: 16px; width: 100%; min-width: 0; padding: 0 8px 32px; box-sizing: border-box; color: #0f172a; }
        .dds2025__drag-preview { position: fixed; left: 0; top: 0; transform: translate(-50%, -50%); pointer-events: none; z-index: 5000; background: #dcfce7; color: #0f172a; padding: 6px 10px; border-radius: 999px; font-size: 12px; box-shadow: 0 8px 20px rgba(15,23,42,0.2); opacity: 0.95; border: 1px solid #a7f3d0; }
        .dds2025__control-panel { overflow: hidden; border: 1px solid #dbe4f0; border-radius: 14px; background: #ffffff; box-shadow: 0 6px 18px rgba(15,23,42,0.06); }
        .dds2025__control-header { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 16px 18px; border-bottom: 1px solid #e2e8f0; background: linear-gradient(135deg, #f8fafc 0%, #ffffff 70%); }
        .dds2025__control-title { font-size: 16px; line-height: 1.25; font-weight: 800; color: #0f172a; }
        .dds2025__control-subtitle { margin-top: 3px; font-size: 12px; color: #64748b; }
        .dds2025__optimize-btn { flex: 0 0 auto; min-height: 42px; padding: 10px 18px; border: 1px solid #1d4ed8; border-radius: 9px; background: #2563eb; color: #ffffff; font-size: 14px; font-weight: 750; letter-spacing: 0.01em; cursor: pointer; box-shadow: 0 6px 12px rgba(37,99,235,0.2); transition: background 120ms ease, box-shadow 120ms ease, transform 120ms ease; }
        .dds2025__optimize-btn:hover:not(:disabled) { background: #1d4ed8; box-shadow: 0 8px 16px rgba(37,99,235,0.26); transform: translateY(-1px); }
        .dds2025__optimize-btn:disabled { border-color: #cbd5e1; background: #e2e8f0; color: #94a3b8; cursor: not-allowed; box-shadow: none; }
        .dds2025__filters { display: grid; grid-template-columns: repeat(auto-fit, minmax(165px, 1fr)); gap: 14px; align-items: end; padding: 16px 18px 18px; }
        .dds2025__filter { display: flex; min-width: 0; flex-direction: column; gap: 6px; font-size: 13px; }
        .dds2025__filter label { color: #475569; font-size: 11px; font-weight: 750; letter-spacing: 0.03em; text-transform: uppercase; }
        .dds2025__filter select, .dds2025__filter input { width: 100%; min-width: 0; height: 38px; padding: 7px 9px; box-sizing: border-box; border: 1px solid #cbd5e1; border-radius: 8px; background: #ffffff; color: #0f172a; }
        .dds2025__filter select:focus, .dds2025__filter input:focus { outline: 2px solid rgba(37,99,235,0.18); border-color: #2563eb; }
        .dds2025__schedule-select-row { display: flex; align-items: center; gap: 8px; min-width: 0; }
        .dds2025__schedule-select-row select { flex: 1 1 auto; }
        .dds2025__schedule-select-row .dds2025__nav-btn { flex: 0 0 auto; height: 38px; white-space: nowrap; }
        .dds2025__filters > .dds2025__status { grid-column: 1 / -1; }
        .dds2025__optimization-result { margin: 0; padding: 12px 14px; display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; border: 1px solid #bfdbfe; border-radius: 10px; background: #eff6ff; color: #1e3a8a; font-size: 13px; }
        .dds2025__optimization-result--success { border-color: #86efac; background: #f0fdf4; color: #166534; }
        .dds2025__optimization-result--warning { border-color: #fde68a; background: #fffbeb; color: #92400e; }
        .dds2025__optimization-result--error { border-color: #fca5a5; background: #fef2f2; color: #991b1b; }
        .dds2025__optimization-metrics { display: block; margin-top: 4px; font-weight: 500; }
        .dds2025__optimization-result details { margin-top: 6px; }
        .dds2025__optimization-result ul { margin: 6px 0 0; padding-left: 18px; }
        .dds2025__status { font-size: 12px; color: #6b7280; }
        .dds2025__grid { display: grid; grid-template-columns: minmax(235px, 280px) minmax(0, 1fr); gap: 16px; align-items: start; min-width: 0; }
        .dds2025__column-title { font-weight: 800; margin-bottom: 8px; color: #0f172a; }
        .dds2025__students { position: sticky; top: 12px; max-height: calc(100vh - 24px); overflow: hidden; border: 1px solid #dbe4f0; border-radius: 12px; padding: 12px; background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%); box-shadow: 0 4px 12px rgba(15,23,42,0.06); }
        .dds2025__student-list { display: flex; flex-direction: column; gap: 10px; max-height: calc(100vh - 165px); overflow: auto; padding: 6px 2px; }
        .dds2025-student-card, .dds2025__student-card { border: 1px solid #dbe4f0; border-radius: 10px; padding: 12px; background: #ffffff; cursor: grab; box-shadow: 0 3px 8px rgba(15,23,42,0.05); transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease; }
        .dds2025-student-card:active, .dds2025__student-card:active { cursor: grabbing; }
        .dds2025-student-card:hover, .dds2025__student-card:hover { transform: translateY(-1px); border-color: #93c5fd; box-shadow: 0 7px 16px rgba(15,23,42,0.1); }
        .dds2025-student-header { display: flex; flex-direction: column; align-items: stretch; gap: 8px; }
        .dds2025-student-name-row { display: grid; grid-template-columns: 24px 1fr; align-items: center; gap: 6px; }
        .dds2025-drag-hint { font-size: 14px; color: #94a3b8; }
        .dds2025-chip { background: #eef2ff; color: #4338ca; padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 700; }
        .dds2025-chip--neutral { background: #f1f5f9; color: #475569; }
        .dds2025__student-name { font-weight: 800; font-size: 13px; }
        .dds2025__student-family { font-size: 10px; color: #6b7280; }
        .dds2025-student-name { font-weight: 800; font-size: 13px; }
        .dds2025-student-chips { display: flex; flex-wrap: wrap; gap: 6px; justify-content: flex-start; }
        .dds2025__calendar { min-width: 0; border: 1px solid #dbe4f0; border-radius: 12px; padding: 12px; background: #fff; overflow-x: hidden; box-shadow: 0 4px 12px rgba(15,23,42,0.05); }
        .dds2025__calendar-header { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 10px; }
        .dds2025__calendar-week { font-weight: 700; }
        .dds2025__calendar-nav { display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }
        .dds2025__nav-btn { border: 1px solid #cbd5e1; background: #fff; padding: 6px 9px; border-radius: 7px; color: #334155; font-weight: 600; cursor: pointer; }
        .dds2025__nav-btn:hover:not(:disabled) { border-color: #94a3b8; background: #f8fafc; }
        .dds2025__nav-btn:disabled { color: #94a3b8; cursor: not-allowed; }
        .dds2025__teacher-meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin: 6px 0; font-size: 12px; color: #334155; }
        .dds2025__teacher-name { font-weight: 700; }
        .dds2025__teacher-instruments { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin: 6px 0; }
        .dds2025__teacher-instruments-label { font-size: 12px; color: #475569; font-weight: 700; }
        .dds2025__drive-cta { display: flex; align-items: center; gap: 8px; margin-top: 6px; font-size: 12px; color: #475569; }
        .dds2025__drive-text { font-size: 12px; }
        .dds2025__week-grid { display: grid; grid-template-columns: 50px repeat(7, minmax(0, 1fr)); gap: 4px; align-items: start; width: 100%; min-width: 0; }
        .dds2025__time-col { position: relative; border-right: 1px solid #e2e8f0; }
        .dds2025__time-marker { position: absolute; left: 2px; font-size: 10px; color: #94a3b8; transform: translateY(14px); }
        .dds2025__day-grid { min-width: 0; border: 1px solid #e2e8f0; border-radius: 7px; overflow: hidden; background: #f8fafc; }
        .dds2025__day-header { display: flex; align-items: baseline; justify-content: center; gap: 4px; min-width: 0; background: #eef2ff; padding: 7px 3px; color: #312e81; font-size: 11px; font-weight: 800; position: sticky; top: 0; z-index: 2; }
        .dds2025__day-date { color: #6366f1; font-size: 9px; font-weight: 700; }
        .dds2025__day-body { position: relative; overflow-y: auto; background: #fff; }
        .dds2025__grid-lines { position: absolute; inset: 0; }
        .dds2025__grid-line { position: absolute; left: 0; right: 0; height: 1px; background: #f1f5f9; }
        .dds2025__grid-line--half { background: #e2e8f0; }
        .dds2025__grid-line--hour { background: #cbd5e1; }
        .dds2025__unavailable { position: absolute; left: 0; right: 0; background: rgba(148, 163, 184, 0.25); pointer-events: none; }
        .dds2025__drive-chip { position: absolute; left: 50%; transform: translate(-50%, -50%); background: #ecfdf3; color: #166534; border: 1px solid #86efac; border-radius: 999px; padding: 2px 8px; font-size: 10px; box-shadow: 0 2px 6px rgba(15,23,42,0.12); pointer-events: none; white-space: nowrap; z-index: 4; }
        .dds2025__drive-chip--warn { background: #fef2f2; color: #991b1b; border-color: #fca5a5; }
        .dds2025__event-block { position: absolute; left: 3px; right: 3px; min-width: 0; padding: 4px; border-radius: 5px; border: 1px solid #e2e8f0; background: #e0f2fe; font-size: 11px; box-shadow: 0 4px 10px rgba(15,23,42,0.06); box-sizing: border-box; overflow: hidden; }
        .dds2025__event-block--planned { background: #dcfce7; border-color: #a7f3d0; }
        .dds2025__event-row { display: flex; align-items: center; gap: 3px; min-width: 0; }
        .dds2025__event-time { flex: 0 0 auto; font-weight: 700; font-size: 9px; white-space: nowrap; }
        .dds2025__event-name { min-width: 0; font-size: 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .dds2025__event-block::after { content: attr(data-tooltip); position: absolute; left: 0; top: -4px; transform: translateY(-100%); background: #0f172a; color: #fff; padding: 6px 8px; border-radius: 8px; box-shadow: 0 8px 16px rgba(15,23,42,0.24); font-size: 11px; white-space: pre-line; opacity: 0; pointer-events: none; transition: opacity 80ms ease, transform 80ms ease; z-index: 5; }
        .dds2025__event-block:hover::after { opacity: 1; transform: translateY(-110%); }
        .dds2025__hover-badge { position: absolute; top: 8px; right: 8px; padding: 4px 8px; font-size: 11px; font-weight: 700; color: #0f172a; background: rgba(255,255,255,0.95); border: 1px solid #cbd5e1; border-radius: 8px; z-index: 3; box-shadow: 0 4px 10px rgba(15,23,42,0.08); pointer-events: none; }
        .dds2025__empty { font-size: 12px; color: #9ca3af; padding: 12px 0; }
        .dds2025__modal-backdrop { position: fixed; inset: 0; background: rgba(15,23,42,0.35); display: flex; align-items: center; justify-content: center; z-index: 3000; }
        .dds2025__modal { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px; max-width: 360px; width: 90%; box-shadow: 0 10px 30px rgba(15,23,42,0.25); }
        .dds2025__modal--wide { max-width: 640px; }
        .dds2025__modal--event { max-width: 1100px; }
        .dds2025__modal-title { font-weight: 700; margin-bottom: 8px; }
        .dds2025__modal-body { font-size: 14px; color: #475569; margin-bottom: 12px; }
        .dds2025__modal-actions { display: flex; justify-content: flex-end; gap: 8px; }
        .dds2025__event-modal-system { display: flex; flex-direction: row; align-items: center; justify-content: center; gap: 10px; width: 90%; height: 90%; }
        .dds2025__event-modal-x { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: flex-start; height: 95%; }
        .dds2025__event-modal-left { flex: 10; background: #fff; border-radius: 10px; height: 95%; padding: 16px; display: flex; flex-direction: column; gap: 12px; overflow: auto; }
        .dds2025__event-modal-center { flex: 20; background: #fff; border-radius: 10px; height: 95%; padding: 16px; display: flex; flex-direction: column; gap: 10px; }
        .dds2025__event-modal-right { flex: 10; background: #1e293b; border-radius: 10px; height: 95%; overflow: hidden; }
        .dds2025__event-modal-student { font-size: 18px; font-weight: 800; color: #0f172a; }
        .dds2025__event-modal-map { width: 100%; flex: 1; min-height: 360px; border-radius: 10px; overflow: hidden; border: 1px solid #e2e8f0; }
        .dds2025__event-modal-edges { display: flex; flex-direction: column; gap: 6px; flex: 1; min-height: 0; }
        .dds2025__event-modal-edges .dds2025__simple-list { flex: 1; min-height: 0; max-height: none; overflow: auto; }
        .dds2025__map-pin { background-color: #475569; width: 30px; height: 30px; display: flex; justify-content: center; align-items: center; border-radius: 5px; font-size: 14px; z-index: 1; }
        .dds2025__map-marker { position: relative; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 10px 12px; box-shadow: 0 16px 30px rgba(15,23,42,0.12); min-width: 210px; max-width: 260px; }
        .dds2025__map-marker__name { font-size: 14px; font-weight: 700; color: #0f172a; }
        .dds2025__map-marker__address { font-size: 11px; color: #64748b; margin-top: 2px; }
        .dds2025__map-marker__icon-row { display: flex; flex-direction: row; justify-content: flex-start; align-items: center; gap: 6px; margin-top: 8px; flex-wrap: wrap; }
        .dds2025__map-marker__students { display: inline-flex; gap: 6px; align-items: center; background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 999px; padding: 2px 8px; font-size: 11px; color: #0f172a; }
        .dds2025__map-marker__instruments { font-size: 10px; display: flex; gap: 6px; flex-wrap: wrap; }
        .dds2025__map-marker__inst { display: inline-flex; align-items: center; justify-content: center; border: 1px solid #e2e8f0; border-radius: 999px; padding: 2px 8px; height: 20px; color: #0f172a; background: #f8fafc; }
        .dds2025__map-marker__tip { position: absolute; bottom: -7px; left: 24px; rotate: 45deg; background-color: #ffffff; border-right: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; width: 12px; height: 12px; }
        .dds2025__section { margin-bottom: 14px; }
        .dds2025__section-title { font-weight: 700; margin-bottom: 6px; }
        .dds2025__simple-list { display: flex; flex-direction: column; gap: 6px; max-height: 320px; overflow: auto; }
        .dds2025__simple-row { border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 10px; background: #f8fafc; }
        .dds2025__simple-row--clickable { cursor: pointer; }
        .dds2025__simple-row--clickable:hover { border-color: #cbd5e1; background: #e2e8f0; }
        .dds2025__simple-name { font-weight: 700; color: #0f172a; }
        .dds2025__simple-sub { font-size: 12px; color: #475569; }
        .dds2025__missing-list { display: flex; flex-direction: column; gap: 8px; max-height: 440px; overflow: auto; }
        .dds2025__missing-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; border: 1px solid #e2e8f0; background: #f8fafc; border-radius: 8px; padding: 8px 10px; }
        .dds2025__missing-name { font-weight: 700; color: #0f172a; }
        .dds2025__missing-address { font-size: 12px; color: #475569; }
        .dds2025__tag { display: inline-flex; align-items: center; gap: 6px; margin-bottom: 2px; font-size: 11px; color: #334155; }
        .dds2025__badge { display: inline-flex; align-items: center; padding: 2px 6px; margin-left: 8px; border-radius: 999px; background: #ecfdf3; color: #166534; font-size: 10px; font-weight: 700; border: 1px solid #86efac; }
        .dds2025__summary-panel { border: 1px solid #dbe4f0; border-radius: 12px; background: #f8fafc; overflow: hidden; }
        .dds2025__summary-header { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 12px 16px; }
        .dds2025__summary-title { font-size: 13px; font-weight: 800; color: #0f172a; }
        .dds2025__summary-stats { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 14px; margin-top: 4px; color: #64748b; font-size: 11px; }
        .dds2025__summary-stats span { position: relative; }
        .dds2025__summary-stats span + span::before { content: ""; position: absolute; left: -8px; top: 50%; width: 3px; height: 3px; border-radius: 50%; background: #94a3b8; transform: translateY(-50%); }
        .dds2025__summary-toggle { flex: 0 0 auto; border: 1px solid #cbd5e1; border-radius: 7px; padding: 6px 10px; background: #ffffff; color: #334155; font-size: 11px; font-weight: 700; cursor: pointer; }
        .dds2025__summary-toggle:hover { border-color: #94a3b8; background: #f1f5f9; }
        .dds2025__summary-list { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 0 16px; max-height: 260px; overflow: auto; padding: 4px 16px 12px; border-top: 1px solid #e2e8f0; background: #ffffff; }
        .dds2025__summary-row { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto; align-items: center; gap: 8px; min-width: 0; padding: 8px 0; border-bottom: 1px solid #eef2f7; font-size: 11px; }
        .dds2025__summary-student { overflow: hidden; color: #0f172a; font-weight: 750; text-overflow: ellipsis; white-space: nowrap; }
        .dds2025__summary-teacher { overflow: hidden; color: #64748b; text-overflow: ellipsis; white-space: nowrap; }
        .dds2025__summary-time { color: #334155; font-weight: 650; white-space: nowrap; }
        .dds2025__drop-ghost { border: 2px dashed #0ea5e9; color: #0ea5e9; padding: 10px; margin-bottom: 10px; border-radius: 8px; text-align: center; background: #e0f2fe; font-weight: 600; }
        .dds2025.dds2025__welcome { position: fixed; inset: 0; width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #f6f7f9; padding: 32px; color: #111827; margin: 0; }
        .dds2025__welcome-card { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 16px; padding: 40px 36px; width: min(640px, 92vw); margin: 0 auto; box-shadow: 0 20px 60px rgba(15,23,42,0.12); text-align: center; }
        .dds2025__welcome-title { margin: 0 0 10px 0; font-size: 28px; font-weight: 700; color: #0f172a; letter-spacing: -0.02em; }
        .dds2025__welcome-subtitle { margin: 0 auto 24px; color: #6b7280; font-size: 15px; max-width: 480px; }
        .dds2025__welcome-actions { display: flex; gap: 14px; justify-content: center; align-items: center; flex-wrap: wrap; margin-top: 8px; }
        .dds2025__welcome .dds2025__nav-btn { padding: 11px 18px; border-radius: 999px; font-weight: 600; letter-spacing: 0.2px; transition: transform 120ms ease, box-shadow 120ms ease, background 120ms ease, border 120ms ease, color 120ms ease; }
        .dds2025__welcome .dds2025__nav-btn--primary { background: #111827; color: #ffffff; border: 1px solid #111827; box-shadow: 0 12px 22px rgba(17,24,39,0.2); }
        .dds2025__welcome .dds2025__nav-btn--primary:hover { background: #0b1220; transform: translateY(-1px); box-shadow: 0 16px 28px rgba(17,24,39,0.24); }
        .dds2025__welcome .dds2025__nav-btn--primary:active { transform: translateY(0); box-shadow: 0 10px 18px rgba(17,24,39,0.18); }
        .dds2025__welcome .dds2025__nav-btn:not(.dds2025__nav-btn--primary) { background: #ffffff; color: #111827; border: 1px solid #d1d5db; box-shadow: 0 8px 18px rgba(15,23,42,0.08); }
        .dds2025__welcome .dds2025__nav-btn:not(.dds2025__nav-btn--primary):hover { background: #f8fafc; transform: translateY(-1px); box-shadow: 0 12px 22px rgba(15,23,42,0.12); }
        .dds2025__welcome .dds2025__nav-btn:not(.dds2025__nav-btn--primary):active { transform: translateY(0); box-shadow: 0 6px 14px rgba(15,23,42,0.08); }
        .dds2025__welcome-field { display: flex; flex-direction: column; gap: 8px; margin: 16px 0; text-align: center; }
        .dds2025__welcome-label { font-weight: 600; color: #111827; font-size: 13px; }
        .dds2025__welcome-field select { padding: 12px 14px; border: 1px solid #d1d5db; border-radius: 10px; background: #ffffff; color: #111827; text-align: center; }
        .dds2025__welcome-hint { font-size: 12px; color: #6b7280; margin-top: 6px; }
        .dds2025__icon-btn { margin-left: 8px; border: 1px solid #d0d5dd; background: #f9fafb; color: #0f172a; border-radius: 50%; width: 28px; height: 28px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
        .dds2025__icon-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .dds2025__icon-btn:hover:not(:disabled) { background: #edf2f7; }
        .dds2025__chip-remove { margin-left: auto; border: 1px solid #e2e8f0; background: #fff; color: #ef4444; width: 22px; height: 22px; border-radius: 999px; display: inline-flex; align-items: center; justify-content: center; font-size: 14px; cursor: pointer; }
        .dds2025__chip-remove:hover { background: #fee2e2; }
        .dds2025__remove-calendar-btn { margin-top: 8px; align-self: center; background: transparent; border: none; color: #ef4444; border: 1px solid #ef4444; padding: 10px 20px; font-weight: 600; cursor: pointer; border-radius: 5px;}
        .dds2025__remove-calendar-btn:hover { color: #dc2626; text-decoration: underline; }
        @media (max-width: 1180px) {
          .dds2025__grid { grid-template-columns: minmax(0, 1fr); }
          .dds2025__students { position: static; max-height: none; }
          .dds2025__student-list { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); max-height: 300px; }
        }
        @media (max-width: 720px) {
          .dds2025 { padding-inline: 0; }
          .dds2025__control-header { align-items: stretch; flex-direction: column; }
          .dds2025__optimize-btn { width: 100%; }
          .dds2025__filters { grid-template-columns: minmax(0, 1fr); }
          .dds2025__summary-header { align-items: flex-start; }
          .dds2025__summary-stats { align-items: flex-start; flex-direction: column; gap: 3px; }
          .dds2025__summary-stats span + span::before { display: none; }
          .dds2025__calendar { overflow-x: auto; }
          .dds2025__week-grid { min-width: 760px; }
        }
      `}</style>
      {false && showCalendarEventsModal && (
        <div className="dds2025__modal-backdrop">
          <div className="dds2025__modal dds2025__modal--wide">
            <div className="dds2025__modal-title">
              Dropdown Teachers — {dropdownTeachersForModal.length}
            </div>
            <div className="dds2025__modal-body">
              <div className="dds2025__modal-actions" style={{ justifyContent: "flex-start", marginBottom: 8 }}>
              </div>
              <div className="dds2025__section">
                <div className="dds2025__section-title">
                  Calendar Events for Dropdown Teachers
                </div>
                <div className="dds2025__simple-list">
                  {weeklyTeacherEventsForModal.length ? (
                    weeklyTeacherEventsForModal.map(({ ev, date, idx }) => {
                      const when = date && !isNaN(date) ? format(date, "EEE MMM d h:mma") : "Date N/A";
                      return (
                        <div key={ev.id || ev._id || idx} className="dds2025__simple-row">
                          <div className="dds2025__simple-name">
                            {ev.studentName || ev.familyName || ev.lessonName || "Lesson"}
                          </div>
                          <div className="dds2025__simple-sub">
                            {when} • {ev.lessonLength || ev.rowLength || "--"} mins • Teacher{" "}
                            {ev.teacherName || ev.teacherId || ev.teacherID || ev.TeacherID}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="dds2025__simple-sub">No events loaded yet.</div>
                  )}
                </div>
              </div>
              <div className="dds2025__section">
                <div className="dds2025__section-title">Candidate Slots (pre-optimization)</div>
                <div className="dds2025__simple-list">
                  {Array.from(candidateSlotsByTeacher.entries()).map(([tid, dayMap]) => {
                    const teacherLabel = teacherNameById.get(String(tid)) || tid;
                    return (
                      <div key={tid} className="dds2025__simple-row">
                        <div className="dds2025__simple-name">{teacherLabel}</div>
                        <div className="dds2025__simple-sub">
                          {weekDays.map((day, idx) => {
                            const slots = dayMap?.[idx] || [];
                            const formatted = slots
                              .map((slot) => {
                                const h = Math.floor(slot.start / 60)
                                  .toString()
                                  .padStart(2, "0");
                                const m = (slot.start % 60).toString().padStart(2, "0");
                                return `${h}:${m}`;
                              })
                              .join(", ");
                            return (
                              <div key={`${tid}-${day}`} style={{ marginBottom: 4 }}>
                                <strong>{day}:</strong>{" "}
                                {formatted || "—"}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  {!candidateSlotsByTeacher.size && (
                    <div className="dds2025__simple-sub">No candidate slots yet.</div>
                  )}
                </div>
              </div>
              <div className="dds2025__section">
                <div className="dds2025__section-title">Student Slot Options (drive-aware)</div>
                <div className="dds2025__simple-list">
                  {studentSlotOptions.map(({ student, options }) => (
                    <div
                      key={student.id || student.studentId || student.studentName}
                      className="dds2025__simple-row"
                    >
                      <div className="dds2025__simple-name">
                        {student.studentName || student.familyName || student.id || "Student"} — {options.length} options
                      </div>
                      <div className="dds2025__simple-sub">
                        {options.slice(0, 6).map((opt) => {
                          const time = `${String(Math.floor(opt.startMinutes / 60)).padStart(2, "0")}:${String(
                            opt.startMinutes % 60
                          ).padStart(2, "0")}`;
                          const teacherLabel = teacherNameById.get(String(opt.teacherId)) || opt.teacherId;
                          const delta = Number.isFinite(opt.extraDriveMinutes)
                            ? `${opt.extraDriveMinutes.toFixed(0)} min`
                            : "n/a";
                          const gapNote = Number.isFinite(opt.score) && Number.isFinite(opt.extraDriveMinutes)
                            ? `gap≈${(opt.score - opt.extraDriveMinutes).toFixed(0)}m`
                            : "";
                          return (
                            <span key={opt.slotId} style={{ display: "block" }}>
                              {weekDays[opt.dayIndex]} @ {time} • {teacherLabel} • Δdrive {delta} {gapNote}
                            </span>
                          );
                        })}
                        {options.length > 6 && <span>…and {options.length - 6} more</span>}
                        {!options.length && "No feasible slots"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="dds2025__section">
                <div className="dds2025__section-title">Drive Edges ({driveEdgesForModal.length})</div>
                <div className="dds2025__simple-list">
                  {driveEdgesForModal.length ? (
                    driveEdgesForModal.map((edge) => (
                      <div key={edge.id} className="dds2025__simple-row">
                        <div className="dds2025__simple-name">
                          {edge.fromLabel} → {edge.toLabel}
                        </div>
                        <div className="dds2025__simple-sub">
                          {Number.isFinite(edge.minutes) ? `${edge.minutes} mins` : "Unavailable"} • {edge.bucket || ""}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="dds2025__simple-sub">No drive edges cached yet.</div>
                  )}
                </div>
              </div>
            </div>
            <div className="dds2025__modal-actions">
              <button className="dds2025__nav-btn" onClick={() => setShowCalendarEventsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showEventDriveModal && (
        <div className="dds2025__modal-backdrop">
          <div className="dds2025__event-modal-system">
            <div className="dds2025__event-modal-x">
              <button
                className="dds2025__icon-btn"
                onClick={() => {
                  setShowEventDriveModal(false);
                  setEventModalData(null);
                }}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            {eventModalData && (
              <>
                <div className="dds2025__event-modal-left">
                  <div className="dds2025__event-modal-student">
                    {eventModalData.studentName || eventModalData.familyName || "Student"}
                  </div>
                  <div>
                    <label className="dds2025__welcome-label">Lesson length</label>
                    <select
                      value={
                        eventModalData.lessonLength ||
                        (eventModalData.rowLength ? eventModalData.rowLength * SLOT_MINUTES : 30)
                      }
                      onChange={(e) => handleModalLessonLengthChange(e.target.value)}
                      style={{ width: "100%", padding: "8px", borderRadius: 8, border: "1px solid #d0d5dd" }}
                    >
                      {[15, 20, 25, 30, 45, 60, 75, 90].map((len) => (
                        <option key={len} value={len}>
                          {len} minutes
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="dds2025__welcome-label">Instruments</label>
                    <div className="dds2025__student-chips" style={{ marginBottom: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {instrumentsForModal.map((inst, idx) => (
                        <span
                          key={idx}
                          className="student-info-tab-center-gutter__instrument-chip"
                          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                        >
                          {inst.parentInstrument || inst.instrument || inst.name || "Instrument"}
                          <button
                            type="button"
                            onClick={() => handleModalRemoveInstrument(inst)}
                            style={{ border: "none", background: "transparent", cursor: "pointer", color: "#ef4444" }}
                            aria-label="Remove instrument"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      {!instrumentsForModal.length && (
                        <span className="dds2025-chip dds2025-chip--neutral">None listed</span>
                      )}
                    </div>
                    <select
                      value=""
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val) handleModalInstrumentChange(val);
                      }}
                      style={{ width: "100%", padding: "8px", borderRadius: 8, border: "1px solid #d0d5dd" }}
                    >
                      <option value="">Select instrument</option>
                      {(allInstruments.length ? allInstruments : Array.from(instrumentSetForSchool)).map((inst, idx) => {
                        const label = inst.parentInstrument || inst.instrument || inst.name || inst;
                        const value = inst.parentInstrument || inst.instrument || inst.name || inst;
                        return (
                          <option key={`${value}-${idx}`} value={value}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="dds2025__event-modal-edges">
                    <div className="dds2025__section-title">Drive Times</div>
                    <div className="dds2025__simple-list">
                      {driveEdgesForPlaceDirectory.length ? (
                        driveEdgesForPlaceDirectory.map((edge) => (
                          <div key={edge.id} className="dds2025__simple-row">
                            <div className="dds2025__simple-name">
                              {edge.fromLabel} → {edge.toLabel}
                            </div>
                            <div className="dds2025__simple-sub">
                              {Number.isFinite(edge.minutes) ? `${edge.minutes} mins` : "Unknown"}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="dds2025__simple-sub">No drive edges found for this school.</div>
                      )}
                    </div>
                    {eventModalData?.planned && (
                      <button
                        type="button"
                        className="dds2025__remove-calendar-btn"
                        onClick={handleRemoveFromCalendar}
                      >
                        Remove From Calendar
                      </button>
                    )}
                  </div>
                </div>
                <div className="dds2025__event-modal-center">
                  {!showEventMap ? (
                    <div
                      className="dds2025__event-modal-map"
                      style={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 8 }}
                    >
                      <div className="dds2025__simple-sub">Map loading is paused to avoid freezing.</div>
                      <button
                        type="button"
                        className="dds2025__nav-btn dds2025__nav-btn--primary"
                        onClick={() => setShowEventMap(true)}
                      >
                        Load map
                      </button>
                    </div>
                  ) : eventModalLocation ? (
                    <div className="dds2025__event-modal-map">
                      <APIProvider apiKey={"AIzaSyCTz6Xc5kEtX2QxgcKe3-sQPwsEwRE8WmU"}>
                        <GoogleMap
                          zoom={10}
                          center={{ lat: eventModalLocation.lat, lng: eventModalLocation.lng }}
                          gestureHandling={"greedy"}
                          disableDefaultUI={true}
                          mapId={"3324feda48bc0f19"}
                        >
                          {mapMarkers.map((marker) => (
                            <AdvancedMarker
                              key={marker.id}
                              position={marker.location}
                              gmpClickable={true}
                              onClick={() =>
                                setActiveMapMarkerId((prev) => (prev === marker.id ? null : marker.id))
                              }
                            >
                              <div className="dds2025__map-pin" style={{ cursor: "pointer" }}>
                                <Pin
                                  background={
                                    marker.role === "teacher"
                                      ? "#2563eb"
                                      : marker.role === "other"
                                      ? "#14532d"
                                      : "#dc2626"
                                  }
                                  borderColor={
                                    marker.role === "teacher"
                                      ? "#1e3a8a"
                                      : marker.role === "other"
                                      ? "#052e16"
                                      : "#991b1b"
                                  }
                                  glyphColor="#ffffff"
                                />
                              </div>
                            </AdvancedMarker>
                          ))}
                          {activeMapMarker && (
                            <InfoWindow
                              position={activeMapMarker.location}
                              onCloseClick={() => setActiveMapMarkerId(null)}
                            >
                              <div className="dds2025__map-marker">
                                <div className="dds2025__map-marker__name">
                                  {activeMapMarker.name} {activeMapMarker.role === "teacher" ? "Teacher" : "Family"}
                                </div>
                                {activeMapMarker.lessonTime ? (
                                  <div className="dds2025__map-marker__address">{activeMapMarker.lessonTime}</div>
                                ) : null}
                                {activeMapMarker.address ? (
                                  <div className="dds2025__map-marker__address">{activeMapMarker.address}</div>
                                ) : null}
                                <div className="dds2025__map-marker__icon-row">
                                  {activeMapMarker.students != null && (
                                    <div className="dds2025__map-marker__students">
                                      <MdAccessibility />
                                      <div>{activeMapMarker.students}</div>
                                    </div>
                                  )}
                                  {activeMapMarker.inst?.length ? (
                                    <div className="dds2025__map-marker__instruments">
                                      {activeMapMarker.inst.map((inst, idx) => (
                                        <div key={`active-inst-${idx}`} className="dds2025__map-marker__inst">
                                          {inst}
                                        </div>
                                      ))}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </InfoWindow>
                          )}
                        </GoogleMap>
                      </APIProvider>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>No location available for this student.</div>
                  )}
                </div>
                <div className="dds2025__event-modal-right student-onboarding-card-modal-fall-2023__notes-area">
                  {eventModalFamily?.familyId ? (
                    <NoteAreaFall2023
                      key={eventModalFamily.familyId}
                      modalInfo={{
                        id: eventModalFamily.familyId,
                        type: "familyId",
                        displayName: eventModalFamily.familyId,
                      }}
                      userData={userData}
                      school={(eventModalFamily.school || selectedSchool || "").toLowerCase()}
                      familyStudentCount={(eventModalFamily.students || []).length}
                    />
                  ) : (
                    <div className="dds2025__simple-sub" style={{ padding: 12 }}>
                      Notes not available.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {dragPreview && (
        <div
          className="dds2025__drag-preview"
          style={{ left: dragPreview.x, top: dragPreview.y }}
        >
          {dragPreview.label}
        </div>
      )}
    </div>
  );
};

export default DragAndDropScheduler2025;
